/**
 * Resolve and collect {@link ExtractedDesignFacts}. Ranking is the source of
 * truth when sub-steps disagree — never average, never pick by confidence,
 * never leave a leaf null when a candidate exists. A window-ratio number that
 * is only an area percentage is not a candidate for that leaf.
 */

import type { ExtractedDrawing } from '../models/project-file-drawing.model';
import { isProjectFileExtractionOutputV1 } from '../models/project-file-extraction.model';
import type {
  BlueprintSubtype,
  ProjectFile,
  ProjectFileBatchBuildingComposition,
  ProjectFileBatchJoinedBlueprintExtraction,
} from '../models/project-file-batch.model';
import {
  EXTRACTED_PROGRAM_TYPES,
  extractedAreaKindSchema,
  extractedDesignFactsAiSchema,
  extractedDesignFactsSchema,
  extractedOtherAreaRowScopeSchema,
  extractedProgramTypeSchema,
  emptyExtractedBuildingFacts,
  emptyExtractedDesignFacts,
  emptyExtractedStoreyFacts,
  emptyExtractedVolumeAreas,
  emptyExtractedVolumeAreasAi,
  RESIDENTIAL_PROGRAM_FORM,
  type ExtractedApartmentFacts,
  type ExtractedBuildingFacts,
  type ExtractedBuildingPartFacts,
  type ExtractedDesignFactCandidate,
  type ExtractedDesignFactConflict,
  type ExtractedDesignFactLeafKind,
  type ExtractedDesignFactSource,
  type ExtractedDesignFacts,
  type ExtractedDesignFactsAi,
  type ExtractedOtherAreaFacts,
  type ExtractedOtherAreaRowScope,
  type ExtractedProgramType,
  type ExtractedStoreyProgram,
  type ExtractedVolumeAreas,
  type ExtractedResidentialFacts,
  type ResidentialProgramForm,
  type ExtractedStairwellsFacts,
  type ExtractedStoreyFacts,
  type RoofForm,
  type RoofFormType,
  type TypicalAreaScope,
  type WindowRatioDeterminedBy,
  type Read,
  type ReadFrom,
  type ReadSource,
  type Reading,
} from '../models/extracted-design-facts.model';
import type { FfuRoof } from '../models/ffu-output.model';
import {
  drawingPlanCoverage,
  placeDrawingCoverages,
} from './project-file-drawing-groups.helpers';
import {
  buildingInstanceIdsFromClassification,
  canonicalBuildingInstanceKey,
  extractBuildingReference,
  normalizeBuildingInstanceKey,
  resolveDrawingBuildingKeys,
  type KnownBuildingIdentity,
} from './project-file-building-key.helpers';
import { assertUnreachable } from './function.helpers';
import { parseBlueprintRoofForm } from './project-file-extraction-json-schema.helpers';
import { keepFirstProjectFilePerContent } from './project-file-content-dedupe.helpers';
import type { StoreyCount, StoreyLevel } from '../models/storey-level.model';
import {
  canonicalStoreyKey,
  formatPlanNumbers,
  planLabel,
  inferGroundFloorPlanNumber,
  isApartmentTypeLabel,
  labelLooksLikeGroundFloor,
  labelLooksLikeUnderground,
  labelledStoreyKind,
  longestContiguousPlanCluster,
  mentionedStoreys,
  parseCanonicalStoreyToken,
  parseElevationMark,
  parsePlanNumbers,
  printedEntranceLabel,
  storeyIndexKey,
  uniquePlanNumbersFromStoreyTokens,
  UNSPECIFIED_ENTRANCE_PLAN_NUMBER,
  type CanonicalStoreyKey,
  type MentionedStoreyKind,
} from './extracted-storey-key.helpers';
import {
  isEntranceStoreyLevel,
  placeStoreyLevel,
  specialStoreyLevel,
  storeyLevelFromAi,
  storeyLevelIndex,
  storeyLevelPosition,
  storeyLevelPositionIndexes,
  storeyLevelSortKey,
} from './storey-level.helpers';

/** Best source first. Sources omitted from a leaf cannot win a clash. */
export const EXTRACTED_DESIGN_FACT_SOURCE_RANK: Record<
  ExtractedDesignFactLeafKind,
  readonly ExtractedDesignFactSource[]
> = {
  bta_m2: [
    'area_schedule',
    'technical_spec',
    'project_brief',
    'blueprint_top_view',
    'blueprint',
    'joined_blueprint',
  ],
  area_m2: [
    'area_schedule',
    'technical_spec',
    'project_brief',
    'blueprint_top_view',
    'blueprint',
    'joined_blueprint',
  ],
  programs: [
    'area_schedule',
    'technical_spec',
    'project_brief',
    'blueprint_top_view',
  ],
  storey_count: [
    'area_schedule',
    'joined_blueprint',
    'blueprint_side_view',
    'technical_spec',
    'blueprint_top_view',
    'project_brief',
  ],
  /**
   * Best source first. The index kept is the one most likely correct for
   * this floor. A missing source cannot win.
   */
  storey_index: [
    'blueprint_side_view',
    'blueprint_top_view',
    'blueprint',
    'joined_blueprint',
    'area_schedule',
    'technical_spec',
    'project_brief',
  ],
  inner_height_m: [
    'blueprint_side_view',
    'technical_spec',
    'blueprint_top_view',
  ],
  floor_to_floor_height_m: [
    'blueprint_side_view',
    'technical_spec',
    'blueprint_top_view',
  ],
  window_ratio: [
    'technical_spec',
    'project_brief',
    'blueprint_side_view',
    'building_rendering',
  ],
  beam_span_m: ['technical_spec'],
  apartment_size: ['technical_spec', 'project_brief', 'area_schedule'],
  balcony: ['technical_spec', 'project_brief', 'area_schedule'],
  bathroom: [
    'technical_spec',
    'project_brief',
    'area_schedule',
    'blueprint_top_view',
  ],
  program_type: ['technical_spec', 'project_brief', 'area_schedule'],
  stairwells: [
    'technical_spec',
    'project_brief',
    'area_schedule',
    'blueprint_top_view',
  ],
  area_total: [
    'area_schedule',
    'technical_spec',
    'project_brief',
    'blueprint_top_view',
    'blueprint',
    'joined_blueprint',
  ],
  other_area: [
    'area_schedule',
    'technical_spec',
    'project_brief',
    'blueprint_top_view',
    'blueprint',
    'blueprint_side_view',
  ],
  apartment_count: ['area_schedule', 'technical_spec', 'project_brief'],
  apartment: ['area_schedule', 'technical_spec', 'project_brief'],
  project_name: ['title_block', 'project_description', 'project_brief'],
  location: ['project_description', 'project_brief', 'title_block'],
  property_designation: [
    'title_block',
    'project_description',
    'technical_spec',
    'project_brief',
  ],
  detailed_plan: [
    'project_description',
    'technical_spec',
    'project_brief',
    'title_block',
  ],
  client: [
    'title_block',
    'project_description',
    'technical_spec',
    'project_brief',
  ],
  building_name: [
    'title_block',
    'blueprint',
    'blueprint_side_view',
    'blueprint_top_view',
    'technical_spec',
    'area_schedule',
  ],
  building_count: [
    'area_schedule',
    'technical_spec',
    'project_brief',
    'blueprint',
  ],
  roof_form: ['blueprint_side_view', 'blueprint', 'blueprint_top_view'],
  slab_thickness_mm: ['technical_spec', 'blueprint_side_view', 'blueprint'],
};

/** Ascending by rank; unranked sources tie (Infinity - Infinity is NaN, which breaks sort). */
const compareSourceRank = (
  kind: ExtractedDesignFactLeafKind,
  a: ExtractedDesignFactSource,
  b: ExtractedDesignFactSource,
): number => {
  const aRank = extractedDesignFactSourceRank(kind, a);
  const bRank = extractedDesignFactSourceRank(kind, b);
  if (aRank === bRank) return 0;
  return aRank < bRank ? -1 : 1;
};

export const extractedDesignFactSourceRank = (
  kind: ExtractedDesignFactLeafKind,
  source: ExtractedDesignFactSource,
): number => {
  const order = EXTRACTED_DESIGN_FACT_SOURCE_RANK[kind];
  const index = order.indexOf(source);
  return index === -1 ? Number.POSITIVE_INFINITY : index;
};

const valuesNearlyEqual = (
  kind: ExtractedDesignFactLeafKind,
  a: string | number,
  b: string | number,
): boolean => {
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  }
  switch (kind) {
    case 'storey_count':
    case 'storey_index':
    case 'building_count':
    case 'stairwells':
      return a === b;
    case 'window_ratio':
      return Math.abs(a - b) <= 0.02;
    case 'inner_height_m':
    case 'floor_to_floor_height_m':
    case 'beam_span_m':
      return Math.abs(a - b) <= 0.05;
    case 'bta_m2':
    case 'area_m2':
    case 'area_total':
    case 'other_area':
    case 'apartment':
    case 'apartment_size':
    case 'balcony':
    case 'bathroom':
      return Math.abs(a - b) <= Math.max(1, Math.abs(a) * 0.005);
    case 'program_type':
      return a === b;
    case 'apartment_count':
      return a === b;
    case 'project_name':
    case 'location':
    case 'property_designation':
    case 'detailed_plan':
    case 'client':
    case 'building_name':
    case 'programs':
    case 'roof_form':
      return a === b;
    case 'slab_thickness_mm':
      return Math.abs(a - b) <= 1;
    default:
      return assertUnreachable(kind);
  }
};

export type PickedExtractedDesignFact = {
  value: string | number;
  picked: ExtractedDesignFactCandidate;
  rejected: ExtractedDesignFactCandidate[];
};

/**
 * Pick one candidate for a single leaf. Agreement (or print-rounding) wins.
 * Same-rank clash: larger agreeing cluster, then earlier file name.
 * Never averages. Never returns null when any candidate exists.
 */
export const pickMostLikelyExtractedDesignFact = (
  kind: ExtractedDesignFactLeafKind,
  candidates: readonly ExtractedDesignFactCandidate[],
): PickedExtractedDesignFact | null => {
  if (candidates.length === 0) return null;
  const clusters: ExtractedDesignFactCandidate[][] = [];
  for (const candidate of candidates) {
    const cluster = clusters.find((group) => {
      const head = group[0];
      return (
        head !== undefined &&
        valuesNearlyEqual(kind, head.value, candidate.value)
      );
    });
    if (cluster) {
      cluster.push(candidate);
      continue;
    }
    clusters.push([candidate]);
  }
  const bestIn = (
    group: readonly ExtractedDesignFactCandidate[],
  ): ExtractedDesignFactCandidate => {
    const sorted = [...group].sort((a, b) => {
      const rank = compareSourceRank(kind, a.source, b.source);
      if (rank !== 0) return rank;
      return a.fileName.localeCompare(b.fileName);
    });
    const first = sorted[0];
    if (first === undefined) {
      throw new Error('cluster unexpectedly empty');
    }
    return first;
  };
  const rankedClusters = [...clusters].sort((a, b) => {
    const aBest = bestIn(a);
    const bBest = bestIn(b);
    const rank = compareSourceRank(kind, aBest.source, bBest.source);
    if (rank !== 0) return rank;
    if (b.length !== a.length) return b.length - a.length;
    return aBest.fileName.localeCompare(bBest.fileName);
  });
  const group = rankedClusters[0];
  if (group === undefined) return null;
  const picked = bestIn(group);
  return {
    value: picked.value,
    picked,
    rejected: candidates.filter((c) => c !== picked),
  };
};

export const WINDOW_RATIO_EVIDENCE_KINDS = [
  'printed',
  'calculated',
  'estimated',
  'area_percentage',
] as const;

export type WindowRatioEvidenceKind =
  (typeof WINDOW_RATIO_EVIDENCE_KINDS)[number];

/** One input to {@link chooseWindowRatio}. Area percentages are never chosen. */
export type WindowRatioEvidence = {
  value: number;
  kind: WindowRatioEvidenceKind;
  source: ExtractedDesignFactSource;
  fileName: string;
};

export type ChosenWindowRatio = {
  windowRatio: number;
  /** False when printed or calculated. True when estimated from a drawing or rendering. */
  windowRatioEstimated: boolean;
  windowRatioDeterminedBy: WindowRatioDeterminedBy;
};

const ESTIMATED_WINDOW_RATIO_SOURCES = new Set<ExtractedDesignFactSource>([
  'blueprint_side_view',
  'building_rendering',
]);

/**
 * Classify a window-ratio candidate. A drawing or rendering with no printed
 * flag is an estimate. Any other untagged number is an area percentage.
 */
export const windowRatioEvidenceFromCandidate = (
  candidate: ExtractedDesignFactCandidate,
): WindowRatioEvidence | null => {
  if (
    candidate.kind !== 'window_ratio' ||
    typeof candidate.value !== 'number'
  ) {
    return null;
  }
  if (candidate.windowRatioDeterminedBy === 'calculated') {
    return {
      kind: 'calculated',
      value: candidate.value,
      source: candidate.source,
      fileName: candidate.fileName,
    };
  }
  if (
    candidate.windowRatioDeterminedBy === 'read' ||
    candidate.windowRatioEstimated === false
  ) {
    return {
      kind: 'printed',
      value: candidate.value,
      source: candidate.source,
      fileName: candidate.fileName,
    };
  }
  if (
    candidate.windowRatioDeterminedBy === 'estimated' ||
    candidate.windowRatioEstimated === true ||
    ESTIMATED_WINDOW_RATIO_SOURCES.has(candidate.source)
  ) {
    return {
      kind: 'estimated',
      value: candidate.value,
      source: candidate.source,
      fileName: candidate.fileName,
    };
  }
  return {
    kind: 'area_percentage',
    value: candidate.value,
    source: candidate.source,
    fileName: candidate.fileName,
  };
};

const pickWindowRatioValue = (
  evidence: readonly WindowRatioEvidence[],
): number | null => {
  if (evidence.length === 0) return null;
  const picked = pickMostLikelyExtractedDesignFact(
    'window_ratio',
    evidence.map((item) => ({
      path: 'windowRatio',
      kind: 'window_ratio' as const,
      value: item.value,
      source: item.source,
      fileName: item.fileName,
      confidence: null,
      sheetNote: null,
      windowRatioEstimated: item.kind === 'estimated',
    })),
  );
  return picked !== null && typeof picked.value === 'number'
    ? picked.value
    : null;
};

/**
 * Printed fönsterandel or glastäckning wins. Otherwise one estimate from a
 * facade, elevation, or rendering. BOA/BTA and parking percentages are ignored.
 */
export const chooseWindowRatio = (
  evidence: readonly WindowRatioEvidence[],
): ChosenWindowRatio | null => {
  const printed = pickWindowRatioValue(
    evidence.filter((item) => item.kind === 'printed'),
  );
  if (printed !== null) {
    return {
      windowRatio: printed,
      windowRatioEstimated: false,
      windowRatioDeterminedBy: 'read',
    };
  }
  const calculated = pickWindowRatioValue(
    evidence.filter((item) => item.kind === 'calculated'),
  );
  if (calculated !== null) {
    return {
      windowRatio: calculated,
      windowRatioEstimated: false,
      windowRatioDeterminedBy: 'calculated',
    };
  }
  const estimated = pickWindowRatioValue(
    evidence.filter((item) => item.kind === 'estimated'),
  );
  if (estimated !== null) {
    return {
      windowRatio: estimated,
      windowRatioEstimated: true,
      windowRatioDeterminedBy: 'estimated',
    };
  }
  return null;
};

const chosenWindowFromCandidates = (
  candidates: readonly ExtractedDesignFactCandidate[],
): ChosenWindowRatio | null =>
  chooseWindowRatio(
    candidates.flatMap((candidate) => {
      const evidence = windowRatioEvidenceFromCandidate(candidate);
      return evidence === null ? [] : [evidence];
    }),
  );

const mergeWindowRatioFields = (
  rows: readonly Pick<
    ExtractedBuildingFacts,
    | 'windowRatio'
    | 'windowRatioEstimated'
    | 'windowRatioDeterminedBy'
    | 'buildingInstanceKey'
  >[],
): {
  windowRatio: number | null;
  windowRatioEstimated: boolean | null;
  windowRatioDeterminedBy: WindowRatioDeterminedBy | null;
} => {
  const chosen = chooseWindowRatio(
    rows.flatMap((row) => {
      if (row.windowRatio === null) return [];
      const determinedBy = row.windowRatioDeterminedBy;
      if (determinedBy === null && row.windowRatioEstimated === null) return [];
      const kind =
        determinedBy === 'calculated'
          ? 'calculated'
          : determinedBy === 'estimated' || row.windowRatioEstimated === true
            ? 'estimated'
            : 'printed';
      const evidence: WindowRatioEvidence = {
        value: row.windowRatio,
        kind,
        source: kind === 'estimated' ? 'blueprint_side_view' : 'technical_spec',
        fileName: row.buildingInstanceKey ?? 'building',
      };
      return [evidence];
    }),
  );
  if (chosen !== null) {
    return {
      windowRatio: chosen.windowRatio,
      windowRatioEstimated: chosen.windowRatioEstimated,
      windowRatioDeterminedBy: chosen.windowRatioDeterminedBy,
    };
  }
  const legacy = rows.find((row) => row.windowRatio !== null);
  return {
    windowRatio: legacy?.windowRatio ?? null,
    windowRatioEstimated: legacy?.windowRatioEstimated ?? null,
    windowRatioDeterminedBy: legacy?.windowRatioDeterminedBy ?? null,
  };
};

const trimNull = (v: string | null): string | null => {
  if (v === null) return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
};

const allNullResidential = (row: ExtractedResidentialFacts): boolean =>
  row.livingAreaPerApartmentM2 === null &&
  row.balconyAreaPerApartmentM2 === null &&
  row.bathroomAreaPerApartmentM2 === null &&
  row.programType === null;

const residentialProgramForm = (
  value: string | null,
): ResidentialProgramForm | null => {
  if (value === null) return null;
  return RESIDENTIAL_PROGRAM_FORM.find((form) => form === value) ?? null;
};

const residentialOrNull = (
  row: ExtractedResidentialFacts,
): ExtractedResidentialFacts | null => (allNullResidential(row) ? null : row);

const allNullStairwells = (row: ExtractedStairwellsFacts): boolean =>
  row.apartmentsPerStairwellPerStorey === null &&
  row.elevatorsPerStairwell === null;

const mapResidential = (
  row: ExtractedDesignFactsAi['buildings'][number]['residential'],
): ExtractedResidentialFacts | null => {
  const mapped: ExtractedResidentialFacts = {
    livingAreaPerApartmentM2: row.living_area_per_apartment_m2,
    balconyAreaPerApartmentM2: row.balcony_area_per_apartment_m2,
    bathroomAreaPerApartmentM2: row.bathroom_area_per_apartment_m2,
    programType: row.program_type,
  };
  return residentialOrNull(mapped);
};

const mapStairwells = (
  row: ExtractedDesignFactsAi['buildings'][number]['stairwells'],
): ExtractedStairwellsFacts | null => {
  const mapped: ExtractedStairwellsFacts = {
    apartmentsPerStairwellPerStorey: row.apartments_per_stairwell_per_storey,
    elevatorsPerStairwell: row.elevators_per_stairwell,
  };
  return allNullStairwells(mapped) ? null : mapped;
};

type VolumeAreasAi = Pick<
  ExtractedDesignFactsAi['buildings'][number],
  | 'ljus_bta_m2'
  | 'boa_m2'
  | 'loa_m2'
  | 'bya_m2'
  | 'apartment_count'
  | 'apartments'
  | 'other_areas'
>;

type VolumeAreasStored = ExtractedVolumeAreas;

const mapApartmentAi = (
  row: VolumeAreasAi['apartments'][number],
): ExtractedApartmentFacts => ({
  label: trimNull(row.label),
  count: row.count,
  areaM2: row.area_m2,
  areaMinM2: row.area_min_m2,
  areaMaxM2: row.area_max_m2,
});

const mapOtherAreaAi = (
  row: VolumeAreasAi['other_areas'][number],
): ExtractedOtherAreaFacts => ({
  label: trimNull(row.label),
  areaM2: row.area_m2,
  ...(row.row_scope === null ? {} : { rowScope: row.row_scope }),
});

const mapVolumeAreas = (row: VolumeAreasAi): VolumeAreasStored => ({
  ljusBtaM2: row.ljus_bta_m2,
  boaM2: row.boa_m2,
  loaM2: row.loa_m2,
  byaM2: row.bya_m2,
  apartmentCount: row.apartment_count,
  apartments: row.apartments.map(mapApartmentAi),
  otherAreas: row.other_areas.map(mapOtherAreaAi),
});

const volumeFieldsOf = (row: VolumeAreasStored): VolumeAreasStored => ({
  ljusBtaM2: row.ljusBtaM2,
  boaM2: row.boaM2,
  loaM2: row.loaM2,
  byaM2: row.byaM2,
  apartmentCount: row.apartmentCount,
  apartments: row.apartments,
  otherAreas: row.otherAreas,
});

/** Stable leaf token: printed label plus the area that keeps duplicate types apart. */
const listRowToken = (
  label: string | null,
  index: number,
  areaSlug: string,
): string => {
  const name = normalizeExtractedBuildingName(label ?? '');
  const base = name === '' ? `i${index}` : name;
  return areaSlug === '' ? `${base}#${index}` : `${base}@${areaSlug}`;
};

const apartmentAreaSlug = (
  areaM2: number | null,
  areaMinM2: number | null,
  areaMaxM2: number | null,
): string => {
  if (areaM2 != null) return String(areaM2);
  if (areaMinM2 == null && areaMaxM2 == null) return '';
  return `${areaMinM2 ?? ''}-${areaMaxM2 ?? ''}`;
};

const apartmentToken = (
  row: Pick<
    ExtractedApartmentFacts,
    'label' | 'areaM2' | 'areaMinM2' | 'areaMaxM2'
  >,
  index: number,
): string =>
  listRowToken(
    row.label,
    index,
    apartmentAreaSlug(row.areaM2, row.areaMinM2, row.areaMaxM2),
  );

const otherAreaToken = (
  row: Pick<ExtractedOtherAreaFacts, 'label'>,
  index: number,
): string => listRowToken(row.label, index, '');

const APARTMENT_TYPE_LABEL_RE = /\d+\s*-?\s*rok\b|lägenhet|lagenhet/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Design-fact payloads stored before bathroom and program form existed still parse. */
const completeResidentialAi = (value: unknown): unknown => {
  if (!isRecord(value)) return value;
  return {
    living_area_per_apartment_m2: null,
    balcony_area_per_apartment_m2: null,
    bathroom_area_per_apartment_m2: null,
    program_type: null,
    ...value,
  };
};

/** Design-fact payloads stored before volume totals existed still parse. */
const withProgramLabels = (storeys: unknown): unknown => {
  if (!Array.isArray(storeys)) return storeys;
  return storeys.map((storey) => {
    if (!isRecord(storey) || !Array.isArray(storey.programs)) return storey;
    return {
      ...storey,
      programs: storey.programs.map((program) => {
        if (!isRecord(program) || 'label' in program) return program;
        return { ...program, label: null };
      }),
    };
  });
};

const withVolumeAreaDefaults = (value: unknown): unknown => {
  if (!isRecord(value) || !Array.isArray(value.buildings)) return value;
  return {
    property_designation: null,
    detailed_plan: null,
    client: null,
    ...value,
    buildings: value.buildings.map((building) => {
      if (!isRecord(building)) return building;
      const parts = Array.isArray(building.parts)
        ? building.parts.map((part) =>
            isRecord(part)
              ? {
                  ...emptyExtractedVolumeAreasAi(),
                  ...part,
                  storeys: withProgramLabels(part.storeys),
                }
              : part,
          )
        : building.parts;
      return {
        ...emptyExtractedVolumeAreasAi(),
        window_ratio_determined_by: null,
        slab_thickness_mm: null,
        ...building,
        residential: completeResidentialAi(building.residential),
        parts,
        storeys: withProgramLabels(building.storeys),
      };
    }),
  };
};

type ExtractedStoreyAi =
  ExtractedDesignFactsAi['buildings'][number]['storeys'][number];

/**
 * Model kind wins; a `regular` row still becomes special when its label names
 * a vind, suterräng, takplan or normalplan. A space on a floor is not a kind.
 */
const aiStoreyKind = (
  s: ExtractedStoreyAi,
): ExtractedStoreyAi['storey_kind'] =>
  s.storey_kind === 'regular'
    ? (labelledStoreyKind(s.label) ?? 'regular')
    : s.storey_kind;

const levelForMention = (kind: MentionedStoreyKind): StoreyLevel => {
  switch (kind) {
    case 'entrance':
      return { kind: 'single', index: 0 };
    case 'underground':
      return { kind: 'single', index: -1 };
    case 'typical':
      return { kind: 'typical' };
    case 'roof':
      return { kind: 'roof' };
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return { kind, index: null };
    default:
      return assertUnreachable(kind);
  }
};

/**
 * A label that names several levels (`entréplan, typplan och garageplan`)
 * is one storey per level. A single level, or a label that also prints plan
 * numbers, stays one row.
 */
const splitMultiKindStorey = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts[] => {
  if (
    storey.planNumbers.length > 0 ||
    parsePlanNumbers(storey.label).length > 0
  ) {
    return [storey];
  }
  const mentioned = mentionedStoreys(storey.label);
  if (mentioned.length < 2) return [storey];
  return mentioned
    .map((item) => ({
      ...storey,
      label: item.label,
      planNumbers: [],
      level: levelForMention(item.kind),
    }))
    .sort((a, b) => storeyLevelSortKey(a.level) - storeyLevelSortKey(b.level));
};

const contiguousFrom = (numbers: readonly number[]): boolean =>
  numbers.every((n, i) => i === 0 || n === (numbers[i - 1] ?? n) + 1);

const rangeStartIndex = (
  level: StoreyLevel | null,
  numbers: readonly number[],
): number | null => {
  if (!contiguousFrom(numbers)) return null;
  if (level?.kind === 'range') return level.fromIndex;
  if (level?.kind === 'single') return level.index;
  return null;
};

/**
 * `Plan 11–15` is one storey per printed number. Sheet prose that merely
 * mentions a plan range is not a storey list.
 */
const expandPlanNumberStoreys = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts[] => {
  const numbers =
    storey.planNumbers.length > 1
      ? storey.planNumbers
      : parsePlanNumbers(storey.label);
  if (numbers.length <= 1) return [storey];
  const key = canonicalStoreyKey({ label: storey.label });
  if (key == null || key.planNumbers.length <= 1) return [storey];
  const start = rangeStartIndex(storey.level, numbers);
  return numbers.map((planNumber, offset) => {
    const level: StoreyLevel | null =
      start === null ? null : { kind: 'single', index: start + offset };
    return {
      ...storey,
      label: planLabel([planNumber], storey.label),
      planNumbers: [planNumber],
      level,
    };
  });
};

const normalizeStoredStorey = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts[] => {
  if (storeyIndexOmitted(storey)) return [storey];
  return splitMultiKindStorey(storey).flatMap(expandPlanNumberStoreys);
};

/** `label` exists only on `other`. A mapped type drops a printed word. */
const storedProgram = (
  type: ExtractedProgramType,
  areaM2: number | null,
  label: string | null,
  share: number | null = null,
): ExtractedStoreyProgram => {
  if (type === 'other') {
    return { type, areaM2, share, label: trimNull(label) };
  }
  return { type, areaM2, share };
};

/**
 * A model entrance mark places the storey at 0 whatever index it guessed.
 * A roof kind is a top view. It is not a storey row.
 */
const mapStoreyAiToStored = (s: ExtractedStoreyAi): ExtractedStoreyFacts[] => {
  if (s.storey_kind === 'roof') return [];
  const label = trimNull(s.label);
  if (isApartmentTypeLabel(label)) return [];
  const planNumbers = parsePlanNumbers(label);
  return normalizeStoredStorey({
    ...emptyExtractedStoreyFacts(),
    label,
    planNumbers,
    level: storeyLevelFromAi({
      kind: aiStoreyKind(s),
      index: s.is_ground_floor === true ? 0 : s.index,
      planNumbers,
    }),
    innerHeightM: s.inner_height_m,
    floorToFloorHeightM: s.floor_to_floor_height_m,
    areaM2: s.area_m2,
    programs: s.programs.map((p) =>
      storedProgram(p.type, p.area_m2, p.label, p.share),
    ),
    elevationMarkM: parseElevationMark(s.label),
  });
};

const exactStoreyCount = (value: number | null): StoreyCount | null =>
  value != null && Number.isInteger(value) && value >= 0
    ? { kind: 'exact', value }
    : null;

/** Maps OpenAI snake_case merge/extractor output to the persisted camelCase tree. */
export const mapExtractedDesignFactsAiToStored = (
  raw: ExtractedDesignFactsAi,
): ExtractedDesignFacts => ({
  projectName: trimNull(raw.project_name),
  location: trimNull(raw.location),
  propertyDesignation: trimNull(raw.property_designation),
  detailedPlan: trimNull(raw.detailed_plan),
  client: trimNull(raw.client),
  storeyCount: raw.storey_count,
  buildingCount: raw.building_count,
  buildings: raw.buildings.map((b) => ({
    buildingInstanceKey: canonicalBuildingInstanceKey(b.building_instance_key),
    buildingName: trimNull(b.building_name),
    storeyCount: exactStoreyCount(b.storey_count),
    btaM2: b.bta_m2,
    ...mapVolumeAreas(b),
    windowRatio: b.window_ratio,
    windowRatioEstimated:
      b.window_ratio === null || b.window_ratio_determined_by === null
        ? null
        : b.window_ratio_determined_by === 'estimated',
    windowRatioDeterminedBy:
      b.window_ratio === null ? null : b.window_ratio_determined_by,
    beamSpanM: b.beam_span_m,
    slabThicknessMm: b.slab_thickness_mm,
    roofForm: null,
    storeys: b.storeys.flatMap(mapStoreyAiToStored),
    parts: b.parts.flatMap((p) => {
      const name = trimNull(p.part_name);
      if (name === null) return [];
      return [
        {
          name,
          storeyCount: exactStoreyCount(p.storey_count),
          btaM2: null,
          ...mapVolumeAreas(p),
          storeys: p.storeys.flatMap(mapStoreyAiToStored),
          sourceBuildingKey: null,
        },
      ];
    }),
    connection: null,
    residential: mapResidential(b.residential),
    stairwells: mapStairwells(b.stairwells),
  })),
});

export const parseExtractedDesignFactsAi = (
  value: unknown,
): ExtractedDesignFactsAi | null => {
  const parsed = extractedDesignFactsAiSchema.safeParse(
    withVolumeAreaDefaults(value),
  );
  return parsed.success ? parsed.data : null;
};

export const parseExtractedDesignFacts = (
  value: unknown,
): ExtractedDesignFacts | null => {
  const stored = extractedDesignFactsSchema.safeParse(value);
  if (stored.success) return stored.data;
  const ai = parseExtractedDesignFactsAi(value);
  if (ai === null) return null;
  return mapExtractedDesignFactsAiToStored(ai);
};

export const blueprintSubtypeToDesignFactSource = (
  subtype: BlueprintSubtype | null | undefined,
): ExtractedDesignFactSource => {
  if (subtype === 'side_view') return 'blueprint_side_view';
  if (subtype === 'top_view') return 'blueprint_top_view';
  return 'blueprint';
};

const pushCandidate = (
  out: ExtractedDesignFactCandidate[],
  input: {
    path: string;
    kind: ExtractedDesignFactLeafKind;
    value: string | number | null;
    source: ExtractedDesignFactSource;
    fileName: string;
    confidence: number | null;
    sheetNote: string | null;
    windowRatioEstimated?: boolean | null;
    windowRatioDeterminedBy?: WindowRatioDeterminedBy | null;
    roofReading?: Reading;
    typicalAreaScope?: TypicalAreaScope;
  },
): void => {
  if (input.value === null) return;
  if (typeof input.value === 'string' && input.value.trim() === '') return;
  const value =
    typeof input.value === 'string' ? input.value.trim() : input.value;
  if (
    out.some(
      (row) =>
        row.path === input.path &&
        row.value === value &&
        row.source === input.source &&
        row.fileName === input.fileName,
    )
  ) {
    return;
  }
  out.push({
    path: input.path,
    kind: input.kind,
    value,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
    ...(input.kind === 'window_ratio'
      ? {
          windowRatioEstimated: input.windowRatioEstimated ?? null,
          windowRatioDeterminedBy: input.windowRatioDeterminedBy ?? null,
        }
      : {}),
    ...(input.kind === 'roof_form' && input.roofReading !== undefined
      ? { roofReading: input.roofReading }
      : {}),
    ...(input.typicalAreaScope === undefined
      ? {}
      : { typicalAreaScope: input.typicalAreaScope }),
  });
};

type ProgramOccurrences = Map<string, number>;

const newProgramOccurrences = (): ProgramOccurrences => new Map();

type MappedProgramRow = Omit<ProgramCandidateInput, 'occurrences'>;

/**
 * Program rows one source states for one extraction. A mapped type on one
 * storey is one program in Nodon, so the rows a sheet prints for it (one per
 * apartment line, one per lokal) are summed here and emitted once by
 * {@link flushMappedPrograms}. Unmapped rows keep their printed word.
 */
type ProgramRows = {
  occurrences: ProgramOccurrences;
  mapped: Map<string, MappedProgramRow>;
};

const newProgramRows = (): ProgramRows => ({
  occurrences: newProgramOccurrences(),
  mapped: new Map(),
});

const sumNullable = (a: number | null, b: number | null): number | null =>
  a === null ? b : b === null ? a : a + b;

/**
 * Stem of the n-th identical program row on one storey. The first row keeps
 * the plain stem; repeats get `#n`, so equal zones the plan prints several
 * times stay separate leaves instead of collapsing into one.
 */
const ordinalProgramStem = (stem: string, occurrence: number): string => {
  if (occurrence === 0) return stem;
  const suffix = `#${occurrence}`;
  return stem.endsWith(']')
    ? `${stem.slice(0, -1)}${suffix}]`
    : `${stem}[${suffix}]`;
};

const REPEAT_ORDINAL_RE = /#\d+\]$/;

const isRepeatedProgramStem = (stem: string): boolean =>
  REPEAT_ORDINAL_RE.test(stem);

/**
 * Stem without its repeat ordinal, the inverse of {@link ordinalProgramStem}.
 */
const baseProgramStem = (stem: string): string =>
  stem.replace(REPEAT_ORDINAL_RE, ']').replace(/\[\]$/, '');

const occurrenceStem = (
  stem: string,
  occurrences: ProgramOccurrences,
  storeyPrefix: string,
): string => {
  const key = `${storeyPrefix}|${stem}`;
  const occurrence = occurrences.get(key) ?? 0;
  occurrences.set(key, occurrence + 1);
  return ordinalProgramStem(stem, occurrence);
};

type ProgramCandidateInput = {
  storeyPrefix: string;
  type: ExtractedProgramType;
  areaM2: number | null;
  share: number | null;
  label: string | null;
  source: ExtractedDesignFactSource;
  fileName: string;
  confidence: number | null;
  sheetNote: string | null;
  occurrences: ProgramOccurrences;
  otherAreaScope: ExtractedOtherAreaRowScope;
  /** Rows measuring different things (e.g. area kinds) are not summed. */
  measure?: string;
};

/**
 * A program type is a candidate even when its area is missing. The area
 * leaf is separate, so a null area does not drop the type. Mapped rows wait
 * in `rows` until {@link flushMappedPrograms}.
 */
const pushProgramCandidate = (
  out: ExtractedDesignFactCandidate[],
  input: Omit<ProgramCandidateInput, 'occurrences'> & { rows: ProgramRows },
): void => {
  const { rows, ...row } = input;
  if (row.type === 'other') {
    emitProgramCandidate(out, { ...row, occurrences: rows.occurrences });
    return;
  }
  const key = `${row.storeyPrefix}|${row.type}|${row.measure ?? ''}`;
  const prev = rows.mapped.get(key);
  rows.mapped.set(
    key,
    prev === undefined
      ? row
      : {
          ...prev,
          areaM2: sumNullable(prev.areaM2, row.areaM2),
          share: sumNullable(prev.share, row.share),
        },
  );
};

const flushMappedPrograms = (
  out: ExtractedDesignFactCandidate[],
  rows: ProgramRows,
): void => {
  for (const row of rows.mapped.values()) {
    emitProgramCandidate(out, { ...row, occurrences: rows.occurrences });
  }
  rows.mapped.clear();
};

const emitProgramCandidate = (
  out: ExtractedDesignFactCandidate[],
  input: ProgramCandidateInput,
): void => {
  const stem = occurrenceStem(
    programCandidateStem(input.type, input.label, input.areaM2),
    input.occurrences,
    input.storeyPrefix,
  );
  if (input.areaM2 != null) {
    pushCandidate(out, {
      path: `${input.storeyPrefix}.programs.${stem}.areaM2`,
      kind: 'programs',
      value: input.areaM2,
      source: input.source,
      fileName: input.fileName,
      confidence: input.confidence,
      sheetNote: input.sheetNote,
    });
  }
  if (input.share != null) {
    pushCandidate(out, {
      path: `${input.storeyPrefix}.programs.${stem}.share`,
      kind: 'programs',
      value: input.share,
      source: input.source,
      fileName: input.fileName,
      confidence: input.confidence,
      sheetNote: input.sheetNote,
    });
  }
  pushCandidate(out, {
    path: `${input.storeyPrefix}.programs.${stem}`,
    kind: 'programs',
    value: input.type,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
  if (input.type !== 'other') return;
  const printed = trimNull(input.label);
  if (printed === null) return;
  pushCandidate(out, {
    path: `${input.storeyPrefix}.programs.${stem}.label`,
    kind: 'programs',
    value: printed,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
  if (input.areaM2 === null) return;
  forwardPrintedOtherArea(out, {
    volumePrefix: volumePrefixOfStorey(input.storeyPrefix),
    label: printed,
    areaM2: input.areaM2,
    rowScope: input.otherAreaScope,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
};

const normalizeExtractedBuildingName = (name: string): string =>
  name.toLowerCase().replace(/[\s.\-_–—]+/g, '');

const volumePrefixOfStorey = (storeyPrefix: string): string => {
  const cut = storeyPrefix.lastIndexOf('.storeys[');
  return cut === -1 ? storeyPrefix : storeyPrefix.slice(0, cut);
};

/**
 * One other-area row per printed word. A later reading of that word joins
 * the row already recorded, instead of opening a second row.
 */
const otherAreaTokenForLabel = (
  out: readonly ExtractedDesignFactCandidate[],
  volumePrefix: string,
  label: string,
): string => {
  const normalized = normalizeExtractedBuildingName(label);
  const prefix = `${volumePrefix}.otherAreas[`;
  for (const candidate of out) {
    if (candidate.kind !== 'other_area') continue;
    if (!candidate.path.startsWith(prefix)) continue;
    if (!candidate.path.endsWith('.label')) continue;
    if (typeof candidate.value !== 'string') continue;
    if (normalizeExtractedBuildingName(candidate.value) !== normalized) {
      continue;
    }
    const token = /\.otherAreas\[([^\]]+)\]\.label$/.exec(candidate.path)?.[1];
    if (token !== undefined) return token;
  }
  return otherAreaToken({ label }, 0);
};

const pushOtherAreaScopeCandidate = (
  out: ExtractedDesignFactCandidate[],
  input: {
    path: string;
    rowScope: ExtractedOtherAreaRowScope | null;
    source: ExtractedDesignFactSource;
    fileName: string;
    confidence: number | null;
    sheetNote: string | null;
  },
): void => {
  if (input.rowScope === null) return;
  pushCandidate(out, {
    path: input.path,
    kind: 'other_area',
    value: input.rowScope,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
};

/** A labelled other program with an area is that other area. */
const forwardPrintedOtherArea = (
  out: ExtractedDesignFactCandidate[],
  input: {
    volumePrefix: string;
    label: string;
    areaM2: number;
    rowScope: ExtractedOtherAreaRowScope;
    source: ExtractedDesignFactSource;
    fileName: string;
    confidence: number | null;
    sheetNote: string | null;
  },
): void => {
  const token = otherAreaTokenForLabel(out, input.volumePrefix, input.label);
  const areaPrefix = `${input.volumePrefix}.otherAreas[${token}]`;
  pushOtherAreaScopeCandidate(out, {
    path: `${areaPrefix}.rowScope`,
    rowScope: input.rowScope,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
  pushCandidate(out, {
    path: `${areaPrefix}.label`,
    kind: 'other_area',
    value: input.label,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
  pushCandidate(out, {
    path: `${areaPrefix}.areaM2`,
    kind: 'other_area',
    value: input.areaM2,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
};

const buildingKeyToken = (key: string | null): string =>
  canonicalBuildingInstanceKey(key) ?? '_';

const HUS_SLUG_RE = /^(hus|byggnad|building|block)[-_]/i;

/** A key earlier steps already assigned as its own house, not a stamp alias. */
const isHouseIdentitySlug = (token: string): boolean => HUS_SLUG_RE.test(token);

/** True when earlier steps already assigned two or more house keys. */
const countsSeveralHouses = (tokens: readonly string[]): boolean => {
  const slugs = new Set(tokens.filter(isHouseIdentitySlug));
  return slugs.size >= 2;
};

const pickCanonicalBuildingInstanceKey = (
  keys: readonly (string | null)[],
): string | null => {
  const tokens = [...new Set(keys.map((k) => k ?? '_'))];
  const named = tokens.filter((t) => t !== '_');
  const slug = named.find((t) => HUS_SLUG_RE.test(t));
  if (slug !== undefined) return slug;
  const first = named[0];
  return first === undefined ? null : first;
};

/** Path token for a building part: its normalized name (`Del A` → `dela`). */
export const buildingPartToken = (name: string): string =>
  normalizeExtractedBuildingName(name.normalize('NFC'));

/** Path token for a stored storey; array position backs label-less rows. */
const storeyKeyOf = (
  storey: Pick<ExtractedStoreyFacts, 'label' | 'level'>,
  indexInArray: number,
): CanonicalStoreyKey | null =>
  canonicalStoreyKey({
    label: storey.label,
    index: storeyLevelIndex(storey.level) ?? indexInArray,
  });

/** Index this step actually read. Array position is not an index. */
const readStoreyIndex = (storey: ExtractedStoreyAi): number | null => {
  if (storey.is_ground_floor === true) return 0;
  return storey.index != null && Number.isInteger(storey.index)
    ? storey.index
    : null;
};

const flattenStoreysAi = (
  storeys: readonly ExtractedStoreyAi[],
  volumePrefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  confidence: number | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  const rows = newProgramRows();
  storeys.forEach((s, i) => {
    const storey = canonicalStoreyKey({
      label: trimNull(s.label),
      index: s.index ?? i,
    });
    if (storey == null) return;
    const sPrefix = `${volumePrefix}.storeys[${storey.token}]`;
    pushCandidate(out, {
      path: `${sPrefix}.index`,
      kind: 'storey_index',
      value: readStoreyIndex(s),
      source,
      fileName,
      confidence,
      sheetNote: s.label,
    });
    pushCandidate(out, {
      path: `${sPrefix}.innerHeightM`,
      kind: 'inner_height_m',
      value: s.inner_height_m,
      source,
      fileName,
      confidence,
      sheetNote: s.label,
    });
    pushCandidate(out, {
      path: `${sPrefix}.floorToFloorHeightM`,
      kind: 'floor_to_floor_height_m',
      value: s.floor_to_floor_height_m,
      source,
      fileName,
      confidence,
      sheetNote: s.label,
    });
    pushCandidate(out, {
      path: `${sPrefix}.areaM2`,
      kind: 'area_m2',
      value: s.area_m2,
      source,
      fileName,
      confidence,
      sheetNote: s.label,
    });
    s.programs.forEach((p) => {
      pushProgramCandidate(out, {
        storeyPrefix: sPrefix,
        type: p.type,
        areaM2: p.area_m2,
        share: asNumber(p.share),
        label: p.label,
        source,
        fileName,
        confidence,
        sheetNote: s.label,
        rows,
        otherAreaScope: 'zone',
      });
    });
  });
  flushMappedPrograms(out, rows);
};

const flattenVolumeCandidates = (
  row: VolumeAreasAi,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  confidence: number | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  const totals: readonly (readonly [string, number | null])[] = [
    ['ljusBtaM2', row.ljus_bta_m2],
    ['boaM2', row.boa_m2],
    ['loaM2', row.loa_m2],
    ['byaM2', row.bya_m2],
  ];
  for (const [leaf, value] of totals) {
    pushCandidate(out, {
      path: `${prefix}.${leaf}`,
      kind: 'area_total',
      value,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
  }
  pushCandidate(out, {
    path: `${prefix}.apartmentCount`,
    kind: 'apartment_count',
    value: row.apartment_count,
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  row.apartments.forEach((apt, index) => {
    const token = apartmentToken(mapApartmentAi(apt), index);
    const aptPrefix = `${prefix}.apartments[${token}]`;
    pushCandidate(out, {
      path: `${aptPrefix}.label`,
      kind: 'apartment',
      value: trimNull(apt.label),
      source,
      fileName,
      confidence,
      sheetNote: apt.label,
    });
    pushCandidate(out, {
      path: `${aptPrefix}.count`,
      kind: 'apartment',
      value: apt.count,
      source,
      fileName,
      confidence,
      sheetNote: apt.label,
    });
    pushCandidate(out, {
      path: `${aptPrefix}.areaM2`,
      kind: 'apartment',
      value: apt.area_m2,
      source,
      fileName,
      confidence,
      sheetNote: apt.label,
    });
    pushCandidate(out, {
      path: `${aptPrefix}.areaMinM2`,
      kind: 'apartment',
      value: apt.area_min_m2,
      source,
      fileName,
      confidence,
      sheetNote: apt.label,
    });
    pushCandidate(out, {
      path: `${aptPrefix}.areaMaxM2`,
      kind: 'apartment',
      value: apt.area_max_m2,
      source,
      fileName,
      confidence,
      sheetNote: apt.label,
    });
  });
  row.other_areas.forEach((area, index) => {
    const mapped = mapOtherAreaAi(area);
    const token =
      mapped.label === null
        ? otherAreaToken(mapped, index)
        : otherAreaTokenForLabel(out, prefix, mapped.label);
    const areaPrefix = `${prefix}.otherAreas[${token}]`;
    pushCandidate(out, {
      path: `${areaPrefix}.label`,
      kind: 'other_area',
      value: trimNull(area.label),
      source,
      fileName,
      confidence,
      sheetNote: area.label,
    });
    pushCandidate(out, {
      path: `${areaPrefix}.areaM2`,
      kind: 'other_area',
      value: area.area_m2,
      source,
      fileName,
      confidence,
      sheetNote: area.label,
    });
    pushOtherAreaScopeCandidate(out, {
      path: `${areaPrefix}.rowScope`,
      rowScope: area.row_scope,
      source,
      fileName,
      confidence,
      sheetNote: area.label,
    });
  });
};

const SITE_IDENTITY_LEAVES = [
  ['propertyDesignation', 'property_designation'],
  ['detailedPlan', 'detailed_plan'],
  ['client', 'client'],
] as const;

type SiteIdentityKind = (typeof SITE_IDENTITY_LEAVES)[number][1];

/** The identifiers that let a search find the site, from one source. */
const pushSiteIdentityCandidates = (
  out: ExtractedDesignFactCandidate[],
  data: Partial<Record<SiteIdentityKind, unknown>>,
  source: ExtractedDesignFactSource,
  fileName: string,
  confidence: number | null,
): void => {
  for (const [path, kind] of SITE_IDENTITY_LEAVES) {
    pushCandidate(out, {
      path,
      kind,
      value: asString(data[kind]),
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
  }
};

const flattenFactsTree = (
  facts: ExtractedDesignFactsAi,
  source: ExtractedDesignFactSource,
  fileName: string,
  confidence: number | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  pushCandidate(out, {
    path: 'projectName',
    kind: 'project_name',
    value: facts.project_name,
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushCandidate(out, {
    path: 'location',
    kind: 'location',
    value: facts.location,
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushSiteIdentityCandidates(out, facts, source, fileName, confidence);
  pushCandidate(out, {
    path: 'storeyCount',
    kind: 'storey_count',
    value: facts.storey_count,
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushCandidate(out, {
    path: 'buildingCount',
    kind: 'building_count',
    value: facts.building_count,
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  for (const b of facts.buildings) {
    const token = buildingKeyToken(trimNull(b.building_instance_key));
    const prefix = `buildings[${token}]`;
    pushCandidate(out, {
      path: `${prefix}.buildingName`,
      kind: 'building_name',
      value: b.building_name,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.btaM2`,
      kind: 'bta_m2',
      value: b.bta_m2,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    flattenVolumeCandidates(b, prefix, source, fileName, confidence, out);
    if (
      b.window_ratio_determined_by === 'read' ||
      b.window_ratio_determined_by === 'calculated'
    ) {
      pushCandidate(out, {
        path: `${prefix}.windowRatio`,
        kind: 'window_ratio',
        value: b.window_ratio,
        source,
        fileName,
        confidence,
        sheetNote: null,
        windowRatioEstimated: false,
        windowRatioDeterminedBy: b.window_ratio_determined_by,
      });
    }
    pushCandidate(out, {
      path: `${prefix}.beamSpanM`,
      kind: 'beam_span_m',
      value: b.beam_span_m,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.slabThicknessMm`,
      kind: 'slab_thickness_mm',
      value: b.slab_thickness_mm,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.livingAreaPerApartmentM2`,
      kind: 'apartment_size',
      value: b.residential.living_area_per_apartment_m2,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.balconyAreaPerApartmentM2`,
      kind: 'balcony',
      value: b.residential.balcony_area_per_apartment_m2,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.bathroomAreaPerApartmentM2`,
      kind: 'bathroom',
      value: b.residential.bathroom_area_per_apartment_m2,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.programType`,
      kind: 'program_type',
      value: b.residential.program_type,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.stairwells.apartmentsPerStairwellPerStorey`,
      kind: 'stairwells',
      value: b.stairwells.apartments_per_stairwell_per_storey,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.stairwells.elevatorsPerStairwell`,
      kind: 'stairwells',
      value: b.stairwells.elevators_per_stairwell,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.storeyCount`,
      kind: 'storey_count',
      value: b.storey_count,
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    flattenStoreysAi(b.storeys, prefix, source, fileName, confidence, out);
    for (const part of b.parts) {
      const name = trimNull(part.part_name);
      if (name === null) continue;
      const partPrefix = `${prefix}.parts[${buildingPartToken(name)}]`;
      pushCandidate(out, {
        path: `${partPrefix}.name`,
        kind: 'building_name',
        value: name,
        source,
        fileName,
        confidence,
        sheetNote: null,
      });
      pushCandidate(out, {
        path: `${partPrefix}.storeyCount`,
        kind: 'storey_count',
        value: part.storey_count,
        source,
        fileName,
        confidence,
        sheetNote: null,
      });
      flattenVolumeCandidates(
        part,
        partPrefix,
        source,
        fileName,
        confidence,
        out,
      );
      flattenStoreysAi(
        part.storeys,
        partPrefix,
        source,
        fileName,
        confidence,
        out,
      );
    }
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const asString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const READ_FROM_RANK: Record<ReadFrom, number> = {
  direct: 0,
  estimate: 1,
  extrapolated: 2,
};

const ROOF_FORM_LABEL: Record<RoofFormType, string> = {
  flat: 'Plantak',
  sadel: 'Sadeltak',
  pulpet: 'Pulpettak',
  mansard: 'Mansardtak',
};

const ROOF_SOURCE_LABEL: Record<ReadSource, string> = {
  text: 'text',
  geometry: 'geometri',
};

/** Omitted for `direct`: that read is the stated value, not a note. */
const READ_FROM_LABEL: Record<ReadFrom, string | null> = {
  direct: null,
  estimate: 'uppskattning',
  extrapolated: 'extrapolering',
};

const isRoofFormType = (value: string): value is RoofFormType =>
  value === 'flat' ||
  value === 'sadel' ||
  value === 'pulpet' ||
  value === 'mansard';

const pushRoofReads = (
  out: ExtractedDesignFactCandidate[],
  raw: unknown,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  sheet: string | null,
): void => {
  const read = parseBlueprintRoofForm(raw);
  if (read === null) return;
  const base = {
    kind: 'roof_form' as const,
    source,
    fileName,
    confidence: null,
    sheetNote: sheet,
  };
  if (read.type !== null) {
    pushCandidate(out, {
      ...base,
      path: `${prefix}.roofForm`,
      value: read.type.value,
      roofReading: { source: read.type.source, from: read.type.from },
    });
  }
  if (read.pitchDeg !== null) {
    pushCandidate(out, {
      ...base,
      path: `${prefix}.roofPitchDeg`,
      value: read.pitchDeg.value,
      roofReading: { source: read.pitchDeg.source, from: read.pitchDeg.from },
    });
  }
};

const roofMediumRank = (
  field: 'type' | 'pitch',
  source: ReadSource,
): number => {
  if (field === 'type') return source === 'geometry' ? 0 : 1;
  return source === 'text' ? 0 : 1;
};

const pickRoofCandidate = (
  candidates: readonly ExtractedDesignFactCandidate[],
  field: 'type' | 'pitch',
): ExtractedDesignFactCandidate | null => {
  const ranked = candidates
    .filter((candidate) => candidate.roofReading !== undefined)
    .sort((a, b) => {
      const aRead = a.roofReading;
      const bRead = b.roofReading;
      if (aRead === undefined || bRead === undefined) return 0;
      const from = READ_FROM_RANK[aRead.from] - READ_FROM_RANK[bRead.from];
      if (from !== 0) return from;
      const medium =
        roofMediumRank(field, aRead.source) -
        roofMediumRank(field, bRead.source);
      if (medium !== 0) return medium;
      const segment =
        extractedDesignFactSourceRank('roof_form', a.source) -
        extractedDesignFactSourceRank('roof_form', b.source);
      if (segment !== 0) return segment;
      return a.fileName.localeCompare(b.fileName);
    });
  return ranked[0] ?? null;
};

const roofReadFromCandidate = (
  candidate: ExtractedDesignFactCandidate | null,
): Read<string | number> | null => {
  const reading = candidate?.roofReading;
  if (candidate === undefined || candidate === null || reading === undefined) {
    return null;
  }
  return { value: candidate.value, source: reading.source, from: reading.from };
};

const assembleRoofForm = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  prefix: string,
): RoofForm | null => {
  const typeRead = roofReadFromCandidate(
    pickRoofCandidate(byPath.get(`${prefix}.roofForm`) ?? [], 'type'),
  );
  if (typeRead === null || typeof typeRead.value !== 'string') return null;
  if (!isRoofFormType(typeRead.value)) return null;
  const reading = { source: typeRead.source, from: typeRead.from };
  switch (typeRead.value) {
    case 'flat':
      return { type: { value: 'flat', ...reading } };
    case 'sadel':
    case 'pulpet':
    case 'mansard': {
      const pitchRead = roofReadFromCandidate(
        pickRoofCandidate(byPath.get(`${prefix}.roofPitchDeg`) ?? [], 'pitch'),
      );
      const pitchDeg =
        pitchRead === null || typeof pitchRead.value !== 'number'
          ? null
          : {
              value: pitchRead.value,
              source: pitchRead.source,
              from: pitchRead.from,
            };
      return { type: { value: typeRead.value, ...reading }, pitchDeg };
    }
    default:
      return assertUnreachable(typeRead.value);
  }
};

const roofReadMeta = (read: Read<string | number>): string => {
  const source = ROOF_SOURCE_LABEL[read.source];
  const from = READ_FROM_LABEL[read.from];
  if (from === null) return source;
  return `${source} · ${from}`;
};

/** Admin split of a stored roof. Fact is the form and pitch; meta is how each was read. */
export type ExtractedRoofFormLabel = {
  fact: string;
  meta: string;
};

/**
 * Admin label for a stored {@link RoofForm}.
 * Forwards `source` and `from` in the same order as the fact. Does not parse prose.
 */
export const formatExtractedRoofForm = (
  roof: RoofForm,
): ExtractedRoofFormLabel => {
  const typeFact = ROOF_FORM_LABEL[roof.type.value];
  const typeMeta = roofReadMeta(roof.type);
  switch (roof.type.value) {
    case 'flat':
      return { fact: typeFact, meta: typeMeta };
    case 'sadel':
    case 'pulpet':
    case 'mansard': {
      // The switch narrows `type.value`. Flat has no `pitchDeg`, so the parent
      // is still the union until the property is present.
      if (!('pitchDeg' in roof) || roof.pitchDeg === null) {
        return { fact: typeFact, meta: typeMeta };
      }
      const pitch = roof.pitchDeg;
      return {
        fact: `${typeFact}, ${pitch.value}°`,
        meta: `${typeMeta}, ${roofReadMeta(pitch)}`,
      };
    }
    default:
      return assertUnreachable(roof.type);
  }
};

/** {@link formatExtractedRoofForm} for the stored FFU roof (`geometry.roof`). */
export const formatFfuRoof = (roof: FfuRoof): ExtractedRoofFormLabel => {
  const meta = (read: FfuRoof['formRead']): string | null => {
    if (read === null) return null;
    const from = READ_FROM_LABEL[read.from];
    const source = ROOF_SOURCE_LABEL[read.source];
    return from === null ? source : `${source} · ${from}`;
  };
  const typeFact = ROOF_FORM_LABEL[roof.form];
  const fact =
    roof.pitchDeg === null ? typeFact : `${typeFact}, ${roof.pitchDeg}°`;
  const metas = [meta(roof.formRead), meta(roof.pitchRead)].filter(
    (item): item is string => item !== null,
  );
  return { fact, meta: metas.join(', ') };
};

const readConfidence = (data: Record<string, unknown>): number | null =>
  asNumber(data.confidence);

const blueprintSubtypeForSegment = (
  file: Pick<ProjectFile, 'classifications'>,
  segmentIndex: number,
): BlueprintSubtype | null => {
  const env = file.classifications[segmentIndex];
  return env?.data.blueprintSubtype ?? null;
};

const pushStoreyMeasure = (
  out: ExtractedDesignFactCandidate[],
  volumePrefix: string,
  storeyToken: string,
  leaf: 'innerHeightM' | 'floorToFloorHeightM' | 'areaM2',
  kind: 'inner_height_m' | 'floor_to_floor_height_m' | 'area_m2',
  value: number | null,
  source: ExtractedDesignFactSource,
  fileName: string,
  sheetNote: string | null,
): void => {
  pushCandidate(out, {
    path: `${volumePrefix}.storeys[${storeyToken}].${leaf}`,
    kind,
    value,
    source,
    fileName,
    confidence: null,
    sheetNote,
  });
};

/**
 * Path id for a row the model left unindexed. The printed name is copied
 * through so the row can keep its label. It is not looked up and not an index.
 */
const UNPLACED_STOREY_PREFIX = 'u_';

const unplacedStoreyToken = (label: string): string => {
  const slug = normalizeExtractedBuildingName(label);
  return `${UNPLACED_STOREY_PREFIX}${slug === '' ? '_' : slug}`;
};

const isUnplacedStoreyToken = (token: string): boolean =>
  token.startsWith(UNPLACED_STOREY_PREFIX);

/**
 * Index the model assigned on this row. Absent or null stays empty.
 * A present index is the identity, whatever the label says.
 */
const assignedStoreyIndex = (row: Record<string, unknown>): number | null => {
  if (!Object.prototype.hasOwnProperty.call(row, 'storey_index')) return null;
  if (row.storey_index === null) return null;
  const index = asNumber(row.storey_index);
  if (index === null || !Number.isInteger(index)) return null;
  return index;
};

const storeyKeyForExtractedRow = (
  row: Record<string, unknown>,
): CanonicalStoreyKey | null => {
  const index = assignedStoreyIndex(row);
  if (index !== null) return storeyIndexKey(index);
  const label = trimNull(asString(row.storey_label));
  if (label === null) return null;
  return {
    token: unplacedStoreyToken(label),
    label,
    planNumbers: [],
    index: null,
    labelledKind: null,
  };
};

const pushAssignedStoreyIndex = (
  out: ExtractedDesignFactCandidate[],
  volumePrefix: string,
  storey: CanonicalStoreyKey,
  label: string | null,
  source: ExtractedDesignFactSource,
  fileName: string,
): void => {
  if (storey.index === null) return;
  pushCandidate(out, {
    path: `${volumePrefix}.storeys[${storey.token}].index`,
    kind: 'storey_index',
    value: storey.index,
    source,
    fileName,
    confidence: null,
    sheetNote: label,
  });
};

/**
 * Floor-to-floor height a row leaves out, from the floor levels this drawing
 * prints on it and on the storey above in the same part. Null when either
 * level is missing.
 */
const heightFromFloorLevels = (
  height: Record<string, unknown>,
  rows: readonly Record<string, unknown>[],
): number | null => {
  const level = asNumber(height.floor_level_m);
  const index = asNumber(height.storey_index);
  if (level === null || index === null) return null;
  const part = trimNull(asString(height.part_name));
  const above = rows.find(
    (row) =>
      asNumber(row.storey_index) === index + 1 &&
      trimNull(asString(row.part_name)) === part,
  );
  const aboveLevel = above === undefined ? null : asNumber(above.floor_level_m);
  if (aboveLevel === null || aboveLevel <= level) return null;
  return Math.round((aboveLevel - level) * 100) / 100;
};

/**
 * One structured height row per storey and part. A label that is not already
 * a storey name is left on the row; this does not invent a storey. A row
 * without a printed floor-to-floor height takes the difference between the
 * floor levels printed on it and on the storey above.
 */
const forwardBlueprintStoreyHeights = (
  row: Record<string, unknown>,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  sheet: string | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  if (!Array.isArray(row.storey_heights)) return;
  const rows = row.storey_heights.flatMap((raw) => {
    const height = asRecord(raw);
    return height === null ? [] : [height];
  });
  for (const height of rows) {
    const storey = storeyKeyForExtractedRow(height);
    if (storey == null) continue;
    const partName = trimNull(asString(height.part_name));
    const volumePrefix =
      partName === null
        ? prefix
        : `${prefix}.parts[${buildingPartToken(partName)}]`;
    if (partName !== null) {
      pushCandidate(out, {
        path: `${volumePrefix}.name`,
        kind: 'building_name',
        value: partName,
        source,
        fileName,
        confidence: null,
        sheetNote: sheet,
      });
    }
    const note = asString(height.storey_label) ?? sheet;
    pushAssignedStoreyIndex(
      out,
      volumePrefix,
      storey,
      asString(height.storey_label),
      source,
      fileName,
    );
    pushStoreyMeasure(
      out,
      volumePrefix,
      storey.token,
      'innerHeightM',
      'inner_height_m',
      asNumber(height.inner_height_m),
      source,
      fileName,
      note,
    );
    pushStoreyMeasure(
      out,
      volumePrefix,
      storey.token,
      'floorToFloorHeightM',
      'floor_to_floor_height_m',
      asNumber(height.floor_to_floor_height_m) ??
        heightFromFloorLevels(height, rows),
      source,
      fileName,
      note,
    );
    pushStoreyMeasure(
      out,
      volumePrefix,
      storey.token,
      'areaM2',
      'area_m2',
      asNumber(height.area_m2),
      source,
      fileName,
      note,
    );
  }
};

/** A floor or building sum is not a zone, so it never becomes a program row. */
const isBlueprintProgramAggregate = (rowScope: unknown): boolean =>
  rowScope === 'storey_total' || rowScope === 'building_total';

/** Each program row is forwarded on its own. Areas are not added together. */
const forwardBlueprintPrograms = (
  row: Record<string, unknown>,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  out: ExtractedDesignFactCandidate[],
): void => {
  if (!Array.isArray(row.programs)) return;
  const rows = newProgramRows();
  for (const raw of row.programs) {
    const program = asRecord(raw);
    if (program === null) continue;
    const parsed = extractedProgramTypeSchema.safeParse(program.type);
    if (!parsed.success) continue;
    if (isBlueprintProgramAggregate(program.row_scope)) continue;
    const storey = storeyKeyForExtractedRow(program);
    if (storey == null) continue;
    const partName = trimNull(asString(program.part_name));
    const volumePrefix =
      partName === null
        ? prefix
        : `${prefix}.parts[${buildingPartToken(partName)}]`;
    if (partName !== null) {
      pushCandidate(out, {
        path: `${volumePrefix}.name`,
        kind: 'building_name',
        value: partName,
        source,
        fileName,
        confidence: null,
        sheetNote: asString(program.storey_label),
      });
    }
    pushAssignedStoreyIndex(
      out,
      volumePrefix,
      storey,
      asString(program.storey_label),
      source,
      fileName,
    );
    pushProgramCandidate(out, {
      storeyPrefix: `${volumePrefix}.storeys[${storey.token}]`,
      type: parsed.data,
      areaM2: asNumber(program.area_m2),
      share: asNumber(program.share),
      label: asString(program.label),
      source,
      fileName,
      confidence: null,
      sheetNote: asString(program.storey_label),
      rows,
      otherAreaScope: 'zone',
    });
  }
  flushMappedPrograms(out, rows);
};

const windowRatioDeterminedByFromUnknown = (
  value: unknown,
): WindowRatioDeterminedBy | null =>
  value === 'read' || value === 'calculated' || value === 'estimated'
    ? value
    : null;

/**
 * Apartments on the storey with the most apartments, summed from the
 * residential zone rows the plan shows. Null when no row states a count.
 */
const maxApartmentsOnOneBlueprintStorey = (
  row: Record<string, unknown>,
): number | null => {
  if (!Array.isArray(row.programs)) return null;
  const byStorey = new Map<string, number>();
  for (const raw of row.programs) {
    const program = asRecord(raw);
    if (program === null) continue;
    if (program.type !== 'residential') continue;
    if (isBlueprintProgramAggregate(program.row_scope)) continue;
    const count = asNumber(program.apartment_count);
    if (count === null || count <= 0) continue;
    const storey = storeyKeyForExtractedRow(program);
    if (storey == null) continue;
    byStorey.set(storey.token, (byStorey.get(storey.token) ?? 0) + count);
  }
  return byStorey.size === 0 ? null : Math.max(...byStorey.values());
};

/**
 * Stairwells a plan sheet shows: its distinct printed tags, else the model's
 * own count. Whether the sheet is complete is not asked here: sheets that
 * disagree are resolved by {@link pickMostLikelyExtractedDesignFact}, which
 * prefers the value several sheets agree on.
 */
const stairwellsOnBlueprint = (row: Record<string, unknown>): number | null => {
  const tags = Array.isArray(row.stairwell_tags)
    ? new Set(
        row.stairwell_tags.flatMap((tag) => {
          const text = typeof tag === 'string' ? tag.trim().toLowerCase() : '';
          return text === '' ? [] : [text];
        }),
      )
    : new Set<string>();
  return tags.size > 0 ? tags.size : asNumber(row.stairwell_count);
};

/** Apartments per stairwell on the busiest storey, from counts one plan shows. */
export const apartmentsPerStairwellFromBlueprintRow = (
  row: Record<string, unknown>,
): number | null => {
  const stairwells = stairwellsOnBlueprint(row);
  if (stairwells === null || stairwells <= 0) return null;
  const apartments = maxApartmentsOnOneBlueprintStorey(row);
  return apartments === null ? null : apartments / stairwells;
};

const pushBlueprintResidentialReads = (
  row: Record<string, unknown>,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  sheet: string | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  const determinedBy = windowRatioDeterminedByFromUnknown(
    row.bathroom_area_determined_by,
  );
  if (determinedBy !== null) {
    pushCandidate(out, {
      path: `${prefix}.residential.bathroomAreaPerApartmentM2`,
      kind: 'bathroom',
      value: asNumber(row.bathroom_area_per_apartment_m2),
      source,
      fileName,
      confidence: null,
      sheetNote: sheet,
    });
  }
  pushCandidate(out, {
    path: `${prefix}.stairwells.apartmentsPerStairwellPerStorey`,
    kind: 'stairwells',
    value: apartmentsPerStairwellFromBlueprintRow(row),
    source,
    fileName,
    confidence: null,
    sheetNote: sheet,
  });
};

const impliedExactStoreyCountFromHeightRows = (
  heights: readonly Record<string, unknown>[],
): number | null => {
  const indexes = heights.flatMap((raw) => {
    const index = asNumber(raw.storey_index);
    return index === null || index < 0 ? [] : [index];
  });
  if (indexes.length > 0) return Math.max(...indexes) + 1;
  return heights.length === 0 ? null : heights.length;
};

/**
 * When one section row dimensions several parts, each part's count is the
 * stack that row shows for that part — not the building-level table count.
 */
const pushSectionPartStoreyCountsFromHeights = (
  row: Record<string, unknown>,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  sheet: string | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  if (!Array.isArray(row.storey_heights)) return;
  const byPart = new Map<string, Record<string, unknown>[]>();
  for (const raw of row.storey_heights) {
    const height = asRecord(raw);
    if (height === null) continue;
    const partName = trimNull(asString(height.part_name));
    if (partName === null) continue;
    const token = buildingPartToken(partName);
    const list = byPart.get(token) ?? [];
    list.push(height);
    byPart.set(token, list);
  }
  if (byPart.size < 2) return;
  for (const [partToken, heights] of byPart) {
    const count = impliedExactStoreyCountFromHeightRows(heights);
    if (count === null) continue;
    pushCandidate(out, {
      path: `${prefix}.parts[${partToken}].storeyCount`,
      kind: 'storey_count',
      value: count,
      source,
      fileName,
      confidence: null,
      sheetNote: sheet,
    });
  }
};

/** One part name when every height row names the same part, else the row label. */
const partNameFromSectionRow = (
  row: Record<string, unknown>,
): string | null => {
  const heights = row.storey_heights;
  if (!Array.isArray(heights)) return trimNull(asString(row.building_label));
  const names = heights.flatMap((raw) => {
    const height = asRecord(raw);
    if (height === null) return [];
    const part = trimNull(asString(height.part_name));
    return part === null ? [] : [part];
  });
  if (names.length === 0) return trimNull(asString(row.building_label));
  const unique = [...new Set(names)];
  if (unique.length !== 1) return null;
  const only = unique[0];
  return only === undefined ? null : only;
};

const collectFromBlueprintRow = (
  row: Record<string, unknown>,
  source: ExtractedDesignFactSource,
  fileName: string,
  out: ExtractedDesignFactCandidate[],
): void => {
  const key = trimNull(asString(row.building_instance_key));
  const prefix = `buildings[${buildingKeyToken(key)}]`;
  const sheet = asString(row.sheet_scope_note);
  const isSection = source === 'blueprint_side_view';
  pushCandidate(out, {
    path: `${prefix}.buildingName`,
    kind: 'building_name',
    value: asString(row.building_label),
    source,
    fileName,
    confidence: null,
    sheetNote: sheet,
  });
  const windowRatio = asNumber(row.window_ratio);
  const determinedBy = windowRatioDeterminedByFromUnknown(
    row.window_ratio_determined_by,
  );
  const keepWindowRatio =
    windowRatio !== null &&
    (determinedBy === 'read' ||
      determinedBy === 'calculated' ||
      (determinedBy === 'estimated' && source === 'blueprint_side_view') ||
      (determinedBy === null && source === 'blueprint_side_view'));
  if (keepWindowRatio && windowRatio !== null) {
    const resolved =
      determinedBy ?? (source === 'blueprint_side_view' ? 'estimated' : null);
    pushCandidate(out, {
      path: `${prefix}.windowRatio`,
      kind: 'window_ratio',
      value: windowRatio,
      source,
      fileName,
      confidence: null,
      sheetNote: sheet,
      windowRatioEstimated: resolved === 'estimated',
      windowRatioDeterminedBy: resolved,
    });
  }
  pushRoofReads(out, row.roof_form, prefix, source, fileName, sheet);
  pushCandidate(out, {
    path: `${prefix}.slabThicknessMm`,
    kind: 'slab_thickness_mm',
    value: asNumber(row.slab_thickness_mm),
    source,
    fileName,
    confidence: null,
    sheetNote: sheet,
  });
  forwardBlueprintStoreyHeights(row, prefix, source, fileName, sheet, out);
  forwardBlueprintPrograms(row, prefix, source, fileName, out);
  if (!isSection) {
    pushBlueprintResidentialReads(row, prefix, source, fileName, sheet, out);
  }
  if (isSection) {
    pushSectionPartStoreyCountsFromHeights(
      row,
      prefix,
      source,
      fileName,
      sheet,
      out,
    );
    pushCandidate(out, {
      path: `${prefix}.innerHeightTypical`,
      kind: 'inner_height_m',
      value: asNumber(row.inner_height_m),
      source,
      fileName,
      confidence: null,
      sheetNote: sheet,
    });
    const storeyCount =
      asNumber(row.storey_count) ??
      asNumber(row.storeyCount) ??
      asNumber(row.floors_count);
    const sectionPartName = partNameFromSectionRow(row);
    if (sectionPartName !== null) {
      const partPrefix = `${prefix}.parts[${buildingPartToken(sectionPartName)}]`;
      if (storeyCount !== null) {
        pushCandidate(out, {
          path: `${partPrefix}.storeyCount`,
          kind: 'storey_count',
          value: storeyCount,
          source,
          fileName,
          confidence: null,
          sheetNote: sheet,
        });
      }
    } else {
      pushCandidate(out, {
        path: `${prefix}.storeyCount`,
        kind: 'storey_count',
        value: storeyCount,
        source,
        fileName,
        confidence: null,
        sheetNote: sheet,
      });
      pushCandidate(out, {
        path: 'storeyCount',
        kind: 'storey_count',
        value: storeyCount,
        source,
        fileName,
        confidence: null,
        sheetNote: sheet,
      });
    }
    const heights = row.storeyHeightsMeters;
    if (Array.isArray(heights)) {
      heights.forEach((h, i) => {
        const storey = canonicalStoreyKey({ index: i });
        if (storey == null) return;
        pushCandidate(out, {
          path: `${prefix}.storeys[${storey.token}].floorToFloorHeightM`,
          kind: 'floor_to_floor_height_m',
          value: asNumber(h),
          source,
          fileName,
          confidence: null,
          sheetNote: sheet,
        });
        pushCandidate(out, {
          path: `${prefix}.storeys[${storey.token}].index`,
          kind: 'storey_index',
          value: i,
          source,
          fileName,
          confidence: null,
          sheetNote: null,
        });
      });
    }
    return;
  }
  const storey = canonicalStoreyKey({ label: sheet });
  if (storey == null) return;
  const floorArea =
    asNumber(row.area_per_floor_m2) ?? asNumber(row.total_area_m2);
  pushCandidate(out, {
    path: `${prefix}.storeys[${storey.token}].areaM2`,
    kind: 'area_m2',
    value: floorArea,
    source,
    fileName,
    confidence: null,
    sheetNote: sheet,
  });
};

const pushLooseResidentialCandidates = (
  data: Record<string, unknown>,
  prefix: string,
  source: ExtractedDesignFactSource,
  fileName: string,
  confidence: number | null,
  out: ExtractedDesignFactCandidate[],
): void => {
  const residential = asRecord(data.residential);
  if (residential !== null) {
    pushCandidate(out, {
      path: `${prefix}.residential.livingAreaPerApartmentM2`,
      kind: 'apartment_size',
      value: asNumber(residential.living_area_per_apartment_m2),
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.balconyAreaPerApartmentM2`,
      kind: 'balcony',
      value: asNumber(residential.balcony_area_per_apartment_m2),
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.bathroomAreaPerApartmentM2`,
      kind: 'bathroom',
      value: asNumber(residential.bathroom_area_per_apartment_m2),
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.residential.programType`,
      kind: 'program_type',
      value: residentialProgramForm(asString(residential.program_type)),
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
  }
  const stairwells = asRecord(data.stairwells);
  if (stairwells === null) return;
  pushCandidate(out, {
    path: `${prefix}.stairwells.apartmentsPerStairwellPerStorey`,
    kind: 'stairwells',
    value: asNumber(stairwells.apartments_per_stairwell_per_storey),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushCandidate(out, {
    path: `${prefix}.stairwells.elevatorsPerStairwell`,
    kind: 'stairwells',
    value: asNumber(stairwells.elevators_per_stairwell),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
};

type StoreyAreaScheduleRowScope = 'per_storey' | 'building_total';

type TypicalAreaScheduleRowScope = 'typical_per_storey' | 'typical_sum';

type AreaScheduleRowScope =
  StoreyAreaScheduleRowScope | TypicalAreaScheduleRowScope;

const TYPICAL_AREA_SCOPE_OF_ROW: Record<
  TypicalAreaScheduleRowScope,
  TypicalAreaScope
> = {
  typical_per_storey: 'per_storey',
  typical_sum: 'sum',
};

const isTypicalAreaScheduleRowScope = (
  scope: AreaScheduleRowScope,
): scope is TypicalAreaScheduleRowScope => scope in TYPICAL_AREA_SCOPE_OF_ROW;

/**
 * Batches read before the typical scope was split print `typical_aggregate`,
 * an area over the typical storeys together.
 */
const parseAreaScheduleRowScope = (
  value: unknown,
): AreaScheduleRowScope | null => {
  if (value === 'typical_aggregate') return 'typical_sum';
  if (
    value === 'per_storey' ||
    value === 'building_total' ||
    value === 'typical_per_storey' ||
    value === 'typical_sum'
  ) {
    return value;
  }
  return null;
};

const storeyKeyForAreaScheduleZone = (
  zone: Record<string, unknown>,
): CanonicalStoreyKey | null => {
  const planNumber = asNumber(zone.plan_number);
  if (planNumber !== null && Number.isInteger(planNumber)) {
    return canonicalStoreyKey({ label: formatPlanNumbers([planNumber]) });
  }
  return canonicalStoreyKey({ label: asString(zone.floor) });
};

const inferredAreaScheduleRowScope = (
  zone: Record<string, unknown>,
  storeyKey: CanonicalStoreyKey | null,
): AreaScheduleRowScope => {
  const explicit = parseAreaScheduleRowScope(zone.row_scope);
  if (explicit !== null) return explicit;
  const planNumber = asNumber(zone.plan_number);
  if (planNumber !== null && Number.isInteger(planNumber)) return 'per_storey';
  const floor = asString(zone.floor);
  if (floor === null) return 'building_total';
  if ((storeyKey?.planNumbers.length ?? 0) > 0) return 'per_storey';
  if (storeyKey?.labelledKind === 'typical') return 'typical_per_storey';
  return 'per_storey';
};

/** Area-table rows that are not one scored storey never open `storeys[token]`. */
const areaScheduleRowTargetsStorey = (
  zone: Record<string, unknown>,
): boolean => {
  const storeyKey = storeyKeyForAreaScheduleZone(zone);
  const scope = inferredAreaScheduleRowScope(zone, storeyKey);
  if (scope === 'building_total' || isTypicalAreaScheduleRowScope(scope)) {
    return false;
  }
  const planNumber = asNumber(zone.plan_number);
  if (planNumber !== null && Number.isInteger(planNumber)) return true;
  if ((storeyKey?.planNumbers.length ?? 0) > 0) return true;
  if (storeyKey?.labelledKind != null) {
    return (
      storeyKey.labelledKind !== 'typical' && storeyKey.labelledKind !== 'roof'
    );
  }
  const kindParse = extractedAreaKindSchema.safeParse(zone.area_kind);
  const areaKind = kindParse.success ? kindParse.data : 'unknown';
  if (areaKind === 'bta') return false;
  return false;
};

const isMaterializedStoreyToken = (storeyToken: string): boolean => {
  const canonical = parseCanonicalStoreyToken(storeyToken);
  if (canonical?.labelledKind === 'roof') return false;
  return storeyToken !== 'roof';
};

const OTHER_AREA_SCOPE_OF_SCHEDULE_ROW: Record<
  StoreyAreaScheduleRowScope,
  ExtractedOtherAreaRowScope
> = {
  per_storey: 'zone',
  building_total: 'building_total',
};

/** Null for a typical row: it is no zone, floor or building total. */
const otherAreaScopeOfScheduleRow = (
  scope: AreaScheduleRowScope,
): ExtractedOtherAreaRowScope | null =>
  isTypicalAreaScheduleRowScope(scope)
    ? null
    : OTHER_AREA_SCOPE_OF_SCHEDULE_ROW[scope];

const parseOtherAreaRowScope = (
  value: unknown,
): ExtractedOtherAreaRowScope | null => {
  const parsed = extractedOtherAreaRowScopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

const pushAreaScheduleZoneToOtherAreas = (
  out: ExtractedDesignFactCandidate[],
  input: {
    prefix: string;
    label: string;
    area: number;
    rowScope: ExtractedOtherAreaRowScope | null;
    source: ExtractedDesignFactSource;
    fileName: string;
    confidence: number | null;
    sheetNote: string | null;
  },
): void => {
  const token = otherAreaTokenForLabel(out, input.prefix, input.label);
  const areaPrefix = `${input.prefix}.otherAreas[${token}]`;
  pushOtherAreaScopeCandidate(out, {
    path: `${areaPrefix}.rowScope`,
    rowScope: input.rowScope,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
  pushCandidate(out, {
    path: `${areaPrefix}.label`,
    kind: 'other_area',
    value: input.label,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
  pushCandidate(out, {
    path: `${areaPrefix}.areaM2`,
    kind: 'other_area',
    value: input.area,
    source: input.source,
    fileName: input.fileName,
    confidence: input.confidence,
    sheetNote: input.sheetNote,
  });
};

/** Canonical ids an area-schedule row says it is printed for. */
const areaScheduleRowBuildingKeys = (
  zone: Record<string, unknown>,
): readonly string[] => {
  const raw = zone.building_instance_keys;
  if (!Array.isArray(raw)) return [];
  const keys = raw.flatMap((value) => {
    const key = canonicalBuildingInstanceKey(asString(value));
    return key === null ? [] : [key];
  });
  return [...new Set(keys)].sort();
};

/** Canonical ids the classifier states for a segment, never read from its text. */
const statedClassifierBuildingKeys = (
  data:
    | { buildingInstanceId?: string | null; buildingInstanceIds?: string[] }
    | undefined,
): readonly string[] => {
  const keys = [
    data?.buildingInstanceId ?? null,
    ...(data?.buildingInstanceIds ?? []),
  ].flatMap((id) => {
    const key = canonicalBuildingInstanceKey(id);
    return key === null ? [] : [key];
  });
  return [...new Set(keys)].sort();
};

/** Tags the candidates from `start` on with the buildings their row covers. */
const markCoveredBuildings = (
  out: ExtractedDesignFactCandidate[],
  start: number,
  keys: readonly string[],
): void => {
  if (keys.length < 2) return;
  for (let index = start; index < out.length; index += 1) {
    const candidate = out[index];
    if (candidate === undefined) continue;
    out[index] = { ...candidate, coversBuildingKeys: keys };
  }
};

type AreaScheduleZoneGroup = {
  prefix: string;
  covers: readonly string[];
  zones: Record<string, unknown>[];
};

/**
 * Building prefix for rows printed for `keys`. One id is that building.
 * Several ids are no single building. None keep the table's own key.
 */
const areaScheduleRowPrefix = (
  keys: readonly string[],
  tablePrefix: string,
): string => {
  const [only] = keys;
  if (keys.length === 1 && only !== undefined) return `buildings[${only}]`;
  if (keys.length > 1) return `buildings[${buildingKeyToken(null)}]`;
  return tablePrefix;
};

/** Area-schedule rows grouped by the buildings each row is printed for. */
const areaScheduleZoneGroups = (
  zones: readonly Record<string, unknown>[],
  tablePrefix: string,
): AreaScheduleZoneGroup[] => {
  const groups = new Map<string, AreaScheduleZoneGroup>();
  for (const zone of zones) {
    const keys = areaScheduleRowBuildingKeys(zone);
    const prefix = areaScheduleRowPrefix(keys, tablePrefix);
    const covers = keys.length > 1 ? keys : [];
    const id = `${prefix}|${covers.join('+')}`;
    const group = groups.get(id) ?? { prefix, covers, zones: [] };
    group.zones.push(zone);
    groups.set(id, group);
  }
  return [...groups.values()];
};

const collectFromAreaSchedule = (
  data: Record<string, unknown>,
  file: Pick<ProjectFile, 'fileName' | 'classifications'>,
  segmentIndex: number,
  out: ExtractedDesignFactCandidate[],
): void => {
  const source: ExtractedDesignFactSource = 'area_schedule';
  const fileName = file.fileName;
  const confidence = readConfidence(data);
  const key = trimNull(asString(data.building_instance_key));
  const prefix = `buildings[${buildingKeyToken(key)}]`;
  const tableStart = out.length;
  pushCandidate(out, {
    path: `${prefix}.buildingName`,
    kind: 'building_name',
    value: asString(data.building_name),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushCandidate(out, {
    path: `${prefix}.btaM2`,
    kind: 'bta_m2',
    value: asNumber(data.total_area_m2),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushCandidate(out, {
    path: `${prefix}.storeyCount`,
    kind: 'storey_count',
    value: asNumber(data.storey_count),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  pushCandidate(out, {
    path: 'storeyCount',
    kind: 'storey_count',
    value: asNumber(data.storey_count),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
  flattenVolumeCandidates(
    {
      ljus_bta_m2: asNumber(data.ljus_bta_m2),
      boa_m2: asNumber(data.boa_m2),
      loa_m2: asNumber(data.loa_m2),
      bya_m2: asNumber(data.bya_m2),
      apartment_count: asNumber(data.apartment_count),
      apartments: readVolumeRows(data.apartments, (row) => ({
        label: asString(row.label),
        count: asNumber(row.count),
        area_m2: asNumber(row.area_m2),
        area_min_m2: asNumber(row.area_min_m2),
        area_max_m2: asNumber(row.area_max_m2),
      })),
      other_areas: readVolumeRows(data.other_areas, (row) => ({
        label: asString(row.label),
        area_m2: asNumber(row.area_m2),
        row_scope: parseOtherAreaRowScope(row.row_scope),
      })),
    },
    prefix,
    source,
    fileName,
    confidence,
    out,
  );
  pushLooseResidentialCandidates(
    data,
    prefix,
    source,
    fileName,
    confidence,
    out,
  );
  if (key === null) {
    markCoveredBuildings(
      out,
      tableStart,
      statedClassifierBuildingKeys(file.classifications[segmentIndex]?.data),
    );
  }
  const zones = data.areas_by_zone;
  if (!Array.isArray(zones)) return;
  const records = zones.flatMap((raw) => {
    const zone = asRecord(raw);
    return zone === null ? [] : [zone];
  });
  for (const group of areaScheduleZoneGroups(records, prefix)) {
    const start = out.length;
    collectAreaScheduleZones(group.zones, group.prefix, {
      source,
      fileName,
      confidence,
      out,
    });
    markCoveredBuildings(out, start, group.covers);
  }
};

const wholeBuildingAreaLeaf = (kind: string): string | null => {
  switch (kind) {
    case 'ljus_bta':
      return 'ljusBtaM2';
    case 'mork_bta':
      return 'morkBtaM2';
    case 'boa':
      return 'boaM2';
    case 'loa':
      return 'loaM2';
    case 'bya':
      return 'byaM2';
    default:
      return null;
  }
};

const collectAreaScheduleZones = (
  zones: readonly Record<string, unknown>[],
  prefix: string,
  context: {
    source: ExtractedDesignFactSource;
    fileName: string;
    confidence: number | null;
    out: ExtractedDesignFactCandidate[];
  },
): void => {
  const { source, fileName, confidence, out } = context;
  const rows = newProgramRows();
  zones.forEach((zone) => {
    const area = asNumber(zone.area_m2);
    const statedShare = asNumber(zone.share);
    const floor = asString(zone.floor);
    const kindParse = extractedAreaKindSchema.safeParse(zone.area_kind);
    const areaKind = kindParse.success ? kindParse.data : 'unknown';
    const activityParse = extractedProgramTypeSchema.safeParse(
      zone.activity_type,
    );
    const storeyKey = storeyKeyForAreaScheduleZone(zone);
    const rowScope = inferredAreaScheduleRowScope(zone, storeyKey);
    const isBuildingTotal = floor == null || rowScope === 'building_total';
    const targetsStorey = areaScheduleRowTargetsStorey(zone);
    const storey = targetsStorey ? storeyKey : null;
    const storeyToken = storey?.token ?? null;
    const namedProgram =
      activityParse.success && activityParse.data !== 'other'
        ? activityParse.data
        : null;
    const isNamedProgram = namedProgram !== null;
    const zoneNote = asString(zone.zone);
    if (areaKind === 'bta' && isBuildingTotal && !isNamedProgram) {
      pushCandidate(out, {
        path: `${prefix}.btaM2`,
        kind: 'bta_m2',
        value: area,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
    }
    if (areaKind === 'bta' && isBuildingTotal && namedProgram !== null) {
      pushCandidate(out, {
        path: `${prefix}.btaOfUse[${namedProgram}]`,
        kind: 'area_total',
        value: area,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
    }
    if (
      areaKind === 'bta' &&
      isTypicalAreaScheduleRowScope(rowScope) &&
      storeyKey !== null &&
      !isNamedProgram
    ) {
      pushCandidate(out, {
        path: `${prefix}.storeys[${storeyKey.token}].areaM2`,
        kind: 'area_m2',
        value: area,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
        typicalAreaScope: TYPICAL_AREA_SCOPE_OF_ROW[rowScope],
      });
    }
    if (
      areaKind === 'bta' &&
      floor != null &&
      storeyToken != null &&
      !isNamedProgram
    ) {
      pushCandidate(out, {
        path: `${prefix}.storeys[${storeyToken}].areaM2`,
        kind: 'area_m2',
        value: area,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
    }
    const printedZone = asString(zone.zone);
    const unmappedProgram =
      activityParse.success &&
      activityParse.data === 'other' &&
      printedZone !== null &&
      areaKind !== 'bta';
    if (
      activityParse.success &&
      storeyToken != null &&
      !isTypicalAreaScheduleRowScope(rowScope) &&
      (activityParse.data !== 'other' || unmappedProgram)
    ) {
      pushProgramCandidate(out, {
        storeyPrefix: `${prefix}.storeys[${storeyToken}]`,
        type: activityParse.data,
        areaM2: area,
        share: statedShare,
        label: printedZone,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
        measure: areaKind,
        rows,
        otherAreaScope: OTHER_AREA_SCOPE_OF_SCHEDULE_ROW[rowScope],
      });
    }
    const wholeLeaf =
      isBuildingTotal && !isNamedProgram
        ? wholeBuildingAreaLeaf(areaKind)
        : null;
    if (wholeLeaf !== null) {
      pushCandidate(out, {
        path: `${prefix}.${wholeLeaf}`,
        kind: 'area_total',
        value: area,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
    }
    const zoneLabel = asString(zone.zone);
    const mayOtherAreaRow =
      zoneLabel !== null &&
      area !== null &&
      areaKind !== 'bta' &&
      !APARTMENT_TYPE_LABEL_RE.test(zoneLabel);
    const mappedPremisesProgram =
      activityParse.success &&
      activityParse.data !== 'other' &&
      activityParse.data !== 'residential' &&
      activityParse.data !== 'stairwells';
    const premisesAreaKind = areaKind === 'loa' || areaKind === 'unknown';
    const shouldForwardPremisesToBuildingOtherAreas =
      mayOtherAreaRow &&
      mappedPremisesProgram &&
      premisesAreaKind &&
      (rowScope === 'building_total' ||
        (rowScope === 'per_storey' && targetsStorey));
    if (shouldForwardPremisesToBuildingOtherAreas) {
      const partName = trimNull(asString(zone.part_name));
      const otherAreasPrefix =
        partName !== null && rowScope !== 'building_total'
          ? (() => {
              const partPrefix = `${prefix}.parts[${buildingPartToken(partName)}]`;
              pushCandidate(out, {
                path: `${partPrefix}.name`,
                kind: 'building_name',
                value: partName,
                source,
                fileName,
                confidence,
                sheetNote: zoneNote,
              });
              return partPrefix;
            })()
          : prefix;
      pushAreaScheduleZoneToOtherAreas(out, {
        prefix: otherAreasPrefix,
        label: zoneLabel,
        area,
        rowScope: otherAreaScopeOfScheduleRow(rowScope),
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
      if (rowScope === 'building_total' && mappedPremisesProgram) return;
    }
    if (floor != null || wholeLeaf !== null || areaKind === 'bta') return;
    const label = asString(zone.zone);
    if (label === null || area === null) return;
    if (APARTMENT_TYPE_LABEL_RE.test(label)) {
      const token = apartmentToken(
        {
          label,
          areaM2: area,
          areaMinM2: null,
          areaMaxM2: null,
        },
        0,
      );
      const aptPrefix = `${prefix}.apartments[${token}]`;
      pushCandidate(out, {
        path: `${aptPrefix}.label`,
        kind: 'apartment',
        value: label,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
      pushCandidate(out, {
        path: `${aptPrefix}.areaM2`,
        kind: 'apartment',
        value: area,
        source,
        fileName,
        confidence,
        sheetNote: zoneNote,
      });
      return;
    }
    pushAreaScheduleZoneToOtherAreas(out, {
      prefix,
      label,
      area,
      rowScope: otherAreaScopeOfScheduleRow(rowScope),
      source,
      fileName,
      confidence,
      sheetNote: zoneNote,
    });
  });
  flushMappedPrograms(out, rows);
};

const readVolumeRows = <T>(
  value: unknown,
  mapRow: (row: Record<string, unknown>) => T,
): T[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    const row = asRecord(raw);
    return row === null ? [] : [mapRow(row)];
  });
};

const collectFromIdentity = (
  data: Record<string, unknown>,
  classificationType: 'project_description' | 'title_block',
  fileName: string,
  out: ExtractedDesignFactCandidate[],
): void => {
  const source: ExtractedDesignFactSource = classificationType;
  const confidence = readConfidence(data);
  pushSiteIdentityCandidates(out, data, source, fileName, confidence);
  if (classificationType === 'project_description') {
    pushCandidate(out, {
      path: 'location',
      kind: 'location',
      value: asString(data.location),
      source,
      fileName,
      confidence,
      sheetNote: null,
    });
    return;
  }
  pushCandidate(out, {
    path: 'projectName',
    kind: 'project_name',
    value: asString(data.project_title),
    source,
    fileName,
    confidence,
    sheetNote: null,
  });
};

const collectFromBuildingRendering = (
  data: Record<string, unknown>,
  file: Pick<ProjectFile, 'fileName' | 'classifications'>,
  segmentIndex: number,
  out: ExtractedDesignFactCandidate[],
): void => {
  const windowRatio = asNumber(data.window_ratio);
  if (windowRatio === null) return;
  const determinedBy =
    windowRatioDeterminedByFromUnknown(data.window_ratio_determined_by) ??
    'estimated';
  const envelope = file.classifications[segmentIndex];
  const ids =
    envelope === undefined
      ? []
      : buildingInstanceIdsFromClassification(envelope.data);
  const keys = ids.length > 0 ? ids : [null];
  for (const key of keys) {
    pushCandidate(out, {
      path: `buildings[${buildingKeyToken(key)}].windowRatio`,
      kind: 'window_ratio',
      value: windowRatio,
      source: 'building_rendering',
      fileName: file.fileName,
      confidence: readConfidence(data),
      sheetNote: null,
      windowRatioEstimated: determinedBy === 'estimated',
      windowRatioDeterminedBy: determinedBy,
    });
  }
};

/**
 * Flatten successful segment extractions (plus joined rows) into per-leaf
 * candidates for merge input and heuristic fallback.
 */
export const collectDesignFactCandidates = (input: {
  files: readonly (Pick<
    ProjectFile,
    'fileName' | 'classifications' | 'extractionOutput' | 'updatedAt'
  > & { sizeBytes?: number | null })[];
  joinedBlueprintExtractions?: readonly ProjectFileBatchJoinedBlueprintExtraction[];
  /** Merger / classifier buildings. Zone rows attach only to these. */
  knownBuildings?: readonly KnownBuildingIdentity[];
}): ExtractedDesignFactCandidate[] => {
  const out: ExtractedDesignFactCandidate[] = [];
  const ordered = keepFirstProjectFilePerContent(
    [...input.files]
      .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
      .map((file) => ({ ...file, sizeBytes: file.sizeBytes ?? null })),
  );
  for (const file of ordered) {
    const eo = file.extractionOutput;
    if (eo == null || !isProjectFileExtractionOutputV1(eo)) continue;
    for (const seg of eo.segments) {
      if (!seg.ok || seg.data == null) continue;
      const data = seg.data as Record<string, unknown>;
      switch (seg.classificationType) {
        case 'technical_spec':
        case 'project_brief': {
          const facts = parseExtractedDesignFactsAi(data.design_facts);
          if (facts !== null) {
            flattenFactsTree(
              facts,
              seg.classificationType,
              file.fileName,
              readConfidence(data),
              out,
            );
          }
          break;
        }
        case 'area_schedule':
          collectFromAreaSchedule(
            data,
            file,
            seg.classificationSegmentIndex,
            out,
          );
          break;
        case 'blueprint': {
          const source = blueprintSubtypeToDesignFactSource(
            blueprintSubtypeForSegment(file, seg.classificationSegmentIndex),
          );
          const buildings = data.buildings;
          if (!Array.isArray(buildings)) break;
          for (const raw of buildings) {
            const row = asRecord(raw);
            if (row === null) continue;
            collectFromBlueprintRow(row, source, file.fileName, out);
          }
          break;
        }
        case 'project_description':
        case 'title_block':
          collectFromIdentity(data, seg.classificationType, file.fileName, out);
          break;
        case 'building_rendering':
          collectFromBuildingRendering(
            data,
            file,
            seg.classificationSegmentIndex,
            out,
          );
          break;
        case 'bill_of_materials':
        case 'cost_estimate':
        case 'unknown':
          break;
        default:
          assertUnreachable(seg.classificationType);
      }
    }
  }
  for (const joined of input.joinedBlueprintExtractions ?? []) {
    const token = buildingKeyToken(trimNull(joined.buildingInstanceKey));
    const prefix = `buildings[${token}]`;
    const fileName = joined.sourceFileNames[0] ?? 'joined';
    pushCandidate(out, {
      path: `${prefix}.buildingName`,
      kind: 'building_name',
      value: joined.buildingLabel,
      source: 'joined_blueprint',
      fileName,
      confidence: null,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: `${prefix}.storeyCount`,
      kind: 'storey_count',
      value: joined.storeyCount,
      source: 'joined_blueprint',
      fileName,
      confidence: null,
      sheetNote: null,
    });
    pushCandidate(out, {
      path: 'storeyCount',
      kind: 'storey_count',
      value: joined.storeyCount,
      source: 'joined_blueprint',
      fileName,
      confidence: null,
      sheetNote: null,
    });
  }
  return collapseExtractedDesignFactCandidateBuildings(
    out,
    input.knownBuildings ?? [],
  );
};

const groupByPath = (
  candidates: readonly ExtractedDesignFactCandidate[],
): Map<string, ExtractedDesignFactCandidate[]> => {
  const map = new Map<string, ExtractedDesignFactCandidate[]>();
  for (const c of candidates) {
    const list = map.get(c.path) ?? [];
    list.push(c);
    map.set(c.path, list);
  }
  return map;
};

const pickNumber = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  path: string,
  kind: ExtractedDesignFactLeafKind,
): number | null => {
  const group = byPath.get(path);
  if (group === undefined) return null;
  const picked = pickMostLikelyExtractedDesignFact(kind, group);
  return picked !== null && typeof picked.value === 'number'
    ? picked.value
    : null;
};

const pickString = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  path: string,
  kind: ExtractedDesignFactLeafKind,
): string | null => {
  const group = byPath.get(path);
  if (group === undefined) return null;
  const picked = pickMostLikelyExtractedDesignFact(kind, group);
  return picked !== null && typeof picked.value === 'string'
    ? picked.value
    : null;
};

const BUILDING_PATH_RE = /^buildings\[([^\]]+)\]/;

const uniqueBuildingTokens = (
  candidates: readonly ExtractedDesignFactCandidate[],
): string[] => {
  const keys = new Set<string>();
  for (const c of candidates) {
    const match = BUILDING_PATH_RE.exec(c.path);
    if (match?.[1]) keys.add(match[1]);
  }
  return [...keys];
};

const rewriteBuildingPath = (
  path: string,
  tokenMap: ReadonlyMap<string, string>,
): string => {
  const match = BUILDING_PATH_RE.exec(path);
  if (match?.[1] === undefined) return path;
  const canonical = tokenMap.get(match[1]);
  if (canonical === undefined || canonical === match[1]) return path;
  return `buildings[${canonical}]${path.slice(match[0].length)}`;
};

/** Building keys that carry BTA from the areatabell — the scored main volume(s). */
const buildingTokensWithAreaScheduleBta = (
  candidates: readonly ExtractedDesignFactCandidate[],
): readonly string[] => {
  const tokens = new Set<string>();
  for (const candidate of candidates) {
    if (candidate.source !== 'area_schedule') continue;
    if (candidate.kind !== 'bta_m2') continue;
    const match = BUILDING_PATH_RE.exec(candidate.path);
    if (match?.[1] !== undefined) tokens.add(match[1]);
  }
  return [...tokens];
};

/**
 * When the areatabell names one building's BTA but facade steps opened extra
 * keys, treat the batch as one scored building for alias collapse (same as an
 * explicit `building_count` of 1).
 */
const buildingCountForTokenMap = (
  candidates: readonly ExtractedDesignFactCandidate[],
  tokens: readonly string[],
): number | null => {
  const picked = pickMostLikelyExtractedDesignFact(
    'building_count',
    candidates.filter((candidate) => candidate.kind === 'building_count'),
  );
  if (picked?.value === 1) return 1;
  if (countsSeveralHouses(tokens)) {
    return typeof picked?.value === 'number' ? picked.value : null;
  }
  const scheduleBta = buildingTokensWithAreaScheduleBta(candidates);
  if (scheduleBta.length === 1 && tokens.length > 1) return 1;
  return typeof picked?.value === 'number' ? picked.value : null;
};

const representativeBuildingName = (
  token: string,
  candidates: readonly ExtractedDesignFactCandidate[],
): string | null => {
  const names = candidates.filter(
    (c) => c.path === `buildings[${token}].buildingName`,
  );
  const picked = pickMostLikelyExtractedDesignFact('building_name', names);
  if (picked === null || typeof picked.value !== 'string') return null;
  const t = picked.value.trim();
  return t === '' ? null : t;
};

const knownConsolidatedPartVolumeKeys = (
  tokens: readonly string[],
  known: readonly KnownBuildingIdentity[],
): boolean => {
  if (known.length < 2 || tokens.length < 2) return false;
  const knownKeys = new Set(known.map((row) => row.key));
  if (countsSeveralHouses(tokens)) return false;
  return tokens.every((token) => knownKeys.has(token));
};

const buildingTokenMap = (
  candidates: readonly ExtractedDesignFactCandidate[],
  knownBuildings: readonly KnownBuildingIdentity[],
): Map<string, string> => {
  const tokens = uniqueBuildingTokens(candidates);
  const map = new Map<string, string>();
  if (tokens.length <= 1) {
    for (const t of tokens) map.set(t, t);
    return map;
  }
  const countPicked = buildingCountForTokenMap(candidates, tokens);
  const partVolumes = knownConsolidatedPartVolumeKeys(tokens, knownBuildings);
  if (countPicked === 1 && !countsSeveralHouses(tokens) && !partVolumes) {
    const canonical = pickCanonicalBuildingInstanceKey(tokens) ?? '_';
    for (const t of tokens) map.set(t, canonical);
    return map;
  }
  const clusters = new Map<string, string[]>();
  for (const token of tokens) {
    const name = representativeBuildingName(token, candidates);
    const clusterId =
      name !== null
        ? `n:${normalizeExtractedBuildingName(name)}`
        : `k:${token}`;
    const list = clusters.get(clusterId);
    if (list === undefined) clusters.set(clusterId, [token]);
    else list.push(token);
  }
  const namedIds = [...clusters.keys()].filter((id) => id.startsWith('n:'));
  const namedId = namedIds.length === 1 ? namedIds[0] : undefined;
  const namedList = namedId === undefined ? undefined : clusters.get(namedId);
  if (namedList !== undefined) {
    for (const id of [...clusters.keys()]) {
      if (!id.startsWith('k:')) continue;
      const extra = clusters.get(id);
      if (extra === undefined) continue;
      namedList.push(...extra);
      clusters.delete(id);
    }
  }
  for (const list of clusters.values()) {
    const canonical = pickCanonicalBuildingInstanceKey(list) ?? '_';
    for (const t of list) map.set(t, canonical);
  }
  return map;
};

/**
 * Rewrite `buildings[<key>]` candidate paths so alias keys of the same hus
 * share one token. `building_count` 1 collapses every key when the batch
 * has not already named several houses. Distinct house slugs stay apart.
 * Same normalized name collapses. Nameless keys attach to the sole named
 * cluster.
 */
export const collapseExtractedDesignFactCandidateBuildings = (
  candidates: readonly ExtractedDesignFactCandidate[],
  knownBuildings: readonly KnownBuildingIdentity[] = [],
): ExtractedDesignFactCandidate[] => {
  const tokenMap = buildingTokenMap(candidates, knownBuildings);
  let changed = false;
  for (const [from, to] of tokenMap) {
    if (from !== to) {
      changed = true;
      break;
    }
  }
  if (!changed) return [...candidates];
  return candidates.map((c) => ({
    ...c,
    path: rewriteBuildingPath(c.path, tokenMap),
  }));
};

/**
 * Path tokens a stored storey answers to (`plan_13`, `plan_11-14`, `attic`,
 * `i_0`). Empty when the row cannot be keyed.
 */
export const storeyPathTokens = (
  storey: ExtractedStoreyFacts,
  indexInArray: number,
): string[] => {
  if (storey.planNumbers.length === 0 && storey.level == null) {
    const label = storey.label?.trim() ?? '';
    if (label === '') return [];
    return [unplacedStoreyToken(label)];
  }
  const key = storeyKeyOf(storey, indexInArray);
  return key == null ? [] : [key.token];
};

const TOKEN_IN_BRACKETS_RE = /^([^\]]+)\]/;

/** Distinct `[token]` values right after `prefix` in candidate paths. */
const uniqueChildTokens = (
  candidates: readonly ExtractedDesignFactCandidate[],
  prefix: string,
): string[] => {
  const keys = new Set<string>();
  for (const c of candidates) {
    if (!c.path.startsWith(prefix)) continue;
    const match = TOKEN_IN_BRACKETS_RE.exec(c.path.slice(prefix.length));
    if (match?.[1]) keys.add(match[1]);
  }
  return [...keys];
};

const PROGRAM_STEM_RE =
  /^\.programs\.((?:[^.[]+)(?:\[[^\]]+\])?)(?:\.(?:areaM2|label))?$/;

/**
 * Path stem for one program row. Mapped types share a stem. An unmapped
 * program is its own stem, so its stated area stays with the printed word.
 */
const otherProgramStem = (
  label: string | null,
  areaM2: number | null,
): string => {
  const name = normalizeExtractedBuildingName(label ?? '');
  if (name !== '') return `other[${name}]`;
  if (areaM2 != null) return `other[a${areaM2}]`;
  return 'other';
};

/**
 * One merge path per program. A mapped type is one program per storey, so
 * every source that states it lands on the same path and the source ranking
 * picks the value. An unmapped program is its own path.
 */
const programCandidateStem = (
  type: ExtractedProgramType,
  label: string | null,
  areaM2: number | null,
): string => (type === 'other' ? otherProgramStem(label, areaM2) : type);

const programStemOf = (program: ExtractedStoreyProgram): string =>
  program.type === 'other'
    ? otherProgramStem(program.label, program.areaM2)
    : program.type;

/** Programs keyed by stem, repeats numbered like the candidate paths. */
const programsWithStems = (
  programs: readonly ExtractedStoreyProgram[],
): (readonly [string, ExtractedStoreyProgram])[] => {
  const occurrences = newProgramOccurrences();
  return programs.map(
    (p) => [occurrenceStem(programStemOf(p), occurrences, ''), p] as const,
  );
};

const programTypeOfStem = (stem: string): ExtractedProgramType | null => {
  const bracket = stem.indexOf('[');
  const base = bracket === -1 ? stem : stem.slice(0, bracket);
  return isProgramType(base) ? base : null;
};

const isProgramType = (value: string): value is ExtractedProgramType =>
  (EXTRACTED_PROGRAM_TYPES as readonly string[]).includes(value);

/** Level a bare path token implies before an entrance is known. */
const levelFromStoreyKey = (
  key: CanonicalStoreyKey | null,
): StoreyLevel | null => {
  if (key === null) return null;
  if (key.index !== null) return { kind: 'single', index: key.index };
  switch (key.labelledKind) {
    case null:
      return null;
    case 'entrance':
      return { kind: 'single', index: 0 };
    case 'typical':
      return key.planNumbers.length > 0 ? null : { kind: 'typical' };
    case 'roof':
      return { kind: 'roof' };
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return specialStoreyLevel(key.labelledKind);
    default:
      return assertUnreachable(key.labelledKind);
  }
};

/**
 * Token identity wins when it already places the floor (`i_N`, entrance).
 * Otherwise the chosen index — the source most likely right for that fact —
 * places the row. A missing index leaves the token's level.
 */
const levelWithChosenIndex = (
  tokenLevel: StoreyLevel | null,
  chosenIndex: number | null,
): StoreyLevel | null => {
  if (chosenIndex == null || !Number.isInteger(chosenIndex)) return tokenLevel;
  if (tokenLevel == null) return { kind: 'single', index: chosenIndex };
  switch (tokenLevel.kind) {
    case 'single':
      return tokenLevel;
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return tokenLevel.index == null
        ? { kind: tokenLevel.kind, index: chosenIndex }
        : tokenLevel;
    case 'range':
    case 'typical':
    case 'roof':
      return tokenLevel;
    default:
      return assertUnreachable(tokenLevel);
  }
};

const assembleStorey = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  candidates: readonly ExtractedDesignFactCandidate[],
  volumePrefix: string,
  storeyToken: string,
): ExtractedStoreyFacts => {
  const prefix = `${volumePrefix}.storeys[${storeyToken}]`;
  const seen = new Set<string>();
  const stems: string[] = [];
  for (const c of candidates) {
    if (!c.path.startsWith(`${prefix}.programs.`)) continue;
    const match = PROGRAM_STEM_RE.exec(c.path.slice(prefix.length));
    const stem = match?.[1];
    if (
      stem === undefined ||
      programTypeOfStem(stem) === null ||
      seen.has(stem)
    ) {
      continue;
    }
    seen.add(stem);
    stems.push(stem);
  }
  const canonical = parseCanonicalStoreyToken(storeyToken);
  const unplaced = isUnplacedStoreyToken(storeyToken);
  const indexNotes = (byPath.get(`${prefix}.index`) ?? []).flatMap(
    (candidate) => {
      const note = trimNull(candidate.sheetNote);
      return note == null ? [] : [note];
    },
  );
  const printed =
    !unplaced && canonical?.index != null
      ? (indexNotes[0] ?? null)
      : (candidates.find((c) => c.path.startsWith(`${prefix}.`))?.sheetNote ??
        null);
  const keptLabel = (): string | null => {
    if (unplaced) return trimNull(printed);
    if (canonical === null) return storeyToken;
    if (canonical.label !== null) return canonical.label;
    if (canonical.index !== null || canonical.labelledKind !== null) {
      return trimNull(printed);
    }
    return null;
  };
  const label = keptLabel();
  const assembled = {
    ...emptyExtractedStoreyFacts(),
    label,
    planNumbers: unplaced ? [] : (canonical?.planNumbers ?? []),
    level: unplaced
      ? null
      : levelWithChosenIndex(
          levelFromStoreyKey(canonical),
          pickNumber(byPath, `${prefix}.index`, 'storey_index'),
        ),
    innerHeightM: pickNumber(
      byPath,
      `${prefix}.innerHeightM`,
      'inner_height_m',
    ),
    floorToFloorHeightM: pickNumber(
      byPath,
      `${prefix}.floorToFloorHeightM`,
      'floor_to_floor_height_m',
    ),
    areaM2: pickNumber(byPath, `${prefix}.areaM2`, 'area_m2'),
    programs: stems.flatMap((stem) => {
      const type = programTypeOfStem(stem);
      if (type === null) return [];
      return [
        storedProgram(
          type,
          pickNumber(byPath, `${prefix}.programs.${stem}.areaM2`, 'programs'),
          type === 'other'
            ? pickString(byPath, `${prefix}.programs.${stem}.label`, 'programs')
            : null,
          pickNumber(byPath, `${prefix}.programs.${stem}.share`, 'programs'),
        ),
      ];
    }),
    elevationMarkM: parseElevationMark(label),
  };
  if (unplaced) return omitStoreyIndex(assembled);
  if (canonical?.index != null && label != null)
    return lockStoreyIndex(assembled);
  return assembled;
};

const assembleBuilding = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  candidates: readonly ExtractedDesignFactCandidate[],
  token: string,
): ExtractedBuildingFacts => {
  const prefix = `buildings[${token}]`;
  const living = pickNumber(
    byPath,
    `${prefix}.residential.livingAreaPerApartmentM2`,
    'apartment_size',
  );
  const balcony = pickNumber(
    byPath,
    `${prefix}.residential.balconyAreaPerApartmentM2`,
    'balcony',
  );
  const bathroom = pickNumber(
    byPath,
    `${prefix}.residential.bathroomAreaPerApartmentM2`,
    'bathroom',
  );
  const programType = residentialProgramForm(
    pickString(byPath, `${prefix}.residential.programType`, 'program_type'),
  );
  const apartments = pickNumber(
    byPath,
    `${prefix}.stairwells.apartmentsPerStairwellPerStorey`,
    'stairwells',
  );
  const elevators = pickNumber(
    byPath,
    `${prefix}.stairwells.elevatorsPerStairwell`,
    'stairwells',
  );
  const residential = residentialOrNull({
    livingAreaPerApartmentM2: living,
    balconyAreaPerApartmentM2: balcony,
    bathroomAreaPerApartmentM2: bathroom,
    programType,
  });
  const stairwells: ExtractedStairwellsFacts | null =
    apartments === null && elevators === null
      ? null
      : {
          apartmentsPerStairwellPerStorey: apartments,
          elevatorsPerStairwell: elevators,
        };
  const window = chosenWindowFromCandidates(
    byPath.get(`${prefix}.windowRatio`) ?? [],
  );
  return {
    buildingInstanceKey: token === '_' ? null : token,
    buildingName: pickString(byPath, `${prefix}.buildingName`, 'building_name'),
    storeyCount: exactStoreyCount(
      pickNumber(byPath, `${prefix}.storeyCount`, 'storey_count'),
    ),
    btaM2: pickNumber(byPath, `${prefix}.btaM2`, 'bta_m2'),
    ...assembleVolumeAreas(byPath, candidates, prefix),
    windowRatio: window?.windowRatio ?? null,
    windowRatioEstimated: window?.windowRatioEstimated ?? null,
    windowRatioDeterminedBy: window?.windowRatioDeterminedBy ?? null,
    beamSpanM: pickNumber(byPath, `${prefix}.beamSpanM`, 'beam_span_m'),
    slabThicknessMm: pickNumber(
      byPath,
      `${prefix}.slabThicknessMm`,
      'slab_thickness_mm',
    ),
    roofForm: assembleRoofForm(byPath, prefix),
    storeys: assembleStoreys(byPath, candidates, prefix),
    parts: uniqueChildTokens(candidates, `${prefix}.parts[`).flatMap(
      (partToken) => {
        const partPrefix = `${prefix}.parts[${partToken}]`;
        const name = pickString(byPath, `${partPrefix}.name`, 'building_name');
        if (name === null) return [];
        return [
          {
            name,
            storeyCount: exactStoreyCount(
              pickNumber(byPath, `${partPrefix}.storeyCount`, 'storey_count'),
            ),
            btaM2: null,
            ...assembleVolumeAreas(byPath, candidates, partPrefix),
            storeys: assembleStoreys(byPath, candidates, partPrefix),
            sourceBuildingKey: null,
          },
        ];
      },
    ),
    connection: null,
    residential,
    stairwells,
  };
};

const otherAreaScopeField = (
  picked: string | null,
): Pick<ExtractedOtherAreaFacts, 'rowScope'> => {
  const rowScope = parseOtherAreaRowScope(picked);
  return rowScope === null ? {} : { rowScope };
};

const assembleVolumeAreas = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  candidates: readonly ExtractedDesignFactCandidate[],
  prefix: string,
): VolumeAreasStored => ({
  ljusBtaM2: pickNumber(byPath, `${prefix}.ljusBtaM2`, 'area_total'),
  boaM2: pickNumber(byPath, `${prefix}.boaM2`, 'area_total'),
  loaM2: pickNumber(byPath, `${prefix}.loaM2`, 'area_total'),
  byaM2: pickNumber(byPath, `${prefix}.byaM2`, 'area_total'),
  apartmentCount: pickNumber(
    byPath,
    `${prefix}.apartmentCount`,
    'apartment_count',
  ),
  apartments: uniqueChildTokens(candidates, `${prefix}.apartments[`).map(
    (token) => {
      const apt = `${prefix}.apartments[${token}]`;
      return {
        label: pickString(byPath, `${apt}.label`, 'apartment'),
        count: pickNumber(byPath, `${apt}.count`, 'apartment'),
        areaM2: pickNumber(byPath, `${apt}.areaM2`, 'apartment'),
        areaMinM2: pickNumber(byPath, `${apt}.areaMinM2`, 'apartment'),
        areaMaxM2: pickNumber(byPath, `${apt}.areaMaxM2`, 'apartment'),
      };
    },
  ),
  otherAreas: uniqueChildTokens(candidates, `${prefix}.otherAreas[`).map(
    (token) => {
      const area = `${prefix}.otherAreas[${token}]`;
      return {
        label: pickString(byPath, `${area}.label`, 'other_area'),
        areaM2: pickNumber(byPath, `${area}.areaM2`, 'other_area'),
        ...otherAreaScopeField(
          pickString(byPath, `${area}.rowScope`, 'other_area'),
        ),
      };
    },
  ),
});

const dedupeStoreysByLevelIndex = (
  storeys: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const indexed = new Map<number, ExtractedStoreyFacts>();
  const unindexed: ExtractedStoreyFacts[] = [];
  for (const storey of storeys) {
    const index = storeyLevelIndex(storey.level);
    if (index == null) {
      unindexed.push(storey);
      continue;
    }
    const prev = indexed.get(index);
    indexed.set(
      index,
      prev === undefined ? storey : coalesceStorey(storey, prev),
    );
  }
  return [
    ...[...indexed.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, storey]) => storey),
    ...unindexed,
  ];
};

const assembleStoreys = (
  byPath: Map<string, ExtractedDesignFactCandidate[]>,
  candidates: readonly ExtractedDesignFactCandidate[],
  volumePrefix: string,
): ExtractedStoreyFacts[] =>
  dedupeStoreysByLevelIndex(
    uniqueChildTokens(candidates, `${volumePrefix}.storeys[`)
      .filter(isMaterializedStoreyToken)
      .map((storeyToken) =>
        assembleStorey(byPath, candidates, volumePrefix, storeyToken),
      ),
  );

/**
 * Code fallback when the merge LLM fails. Uses the same ranking as the
 * prompt — never highest-confidence-wins.
 */
/** A typical plan's area printed as the sum of its storeys is no storey's area. */
const isLeafValue = (candidate: ExtractedDesignFactCandidate): boolean =>
  candidate.typicalAreaScope !== 'sum';

export const resolveExtractedDesignFactsHeuristic = (
  allCandidates: readonly ExtractedDesignFactCandidate[],
): ExtractedDesignFacts => {
  const candidates = allCandidates.filter(isLeafValue);
  if (candidates.length === 0) return emptyExtractedDesignFacts();
  const byPath = groupByPath(candidates);
  const tokens = uniqueBuildingTokens(candidates);
  return {
    projectName: pickString(byPath, 'projectName', 'project_name'),
    location: pickString(byPath, 'location', 'location'),
    propertyDesignation: pickString(
      byPath,
      'propertyDesignation',
      'property_designation',
    ),
    detailedPlan: pickString(byPath, 'detailedPlan', 'detailed_plan'),
    client: pickString(byPath, 'client', 'client'),
    storeyCount: pickNumber(byPath, 'storeyCount', 'storey_count'),
    buildingCount: pickNumber(byPath, 'buildingCount', 'building_count'),
    buildings: tokens.map((token) =>
      assembleBuilding(byPath, candidates, token),
    ),
  };
};

const fillApartment = (
  preferred: ExtractedApartmentFacts,
  fallback: ExtractedApartmentFacts,
): ExtractedApartmentFacts => ({
  label: preferred.label ?? fallback.label,
  count: preferred.count ?? fallback.count,
  areaM2: preferred.areaM2 ?? fallback.areaM2,
  areaMinM2: preferred.areaMinM2 ?? fallback.areaMinM2,
  areaMaxM2: preferred.areaMaxM2 ?? fallback.areaMaxM2,
});

const mergeApartments = (
  preferred: readonly ExtractedApartmentFacts[],
  fallback: readonly ExtractedApartmentFacts[],
): ExtractedApartmentFacts[] => {
  const byKey = new Map<string, ExtractedApartmentFacts>();
  fallback.forEach((row, index) => {
    byKey.set(apartmentToken(row, index), row);
  });
  preferred.forEach((row, index) => {
    const key = apartmentToken(row, index);
    const prev = byKey.get(key);
    byKey.set(key, prev === undefined ? row : fillApartment(row, prev));
  });
  return [...byKey.values()];
};

/**
 * One other-area row per printed word, as {@link otherAreaTokenForLabel}. The
 * row index in {@link otherAreaToken} differs between the two volumes being
 * merged, so it identifies a row only when there is no label to join on.
 */
const otherAreaMergeKey = (
  row: Pick<ExtractedOtherAreaFacts, 'label'>,
  index: number,
): string => {
  const name = normalizeExtractedBuildingName(row.label ?? '');
  return name === '' ? otherAreaToken(row, index) : name;
};

/** A row the document prints for a wider scope outranks a narrower one. */
const OTHER_AREA_SCOPE_RANK: Record<ExtractedOtherAreaRowScope, number> = {
  zone: 1,
  storey_total: 2,
  building_total: 3,
};

const otherAreaScopeRank = (row: ExtractedOtherAreaFacts): number =>
  row.rowScope == null ? 0 : OTHER_AREA_SCOPE_RANK[row.rowScope];

/**
 * Rows with the same printed word are one row. On a clash the row with the
 * wider printed scope stands (a building total over a per-plan zone row);
 * at equal scope the preferred volume stands. A null area is filled from the
 * row that lost.
 */
const mergeOtherAreas = (
  preferred: readonly ExtractedOtherAreaFacts[],
  fallback: readonly ExtractedOtherAreaFacts[],
): ExtractedOtherAreaFacts[] => {
  const byKey = new Map<string, ExtractedOtherAreaFacts>();
  const put = (row: ExtractedOtherAreaFacts, index: number): void => {
    const key = otherAreaMergeKey(row, index);
    const prev = byKey.get(key);
    if (prev === undefined) {
      byKey.set(key, row);
      return;
    }
    const [winner, loser] =
      otherAreaScopeRank(prev) > otherAreaScopeRank(row)
        ? [prev, row]
        : [row, prev];
    byKey.set(key, {
      ...winner,
      label: winner.label ?? loser.label,
      areaM2: winner.areaM2 ?? loser.areaM2,
    });
  };
  fallback.forEach(put);
  preferred.forEach(put);
  return [...byKey.values()];
};

const mergeVolumeRows = (
  rows: readonly VolumeAreasStored[],
): VolumeAreasStored =>
  rows.reduce(
    (acc, row) => coalesceVolumeAreas(acc, row),
    emptyExtractedVolumeAreas(),
  );

const coalesceVolumeAreas = (
  preferred: VolumeAreasStored,
  fallback: VolumeAreasStored,
): VolumeAreasStored => ({
  ljusBtaM2: coalesceNum(preferred.ljusBtaM2, fallback.ljusBtaM2),
  boaM2: coalesceNum(preferred.boaM2, fallback.boaM2),
  loaM2: coalesceNum(preferred.loaM2, fallback.loaM2),
  byaM2: coalesceNum(preferred.byaM2, fallback.byaM2),
  apartmentCount: coalesceNum(
    preferred.apartmentCount,
    fallback.apartmentCount,
  ),
  apartments: mergeApartments(preferred.apartments, fallback.apartments),
  otherAreas: mergeOtherAreas(preferred.otherAreas, fallback.otherAreas),
});

const coalesceNum = (
  preferred: number | null,
  fallback: number | null,
): number | null => preferred ?? fallback;

const coalesceStr = (
  preferred: string | null,
  fallback: string | null,
): string | null => preferred ?? fallback;

/** Merger understates; heuristic section stacks are exact counts. */
const coalescePartStoreyCount = (
  preferred: StoreyCount | null,
  fallback: StoreyCount | null,
): StoreyCount | null => {
  if (preferred == null) return fallback;
  if (fallback == null) return preferred;
  if (preferred.kind === 'exact' && fallback.kind === 'exact') {
    return preferred.value >= fallback.value ? preferred : fallback;
  }
  return preferred;
};

const coalesceProgram = (
  preferred: ExtractedStoreyProgram,
  fallback: ExtractedStoreyProgram | undefined,
): ExtractedStoreyProgram => {
  const areaM2 = coalesceNum(preferred.areaM2, fallback?.areaM2 ?? null);
  const share = coalesceNum(preferred.share ?? null, fallback?.share ?? null);
  if (preferred.type !== 'other')
    return { type: preferred.type, areaM2, share };
  const fallbackLabel = fallback?.type === 'other' ? fallback.label : null;
  return {
    type: 'other',
    areaM2,
    share,
    label: coalesceStr(preferred.label, fallbackLabel),
  };
};

const storeyMergeToken = (s: ExtractedStoreyFacts, i: number): string =>
  storeyPathTokens(s, i)[0] ?? `x:${s.label ?? i}`;

/**
 * The preferred list decides which storeys exist. Fallback fills the nulls of
 * the storey with the same token and stands in only when the preferred list
 * is empty.
 */
const coalesceStoreyLists = (
  preferred: readonly ExtractedStoreyFacts[],
  fallback: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  if (preferred.length === 0) return [...fallback];
  const fallbackByToken = new Map(
    fallback.map((s, i) => [storeyMergeToken(s, i), s] as const),
  );
  return preferred.map((s, i) => {
    const fb = fallbackByToken.get(storeyMergeToken(s, i));
    return fb === undefined ? s : coalesceStorey(s, fb);
  });
};

const partMergeKey = (part: ExtractedBuildingPartFacts): string =>
  buildingPartToken(part.name);

const coalesceParts = (
  preferred: readonly ExtractedBuildingPartFacts[],
  fallback: readonly ExtractedBuildingPartFacts[],
): ExtractedBuildingPartFacts[] => {
  const byKey = new Map(fallback.map((p) => [partMergeKey(p), p] as const));
  for (const part of preferred) {
    const fb = byKey.get(partMergeKey(part));
    byKey.set(
      partMergeKey(part),
      fb === undefined
        ? part
        : {
            name: part.name,
            storeyCount: coalescePartStoreyCount(
              part.storeyCount,
              fb.storeyCount,
            ),
            btaM2: part.btaM2 ?? fb.btaM2,
            ...coalesceVolumeAreas(part, fb),
            storeys: coalesceStoreyLists(part.storeys, fb.storeys),
            sourceBuildingKey: part.sourceBuildingKey ?? fb.sourceBuildingKey,
          },
    );
  }
  return [...byKey.values()];
};

type StemmedPrograms = readonly (readonly [string, ExtractedStoreyProgram])[];

const programsOfBaseStem = (
  stem: string,
  programs: StemmedPrograms,
): ExtractedStoreyProgram[] =>
  programs
    .filter(([key]) => baseProgramStem(key) === stem)
    .map(([, program]) => program);

/**
 * True when the preferred storey states one row for a stem whose fallback
 * rows are several identical zone rows with that same area. Identical rows
 * read like duplicates, so that one row is the collapsed group, not a total
 * that already includes the others (a total differs from each zone row).
 */
const isCollapsedZoneGroup = (
  stem: string,
  preferred: StemmedPrograms,
  fallback: StemmedPrograms,
): boolean => {
  const stated = programsOfBaseStem(stem, preferred);
  const zones = programsOfBaseStem(stem, fallback);
  const [only] = stated;
  if (stated.length !== 1 || only === undefined || zones.length < 2) {
    return false;
  }
  return zones.every(
    (zone) => zone.areaM2 !== null && zone.areaM2 === only.areaM2,
  );
};

const coalesceStorey = (
  preferred: ExtractedStoreyFacts,
  fallback: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  const preferredPrograms = programsWithStems(preferred.programs);
  const fallbackPrograms = programsWithStems(fallback.programs);
  const statedStems = new Set(
    preferredPrograms
      .map(([key]) => baseProgramStem(key))
      .filter(
        (stem) =>
          !isCollapsedZoneGroup(stem, preferredPrograms, fallbackPrograms),
      ),
  );
  const programsByKey = new Map(
    fallbackPrograms.filter(
      ([key]) =>
        !isRepeatedProgramStem(key) || !statedStems.has(baseProgramStem(key)),
    ),
  );
  for (const [key, p] of preferredPrograms) {
    programsByKey.set(key, coalesceProgram(p, programsByKey.get(key)));
  }
  return {
    ...emptyExtractedStoreyFacts(),
    label: coalesceStr(preferred.label, fallback.label),
    planNumbers:
      preferred.planNumbers.length > 0
        ? preferred.planNumbers
        : fallback.planNumbers,
    level: preferred.level ?? fallback.level,
    innerHeightM: coalesceNum(preferred.innerHeightM, fallback.innerHeightM),
    floorToFloorHeightM: coalesceNum(
      preferred.floorToFloorHeightM,
      fallback.floorToFloorHeightM,
    ),
    areaM2: coalesceNum(preferred.areaM2, fallback.areaM2),
    programs: [...programsByKey.values()],
    elevationMarkM: coalesceNum(
      preferred.elevationMarkM,
      fallback.elevationMarkM,
    ),
    elevationFromGroundM: coalesceNum(
      preferred.elevationFromGroundM,
      fallback.elevationFromGroundM,
    ),
  };
};

const coalesceBuilding = (
  preferred: ExtractedBuildingFacts,
  fallback: ExtractedBuildingFacts,
): ExtractedBuildingFacts => {
  const storeys = coalesceStoreyLists(preferred.storeys, fallback.storeys);
  const residentialPreferred = preferred.residential;
  const residentialFallback = fallback.residential;
  const residential =
    residentialPreferred == null && residentialFallback == null
      ? null
      : {
          livingAreaPerApartmentM2: coalesceNum(
            residentialPreferred?.livingAreaPerApartmentM2 ?? null,
            residentialFallback?.livingAreaPerApartmentM2 ?? null,
          ),
          balconyAreaPerApartmentM2: coalesceNum(
            residentialPreferred?.balconyAreaPerApartmentM2 ?? null,
            residentialFallback?.balconyAreaPerApartmentM2 ?? null,
          ),
          bathroomAreaPerApartmentM2: coalesceNum(
            residentialPreferred?.bathroomAreaPerApartmentM2 ?? null,
            residentialFallback?.bathroomAreaPerApartmentM2 ?? null,
          ),
          programType:
            residentialPreferred?.programType ??
            residentialFallback?.programType ??
            null,
        };
  const stairwellsPreferred = preferred.stairwells;
  const stairwellsFallback = fallback.stairwells;
  const stairwells =
    stairwellsPreferred == null && stairwellsFallback == null
      ? null
      : {
          apartmentsPerStairwellPerStorey: coalesceNum(
            stairwellsPreferred?.apartmentsPerStairwellPerStorey ?? null,
            stairwellsFallback?.apartmentsPerStairwellPerStorey ?? null,
          ),
          elevatorsPerStairwell: coalesceNum(
            stairwellsPreferred?.elevatorsPerStairwell ?? null,
            stairwellsFallback?.elevatorsPerStairwell ?? null,
          ),
        };
  return {
    buildingInstanceKey: coalesceStr(
      preferred.buildingInstanceKey,
      fallback.buildingInstanceKey,
    ),
    buildingName: coalesceStr(preferred.buildingName, fallback.buildingName),
    storeyCount: preferred.storeyCount ?? fallback.storeyCount,
    btaM2: coalesceNum(preferred.btaM2, fallback.btaM2),
    ...coalesceVolumeAreas(preferred, fallback),
    // Candidates choose the ratio. The merger number is not a source.
    windowRatio: fallback.windowRatio,
    windowRatioEstimated: fallback.windowRatioEstimated,
    windowRatioDeterminedBy: fallback.windowRatioDeterminedBy,
    beamSpanM: coalesceNum(preferred.beamSpanM, fallback.beamSpanM),
    slabThicknessMm: coalesceNum(
      preferred.slabThicknessMm,
      fallback.slabThicknessMm,
    ),
    roofForm: preferred.roofForm ?? fallback.roofForm,
    storeys,
    parts: coalesceParts(preferred.parts, fallback.parts),
    connection: preferred.connection ?? fallback.connection,
    residential:
      residential != null && allNullResidential(residential)
        ? null
        : residential,
    stairwells:
      stairwells != null &&
      stairwells.apartmentsPerStairwellPerStorey == null &&
      stairwells.elevatorsPerStairwell == null
        ? null
        : stairwells,
  };
};

/**
 * Fill holes in the LLM tree from ranking. Never averages. Preferred leaf
 * wins when set; fallback fills nulls so a candidate is never dropped to empty.
 */
const coalesceBuildingCount = (
  preferred: ExtractedDesignFacts,
  fallback: ExtractedDesignFacts,
): number | null => {
  const merged = coalesceNum(preferred.buildingCount, fallback.buildingCount);
  if (
    fallback.buildingCount === 1 &&
    preferred.buildingCount !== null &&
    preferred.buildingCount > 1 &&
    fallback.buildings.length <= 1 &&
    !countsSeveralHouses(
      preferred.buildings.flatMap((building) =>
        building.buildingInstanceKey === null
          ? []
          : [building.buildingInstanceKey],
      ),
    )
  ) {
    return 1;
  }
  return merged;
};

const programShareStoreyKey = (storey: ExtractedStoreyFacts): string => {
  const index = storeyLevelIndex(storey.level);
  if (index != null) return `i:${index}`;
  const label = storey.label?.trim();
  if (label !== undefined && label !== '') return `l:${label.toLowerCase()}`;
  return 'u';
};

const programShareLookupKey = (
  buildingKey: string,
  partName: string | null,
  storey: ExtractedStoreyFacts,
  program: ExtractedStoreyProgram,
): string =>
  `${buildingKey}|${partName ?? ''}|${programShareStoreyKey(storey)}|${programStemOf(program)}`;

const buildProgramShareLookup = (
  source: ExtractedDesignFacts,
): Map<string, number> => {
  const lookup = new Map<string, number>();
  const add = (
    buildingKey: string,
    partName: string | null,
    storey: ExtractedStoreyFacts,
    program: ExtractedStoreyProgram,
  ) => {
    if (program.share == null) return;
    lookup.set(
      programShareLookupKey(buildingKey, partName, storey, program),
      program.share,
    );
  };
  for (const building of source.buildings) {
    const key = buildingKeyToken(building.buildingInstanceKey);
    for (const storey of building.storeys) {
      for (const program of storey.programs) add(key, null, storey, program);
    }
    for (const part of building.parts) {
      for (const storey of part.storeys) {
        for (const program of storey.programs) {
          add(key, part.name, storey, program);
        }
      }
    }
  }
  return lookup;
};

const fillNullProgramSharesOnStoreys = (
  buildingKey: string,
  partName: string | null,
  storeys: readonly ExtractedStoreyFacts[],
  lookup: Map<string, number>,
): { storeys: ExtractedStoreyFacts[]; changed: boolean } => {
  let changed = false;
  const next = storeys.map((storey) => {
    let storeyChanged = false;
    const programs = storey.programs.map((program) => {
      if (program.share !== null && program.share !== undefined) return program;
      const primary = lookup.get(
        programShareLookupKey(buildingKey, partName, storey, program),
      );
      const fromBuilding =
        partName !== null
          ? lookup.get(
              programShareLookupKey(buildingKey, null, storey, program),
            )
          : undefined;
      const share = primary ?? fromBuilding ?? null;
      if (share == null) return program;
      storeyChanged = true;
      return { ...program, share };
    });
    if (!storeyChanged) return storey;
    changed = true;
    return { ...storey, programs };
  });
  return { storeys: changed ? next : [...storeys], changed };
};

/**
 * After merge coalesce, copy program shares from the heuristic tree when the
 * coalesced row still has null share but the heuristic had a value on the same
 * building, part, storey level, and program stem.
 */
export const backfillNullProgramSharesFromHeuristic = (
  facts: ExtractedDesignFacts,
  heuristic: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  const lookup = buildProgramShareLookup(heuristic);
  if (lookup.size === 0) return facts;
  let changed = false;
  const buildings = facts.buildings.map((building) => {
    const buildingKey = buildingKeyToken(building.buildingInstanceKey);
    const buildingStoreys = fillNullProgramSharesOnStoreys(
      buildingKey,
      null,
      building.storeys,
      lookup,
    );
    let partsChanged = false;
    const parts = building.parts.map((part) => {
      const partStoreys = fillNullProgramSharesOnStoreys(
        buildingKey,
        part.name,
        part.storeys,
        lookup,
      );
      if (!partStoreys.changed) return part;
      partsChanged = true;
      return { ...part, storeys: partStoreys.storeys };
    });
    if (!buildingStoreys.changed && !partsChanged) return building;
    changed = true;
    return {
      ...building,
      storeys: buildingStoreys.changed
        ? buildingStoreys.storeys
        : building.storeys,
      parts: partsChanged ? parts : building.parts,
    };
  });
  return changed ? { ...facts, buildings } : facts;
};

export const coalesceExtractedDesignFacts = (
  preferred: ExtractedDesignFacts,
  fallback: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  const byKey = new Map<string, ExtractedBuildingFacts>();
  for (const b of fallback.buildings) {
    byKey.set(buildingKeyToken(b.buildingInstanceKey), b);
  }
  for (const b of preferred.buildings) {
    const key = buildingKeyToken(b.buildingInstanceKey);
    const fb = byKey.get(key);
    byKey.set(
      key,
      fb === undefined
        ? {
            ...b,
            windowRatio: null,
            windowRatioEstimated: null,
            windowRatioDeterminedBy: null,
          }
        : coalesceBuilding(b, fb),
    );
  }
  const merged = collapseExtractedBuildings({
    projectName: coalesceStr(preferred.projectName, fallback.projectName),
    location: coalesceStr(preferred.location, fallback.location),
    propertyDesignation: coalesceStr(
      preferred.propertyDesignation,
      fallback.propertyDesignation,
    ),
    detailedPlan: coalesceStr(preferred.detailedPlan, fallback.detailedPlan),
    client: coalesceStr(preferred.client, fallback.client),
    storeyCount: coalesceNum(preferred.storeyCount, fallback.storeyCount),
    buildingCount: coalesceBuildingCount(preferred, fallback),
    buildings: [...byKey.values()],
  });
  return backfillNullProgramSharesFromHeuristic(merged, fallback);
};

const distinctValues = (
  candidates: readonly ExtractedDesignFactCandidate[],
  kind: ExtractedDesignFactLeafKind,
): boolean => {
  const head = candidates[0];
  if (head === undefined) return false;
  return candidates.some((c) => !valuesNearlyEqual(kind, head.value, c.value));
};

/**
 * Sidecar rows for admin: only leaves where candidates actually disagreed.
 * `picked` is the value on the resolved tree (LLM or heuristic).
 */
export const buildExtractedDesignFactConflicts = (
  candidates: readonly ExtractedDesignFactCandidate[],
  resolved: ExtractedDesignFacts,
): ExtractedDesignFactConflict[] => {
  const byPath = groupByPath(candidates);
  const resolvedByPath = flattenResolvedToPathMap(resolved);
  const out: ExtractedDesignFactConflict[] = [];
  for (const [path, group] of byPath) {
    const head = group[0];
    if (head === undefined) continue;
    if (!distinctValues(group, head.kind)) continue;
    const picked = resolvedByPath.get(path) ?? null;
    out.push({
      path,
      picked,
      rejected: group
        .filter((c) => picked === null || c.value !== picked)
        .map((c) => ({
          value: c.value,
          source: c.source,
          fileName: c.fileName,
        })),
    });
  }
  return out;
};

const flattenResolvedToPathMap = (
  facts: ExtractedDesignFacts,
): Map<string, string | number> => {
  const map = new Map<string, string | number>();
  const set = (path: string, value: string | number | null) => {
    if (value === null) return;
    map.set(path, value);
  };
  set('projectName', facts.projectName);
  set('location', facts.location);
  set('propertyDesignation', facts.propertyDesignation);
  set('detailedPlan', facts.detailedPlan);
  set('client', facts.client);
  set('storeyCount', facts.storeyCount);
  set('buildingCount', facts.buildingCount);
  for (const b of facts.buildings) {
    const token = buildingKeyToken(b.buildingInstanceKey);
    const prefix = `buildings[${token}]`;
    set(`${prefix}.buildingName`, b.buildingName);
    set(`${prefix}.btaM2`, b.btaM2);
    setVolumePaths(set, prefix, b);
    set(`${prefix}.windowRatio`, b.windowRatio);
    set(`${prefix}.beamSpanM`, b.beamSpanM);
    set(`${prefix}.slabThicknessMm`, b.slabThicknessMm);
    set(`${prefix}.roofForm`, b.roofForm?.type.value ?? null);
    set(
      `${prefix}.roofPitchDeg`,
      b.roofForm !== null && 'pitchDeg' in b.roofForm
        ? (b.roofForm.pitchDeg?.value ?? null)
        : null,
    );
    set(
      `${prefix}.residential.livingAreaPerApartmentM2`,
      b.residential?.livingAreaPerApartmentM2 ?? null,
    );
    set(
      `${prefix}.residential.balconyAreaPerApartmentM2`,
      b.residential?.balconyAreaPerApartmentM2 ?? null,
    );
    set(
      `${prefix}.residential.bathroomAreaPerApartmentM2`,
      b.residential?.bathroomAreaPerApartmentM2 ?? null,
    );
    set(
      `${prefix}.residential.programType`,
      b.residential?.programType ?? null,
    );
    set(
      `${prefix}.stairwells.apartmentsPerStairwellPerStorey`,
      b.stairwells?.apartmentsPerStairwellPerStorey ?? null,
    );
    set(
      `${prefix}.stairwells.elevatorsPerStairwell`,
      b.stairwells?.elevatorsPerStairwell ?? null,
    );
    set(`${prefix}.storeyCount`, b.storeyCount?.value ?? null);
    setStoreyPaths(set, prefix, b.storeys);
    for (const part of b.parts) {
      const partPrefix = `${prefix}.parts[${buildingPartToken(part.name)}]`;
      set(`${partPrefix}.name`, part.name);
      set(`${partPrefix}.storeyCount`, part.storeyCount?.value ?? null);
      setVolumePaths(set, partPrefix, part);
      setStoreyPaths(set, partPrefix, part.storeys);
    }
  }
  return map;
};

const setVolumePaths = (
  set: (path: string, value: string | number | null) => void,
  prefix: string,
  volume: VolumeAreasStored,
): void => {
  set(`${prefix}.ljusBtaM2`, volume.ljusBtaM2);
  set(`${prefix}.boaM2`, volume.boaM2);
  set(`${prefix}.loaM2`, volume.loaM2);
  set(`${prefix}.byaM2`, volume.byaM2);
  set(`${prefix}.apartmentCount`, volume.apartmentCount);
  volume.apartments.forEach((row, index) => {
    const apt = `${prefix}.apartments[${apartmentToken(row, index)}]`;
    set(`${apt}.label`, row.label);
    set(`${apt}.count`, row.count);
    set(`${apt}.areaM2`, row.areaM2);
    set(`${apt}.areaMinM2`, row.areaMinM2);
    set(`${apt}.areaMaxM2`, row.areaMaxM2);
  });
  volume.otherAreas.forEach((row, index) => {
    const area = `${prefix}.otherAreas[${otherAreaToken(row, index)}]`;
    set(`${area}.label`, row.label);
    set(`${area}.areaM2`, row.areaM2);
    set(`${area}.rowScope`, row.rowScope ?? null);
  });
};

const setStoreyPaths = (
  set: (path: string, value: string | number | null) => void,
  volumePrefix: string,
  storeys: readonly ExtractedStoreyFacts[],
): void => {
  storeys.forEach((s, i) => {
    for (const storeyToken of storeyPathTokens(s, i)) {
      const sPrefix = `${volumePrefix}.storeys[${storeyToken}]`;
      set(`${sPrefix}.innerHeightM`, s.innerHeightM);
      set(`${sPrefix}.floorToFloorHeightM`, s.floorToFloorHeightM);
      set(`${sPrefix}.areaM2`, s.areaM2);
      set(`${sPrefix}.elevationMarkM`, s.elevationMarkM);
      set(`${sPrefix}.elevationFromGroundM`, s.elevationFromGroundM);
      for (const [stem, p] of programsWithStems(s.programs)) {
        set(`${sPrefix}.programs.${stem}.areaM2`, p.areaM2);
        set(`${sPrefix}.programs.${stem}.share`, p.share ?? null);
        if (p.type === 'other') {
          set(`${sPrefix}.programs.${stem}.label`, p.label);
        }
      }
    }
  });
};

export const serializeDesignFactCandidatesForMerge = (
  candidates: readonly ExtractedDesignFactCandidate[],
): readonly Record<string, unknown>[] =>
  candidates.map((c) => ({
    path: c.path,
    kind: c.kind,
    value: c.value,
    source: c.source,
    file_name: c.fileName,
    confidence: c.confidence,
    sheet_note: c.sheetNote,
    ...(c.coversBuildingKeys === undefined
      ? {}
      : { covers_building_keys: c.coversBuildingKeys }),
    ...(c.typicalAreaScope === undefined
      ? {}
      : { typical_area_scope: c.typicalAreaScope }),
    ...(c.kind === 'window_ratio'
      ? {
          window_ratio_determined_by:
            c.windowRatioDeterminedBy ??
            (c.windowRatioEstimated === true
              ? 'estimated'
              : c.windowRatioEstimated === false
                ? 'read'
                : null),
        }
      : {}),
  }));

/** Short source label for admin notices — not the raw enum. */
export const EXTRACTED_DESIGN_FACT_SOURCE_SHORT_LABEL: Record<
  ExtractedDesignFactSource,
  string
> = {
  area_schedule: 'areatabell',
  technical_spec: 'FFU',
  project_brief: 'brief',
  project_description: 'description',
  title_block: 'stamp',
  blueprint_side_view: 'fasad/sektion',
  blueprint_top_view: 'plan',
  blueprint: 'drawing',
  joined_blueprint: 'joined drawing',
  building_rendering: 'rendering',
};

/**
 * Last path segment of a stored file name. Drops noisy folder prefixes
 * (`Projekt/A-ritningar/A-40-1-100.pdf` → `A-40-1-100.pdf`).
 */
export const projectFileDisplayBasename = (fileName: string): string => {
  const normalized = fileName.replaceAll('\\', '/');
  const cut = normalized.lastIndexOf('/');
  const base = cut === -1 ? fileName : normalized.slice(cut + 1);
  return base === '' ? fileName : base;
};

export const extractedDesignFactPathBuildingToken = (
  path: string,
): string | null => {
  const match = /^buildings\[([^\]]+)\]/.exec(path);
  return match?.[1] ?? null;
};

const PROJECT_FACT_PATH_LABEL: Record<string, string> = {
  projectName: 'Project name',
  location: 'Location',
  propertyDesignation: 'Property designation',
  detailedPlan: 'Detailed plan',
  client: 'Client',
  storeyCount: 'Storeys',
  buildingCount: 'Buildings',
};

const BUILDING_FACT_LEAF_LABEL: Record<string, string> = {
  buildingName: 'Name',
  btaM2: 'BTA',
  ljusBtaM2: 'Ljus BTA',
  boaM2: 'BOA',
  loaM2: 'LOA',
  byaM2: 'BYA',
  apartmentCount: 'Apartments',
  windowRatio: 'Window ratio',
  beamSpanM: 'Beam span',
  slabThicknessMm: 'Slab thickness',
  roofForm: 'Roof form',
  roofPitchDeg: 'Roof pitch',
  areaPerFloorM2: 'Area per floor',
  innerHeightTypical: 'Inner height',
  storeyCount: 'Storeys',
  'residential.livingAreaPerApartmentM2': 'Apartment area',
  'residential.balconyAreaPerApartmentM2': 'Balcony',
  'residential.bathroomAreaPerApartmentM2': 'Bathroom',
  'residential.programType': 'Residential form',
  'stairwells.apartmentsPerStairwellPerStorey': 'Apartments per stairwell',
  'stairwells.elevatorsPerStairwell': 'Elevators',
};

const STOREY_FACT_LEAF_LABEL: Record<string, string> = {
  innerHeightM: 'inner height',
  floorToFloorHeightM: 'floor-to-floor',
  areaM2: 'area',
  elevationMarkM: 'elevation mark',
  elevationFromGroundM: 'elevation from ground',
};

const STOREY_TOKEN_HEADING: Record<string, string> = {
  typical: 'Normalplan',
  attic: 'Vind',
  mezzanine: 'Entresol',
  split_level: 'Suterräng',
  roof: 'Takplan',
};

const storeyHeading = (token: string): string => {
  const canonical = parseCanonicalStoreyToken(token);
  if (canonical?.label != null) return canonical.label;
  if (canonical?.index != null) return `Storey ${canonical.index}`;
  return STOREY_TOKEN_HEADING[token] ?? token;
};

const PART_PATH_RE = /^parts\[([^\]]+)\]\.(.+)$/;

/**
 * Human label for a candidate/conflict path. Drops `buildings[bldg_1]` so the
 * field can sit on that building's card. Part paths lead with the part token.
 */
export const extractedDesignFactPathLabel = (path: string): string => {
  const project = PROJECT_FACT_PATH_LABEL[path];
  if (project !== undefined) return project;
  const inBuilding = path.replace(/^buildings\[[^\]]+\]\./, '');
  const partMatch = PART_PATH_RE.exec(inBuilding);
  if (partMatch?.[1] !== undefined && partMatch[2] !== undefined) {
    return `${partMatch[1]} · ${volumePathLabel(partMatch[2])}`;
  }
  return volumePathLabel(inBuilding);
};

const volumePathLabel = (rest: string): string => {
  const apartmentMatch = /^apartments\[[^\]]+\]/.exec(rest);
  if (apartmentMatch !== null) return 'Apartment';
  const otherAreaMatch = /^otherAreas\[[^\]]+\]/.exec(rest);
  if (otherAreaMatch !== null) return 'Other area';
  const storeyMatch = /^storeys\[([^\]]+)\]\.(.+)$/.exec(rest);
  if (storeyMatch?.[1] !== undefined && storeyMatch[2] !== undefined) {
    const heading = storeyHeading(storeyMatch[1]);
    const leaf = storeyMatch[2];
    const program = /^programs\.(\w+)(?:\.areaM2)?$/.exec(leaf);
    if (program?.[1] !== undefined) return `${heading} · ${program[1]}`;
    const storeyLeaf = STOREY_FACT_LEAF_LABEL[leaf];
    return storeyLeaf === undefined
      ? `${heading} · ${leaf}`
      : `${heading} · ${storeyLeaf}`;
  }
  return BUILDING_FACT_LEAF_LABEL[rest] ?? rest;
};

export const formatExtractedDesignFactLeafValue = (
  picked: string | number | null,
  conflict: ExtractedDesignFactConflict | undefined,
): string | null => {
  if (typeof picked === 'number') return formatFactNumber(picked);
  if (typeof picked === 'string') {
    const t = picked.trim();
    if (t !== '') return t;
  }
  if (conflict === undefined) return null;
  const summary = formatExtractedDesignFactConflictNotice(conflict).summary;
  return summary === '' ? null : summary;
};

const uniqueStrings = (values: readonly string[]): string[] => [
  ...new Set(values),
];

export type ExtractedDesignFactConflictNotice = {
  summary: string;
  lines: string[];
};

/**
 * Compact admin copy for a disputed leaf. Summary is the competing numbers;
 * lines are basename-only. Omits source when every reading shares one source.
 */
export const formatExtractedDesignFactConflictNotice = (
  row: ExtractedDesignFactConflict,
): ExtractedDesignFactConflictNotice => {
  const byValue = new Map<
    string,
    { sources: ExtractedDesignFactSource[]; files: string[] }
  >();
  const push = (
    value: string | number,
    source: ExtractedDesignFactSource,
    fileName: string,
  ): void => {
    const key = String(value);
    const group = byValue.get(key) ?? { sources: [], files: [] };
    group.sources.push(source);
    group.files.push(projectFileDisplayBasename(fileName));
    byValue.set(key, group);
  };
  for (const rejected of row.rejected) {
    push(rejected.value, rejected.source, rejected.fileName);
  }
  const values = [...byValue.keys()];
  const allSources = uniqueStrings(
    [...byValue.values()].flatMap((g) => g.sources),
  );
  const omitSource = allSources.length <= 1;
  const lines = [...byValue.entries()].map(([value, group]) => {
    const files = uniqueStrings(group.files).join(', ');
    if (omitSource) return `${value} · ${files}`;
    const source = group.sources[0];
    const sourceLabel =
      source === undefined
        ? ''
        : ` · ${EXTRACTED_DESIGN_FACT_SOURCE_SHORT_LABEL[source]}`;
    return `${value}${sourceLabel} · ${files}`;
  });
  const others = values.filter((v) => String(row.picked) !== v);
  const summary =
    row.picked === null
      ? values.join(' / ')
      : others.length === 0
        ? values.join(' / ')
        : `also ${others.join(' / ')}`;
  return { summary, lines };
};

const formatFactNumber = (n: number): string =>
  Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);

/**
 * Printed sheet name wins. Never "Storey 0 PLAN 09 (+7)" — the 0 is our
 * 0-based index, PLAN 09 is the drawing, +7 is the elevation stamp.
 */
export const extractedStoreyDisplayLabel = (
  storey: ExtractedStoreyFacts,
): string => {
  const label = storey.label?.trim();
  const index = storeyLevelIndex(storey.level);
  const base =
    label && label !== ''
      ? label
      : index != null
        ? `Storey ${index + 1}`
        : 'Storey';
  return isEntranceStoreyLevel(storey.level) ? `${base} · botten` : base;
};

const otherProgramRepeatsStoreyArea = (
  storey: ExtractedStoreyFacts,
): boolean => {
  if (storey.programs.length !== 1) return false;
  const only = storey.programs[0];
  if (only === undefined || only.type !== 'other') return false;
  if (storey.areaM2 == null || only.areaM2 == null) return true;
  return Math.abs(storey.areaM2 - only.areaM2) <= 1;
};

export const extractedStoreyHasDisplayableFacts = (
  storey: ExtractedStoreyFacts,
): boolean =>
  storey.areaM2 != null ||
  storey.innerHeightM != null ||
  storey.floorToFloorHeightM != null ||
  isEntranceStoreyLevel(storey.level) ||
  storey.elevationMarkM != null ||
  storey.programs.some((p) => p.areaM2 != null);

/**
 * One line per storey. Omits empty inner/ff. Drops a lone `other` program
 * that just repeats the storey area.
 */
export const formatExtractedStoreyFactsLine = (
  storey: ExtractedStoreyFacts,
): string => {
  const parts: string[] = [];
  if (storey.areaM2 != null)
    parts.push(`${formatFactNumber(storey.areaM2)} m²`);
  if (storey.innerHeightM != null) {
    parts.push(`room height ${formatFactNumber(storey.innerHeightM)} m`);
  }
  if (storey.floorToFloorHeightM != null) {
    parts.push(
      `floor-to-floor ${formatFactNumber(storey.floorToFloorHeightM)} m`,
    );
  }
  if (storey.elevationFromGroundM != null) {
    parts.push(
      `${formatFactNumber(storey.elevationFromGroundM)} m from ground`,
    );
  } else if (storey.elevationMarkM != null) {
    parts.push(`+${formatFactNumber(storey.elevationMarkM)}`);
  }
  if (!otherProgramRepeatsStoreyArea(storey)) {
    const mix: string[] = [];
    for (const p of storey.programs) {
      if (p.areaM2 == null) continue;
      const name = p.type === 'other' ? p.label?.trim() || 'other' : p.type;
      mix.push(`${name} ${formatFactNumber(p.areaM2)} m²`);
    }
    if (mix.length > 0) parts.push(mix.join(', '));
  }
  return parts.join(' · ');
};

export type GroupedExtractedBuildingFacts = ExtractedBuildingFacts & {
  instanceKeys: string[];
};

const firstPresent = <T>(values: readonly (T | null)[]): T | null => {
  for (const v of values) {
    if (v != null) return v;
  }
  return null;
};

/**
 * Collapse names that differ only in spacing and punctuation ("Hus A - Norr"
 * / "HusA-Norr") to one group. Nameless rows
 * stay keyed by {@link ExtractedBuildingFacts.buildingInstanceKey}.
 */
export const extractedBuildingDisplayGroupKey = (
  building: Pick<
    ExtractedBuildingFacts,
    'buildingName' | 'buildingInstanceKey'
  >,
): string => {
  const name = building.buildingName?.trim() ?? '';
  if (name !== '') return normalizeExtractedBuildingName(name);
  return building.buildingInstanceKey ?? '_';
};

const mergeStoreyPrograms = (
  a: ExtractedStoreyFacts['programs'],
  b: ExtractedStoreyFacts['programs'],
): ExtractedStoreyFacts['programs'] => {
  const byKey = new Map(programsWithStems(a));
  for (const [key, p] of programsWithStems(b)) {
    const prev = byKey.get(key);
    byKey.set(key, prev === undefined ? p : coalesceProgram(prev, p));
  }
  return [...byKey.values()];
};

const sumAreas = (a: number | null, b: number | null): number | null => {
  const sum = sumNullable(a, b);
  return sum === null ? null : Math.round(sum * 100) / 100;
};

/**
 * Zone rows that carry the same printed word on one storey are one program,
 * the same as mapped types: their areas and shares are summed.
 */
const sumOtherProgramsByLabel = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  const summed = new Map<string, ExtractedStoreyProgram>();
  const programs: ExtractedStoreyProgram[] = [];
  for (const program of storey.programs) {
    const word =
      program.type === 'other'
        ? normalizeExtractedBuildingName(program.label ?? '')
        : '';
    const prev = word === '' ? undefined : summed.get(word);
    if (prev === undefined) {
      programs.push(program);
      if (word !== '') summed.set(word, program);
      continue;
    }
    const total = {
      ...prev,
      areaM2: sumAreas(prev.areaM2, program.areaM2),
      share: sumNullable(prev.share ?? null, program.share ?? null),
    };
    programs[programs.indexOf(prev)] = total;
    summed.set(word, total);
  }
  return programs.length === storey.programs.length
    ? storey
    : { ...storey, programs };
};

/** Sums `other` zone lines with the same printed word on every storey. */
export const sumOtherProgramsOnStoreys = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  let changed = false;
  const buildings = facts.buildings.map((building) => {
    const storeys = building.storeys.map((storey) => {
      const next = sumOtherProgramsByLabel(storey);
      if (next === storey) return storey;
      changed = true;
      return next;
    });
    const parts = building.parts.map((part) => {
      const partStoreys = part.storeys.map((storey) => {
        const next = sumOtherProgramsByLabel(storey);
        if (next === storey) return storey;
        changed = true;
        return next;
      });
      if (partStoreys.every((row, index) => row === part.storeys[index])) {
        return part;
      }
      return { ...part, storeys: partStoreys };
    });
    if (
      storeys.every((row, index) => row === building.storeys[index]) &&
      parts.every((row, index) => row === building.parts[index])
    ) {
      return building;
    }
    return { ...building, storeys, parts };
  });
  return changed ? { ...facts, buildings } : facts;
};

const storeyMergeKey = (storey: ExtractedStoreyFacts): string => {
  if (storeyIndexOmitted(storey))
    return unplacedStoreyToken(storey.label ?? '');
  if (storeyIndexLocked(storey)) {
    const index = storeyLevelIndex(storey.level);
    if (index != null) return `i_${index}`;
  }
  const key = canonicalStoreyKey({
    label: storey.label,
    index: storeyLevelIndex(storey.level),
  });
  if (key != null) return key.token;
  return `x:${storey.label ?? ''}`;
};

/** First row wins per field; later rows fill its nulls. */
/** Parts read with different storey heights take the middle one. */
const middleHeight = (
  a: number | null | undefined,
  b: number | null | undefined,
): number | null => (a == null ? (b ?? null) : b == null ? a : (a + b) / 2);

const mergeStoreyPair = (
  prev: ExtractedStoreyFacts,
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  if (storeyIndexOmitted(prev) || storeyIndexOmitted(storey)) {
    return omitStoreyIndex({
      ...emptyExtractedStoreyFacts(),
      label: prev.label ?? storey.label,
      planNumbers: [],
      level: null,
      innerHeightM: middleHeight(prev.innerHeightM, storey.innerHeightM),
      floorToFloorHeightM: middleHeight(
        prev.floorToFloorHeightM,
        storey.floorToFloorHeightM,
      ),
      areaM2: prev.areaM2 ?? storey.areaM2,
      programs: mergeStoreyPrograms(prev.programs, storey.programs),
      elevationMarkM: prev.elevationMarkM ?? storey.elevationMarkM,
      elevationFromGroundM:
        prev.elevationFromGroundM ?? storey.elevationFromGroundM,
    });
  }
  const lockedSource = (): ExtractedStoreyFacts | null => {
    if (storeyIndexLocked(prev)) return prev;
    if (storeyIndexLocked(storey)) return storey;
    return null;
  };
  const lockedRow = lockedSource();
  const planNumbers = (): number[] => {
    if (lockedRow != null) return [];
    if (prev.planNumbers.length > 0) return prev.planNumbers;
    return storey.planNumbers;
  };
  const merged: ExtractedStoreyFacts = {
    ...emptyExtractedStoreyFacts(),
    label: lockedRow?.label ?? prev.label ?? storey.label,
    planNumbers: planNumbers(),
    level: lockedRow?.level ?? prev.level ?? storey.level,
    innerHeightM: middleHeight(prev.innerHeightM, storey.innerHeightM),
    floorToFloorHeightM: middleHeight(
      prev.floorToFloorHeightM,
      storey.floorToFloorHeightM,
    ),
    areaM2: prev.areaM2 ?? storey.areaM2,
    programs: mergeStoreyPrograms(prev.programs, storey.programs),
    elevationMarkM: prev.elevationMarkM ?? storey.elevationMarkM,
    elevationFromGroundM:
      prev.elevationFromGroundM ?? storey.elevationFromGroundM,
  };
  if (lockedRow == null) return merged;
  return lockStoreyIndex(merged);
};

const mergeStoreyFacts = (
  rows: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const order: string[] = [];
  const byKey = new Map<string, ExtractedStoreyFacts>();
  for (const storey of rows) {
    const key = storeyMergeKey(storey);
    const prev = byKey.get(key);
    if (prev === undefined) {
      order.push(key);
      byKey.set(key, { ...emptyExtractedStoreyFacts(), ...storey });
      continue;
    }
    byKey.set(key, mergeStoreyPair(prev, storey));
  }
  return order.flatMap((key) => {
    const row = byKey.get(key);
    return row === undefined ? [] : [row];
  });
};

const mergeBuildingParts = (
  parts: readonly ExtractedBuildingPartFacts[],
): ExtractedBuildingPartFacts[] => {
  const byKey = new Map<string, ExtractedBuildingPartFacts>();
  for (const part of parts) {
    const key = partMergeKey(part);
    const prev = byKey.get(key);
    byKey.set(
      key,
      prev === undefined
        ? part
        : {
            name: prev.name,
            storeyCount: prev.storeyCount ?? part.storeyCount,
            btaM2: prev.btaM2 ?? part.btaM2,
            ...coalesceVolumeAreas(prev, part),
            storeys: mergeStoreyFacts([...prev.storeys, ...part.storeys]),
            sourceBuildingKey: prev.sourceBuildingKey ?? part.sourceBuildingKey,
          },
    );
  }
  return [...byKey.values()];
};

const mergeGroupedBuildings = (
  rows: readonly ExtractedBuildingFacts[],
): GroupedExtractedBuildingFacts => {
  const head = rows[0];
  const instanceKeys = [
    ...new Set(rows.map((b) => b.buildingInstanceKey ?? '_')),
  ];
  if (head === undefined) {
    return { ...emptyExtractedBuildingFacts(), instanceKeys };
  }
  const names = rows
    .map((b) => b.buildingName?.trim() ?? '')
    .filter((name) => name !== '')
    .sort((a, b) => b.length - a.length);
  const residentialRows = rows.flatMap((b) =>
    b.residential == null ? [] : [b.residential],
  );
  const stairwellRows = rows.flatMap((b) =>
    b.stairwells == null ? [] : [b.stairwells],
  );
  return {
    buildingInstanceKey: pickCanonicalBuildingInstanceKey(
      rows.map((b) => b.buildingInstanceKey),
    ),
    buildingName: names[0] ?? head.buildingName,
    storeyCount: firstPresent(rows.map((b) => b.storeyCount)),
    btaM2: firstPresent(rows.map((b) => b.btaM2)),
    ...mergeVolumeRows(rows),
    ...mergeWindowRatioFields(rows),
    beamSpanM: firstPresent(rows.map((b) => b.beamSpanM)),
    slabThicknessMm: firstPresent(rows.map((b) => b.slabThicknessMm)),
    roofForm: firstPresent(rows.map((b) => b.roofForm)),
    storeys: mergeStoreyFacts(rows.flatMap((b) => b.storeys)),
    parts: mergeBuildingParts(rows.flatMap((b) => b.parts)),
    connection: firstPresent(rows.map((b) => b.connection)),
    residential:
      residentialRows.length === 0
        ? null
        : {
            livingAreaPerApartmentM2: firstPresent(
              residentialRows.map((r) => r.livingAreaPerApartmentM2),
            ),
            balconyAreaPerApartmentM2: firstPresent(
              residentialRows.map((r) => r.balconyAreaPerApartmentM2),
            ),
            bathroomAreaPerApartmentM2: firstPresent(
              residentialRows.map((r) => r.bathroomAreaPerApartmentM2),
            ),
            programType: firstPresent(
              residentialRows.map((r) => r.programType),
            ),
          },
    stairwells:
      stairwellRows.length === 0
        ? null
        : {
            apartmentsPerStairwellPerStorey: firstPresent(
              stairwellRows.map((r) => r.apartmentsPerStairwellPerStorey),
            ),
            elevatorsPerStairwell: firstPresent(
              stairwellRows.map((r) => r.elevatorsPerStairwell),
            ),
          },
    instanceKeys,
  };
};

/**
 * One row per building name. Duplicate keys and names that differ only in
 * spacing collapse; facts and storeys unioned. Nameless rows attach to the sole
 * named cluster.
 */
export const groupExtractedBuildingsForDisplay = (
  buildings: readonly ExtractedBuildingFacts[],
): GroupedExtractedBuildingFacts[] => {
  const groups = new Map<string, ExtractedBuildingFacts[]>();
  for (const building of buildings) {
    const key = extractedBuildingDisplayGroupKey(building);
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [building]);
    else list.push(building);
  }
  const entries = [...groups.entries()];
  const named = entries.filter(([, rows]) =>
    rows.some((b) => (b.buildingName?.trim() ?? '') !== ''),
  );
  const unnamed = entries.filter(([, rows]) =>
    rows.every((b) => (b.buildingName?.trim() ?? '') === ''),
  );
  const namedHead = named.length === 1 ? named[0] : undefined;
  if (namedHead !== undefined && unnamed.length > 0) {
    for (const [, rows] of unnamed) namedHead[1].push(...rows);
    return [mergeGroupedBuildings(namedHead[1])];
  }
  return entries.map(([, rows]) => mergeGroupedBuildings(rows));
};

const stripInstanceKeys = (
  row: GroupedExtractedBuildingFacts,
): ExtractedBuildingFacts => ({
  buildingInstanceKey: row.buildingInstanceKey,
  buildingName: row.buildingName,
  storeyCount: row.storeyCount,
  btaM2: row.btaM2,
  ...volumeFieldsOf(row),
  windowRatio: row.windowRatio,
  windowRatioEstimated: row.windowRatioEstimated,
  windowRatioDeterminedBy: row.windowRatioDeterminedBy,
  beamSpanM: row.beamSpanM,
  slabThicknessMm: row.slabThicknessMm,
  roofForm: row.roofForm,
  storeys: row.storeys,
  parts: row.parts,
  connection: row.connection,
  residential: row.residential,
  stairwells: row.stairwells,
});

type StoreyWithPlacementFlag = ExtractedStoreyFacts & {
  indexLocked?: boolean;
  indexOmitted?: boolean;
};

/** The model already assigned this index. Later steps must not replace it. */
const lockStoreyIndex = (
  storey: ExtractedStoreyFacts,
): StoreyWithPlacementFlag => ({
  ...storey,
  indexLocked: true,
});

/** The model left this row unindexed. Later steps must not invent an index. */
const omitStoreyIndex = (
  storey: ExtractedStoreyFacts,
): StoreyWithPlacementFlag => ({
  ...storey,
  indexOmitted: true,
});

const storeyIndexLocked = (storey: ExtractedStoreyFacts): boolean =>
  (storey as StoreyWithPlacementFlag).indexLocked === true;

const storeyIndexOmitted = (storey: ExtractedStoreyFacts): boolean =>
  (storey as StoreyWithPlacementFlag).indexOmitted === true;

const withoutPlacementFlags = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  const flagged = storey as StoreyWithPlacementFlag;
  if (flagged.indexLocked !== true && flagged.indexOmitted !== true) {
    return storey;
  }
  const {
    indexLocked: _indexLocked,
    indexOmitted: _indexOmitted,
    ...rest
  } = flagged;
  return rest;
};

/** A garage or cellar keeps the level below ground it was given, else -1. */
const belowGroundLevel = (level: StoreyLevel | null): StoreyLevel => {
  const index = storeyLevelIndex(level);
  if (level != null && index != null && index < 0) return level;
  return { kind: 'single', index: -1 };
};

const canonicalizeStoreyRow = (
  storey: ExtractedStoreyFacts,
  indexInArray: number,
): ExtractedStoreyFacts | null => {
  if (storeyIndexLocked(storey)) {
    return {
      ...emptyExtractedStoreyFacts(),
      ...storey,
      label: storey.label,
      planNumbers: [],
      level: storey.level,
    };
  }
  if (storeyIndexOmitted(storey)) {
    return omitStoreyIndex({
      ...emptyExtractedStoreyFacts(),
      ...storey,
      label: storey.label,
      planNumbers: [],
      level: null,
    });
  }
  if (storey.level?.kind === 'roof') return null;
  const printedLabel = storey.label;
  if (
    labelLooksLikeUnderground(printedLabel) &&
    parsePlanNumbers(printedLabel).length === 0 &&
    storey.planNumbers.length === 0
  ) {
    return {
      ...emptyExtractedStoreyFacts(),
      ...storey,
      label: printedLabel,
      planNumbers: [],
      level: belowGroundLevel(storey.level),
    };
  }
  const key = storeyKeyOf(storey, indexInArray);
  if (key == null) return null;
  if (key.labelledKind === 'roof') return null;
  const namesEntrance =
    key.planNumbers.length === 1 && labelLooksLikeGroundFloor(printedLabel);
  return {
    ...emptyExtractedStoreyFacts(),
    ...storey,
    label: key.label ?? printedLabel,
    planNumbers:
      key.planNumbers.length > 0 ? key.planNumbers : storey.planNumbers,
    level: namesEntrance
      ? { kind: 'single', index: 0 }
      : levelForStoreyKey(key, storey.level),
    elevationMarkM: storey.elevationMarkM ?? parseElevationMark(printedLabel),
  };
};

/**
 * Level for a canonical row: the key's own placement for index and entrance
 * tokens, the labelled special kind at the row's index, otherwise the row's
 * level.
 */
const levelForStoreyKey = (
  key: CanonicalStoreyKey,
  level: StoreyLevel | null,
): StoreyLevel | null => {
  if (key.index !== null) return { kind: 'single', index: key.index };
  switch (key.labelledKind) {
    case null:
      return level;
    case 'entrance':
      return { kind: 'single', index: 0 };
    case 'typical':
      return key.planNumbers.length > 0 ? level : { kind: 'typical' };
    case 'roof':
      return { kind: 'roof' };
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return { kind: key.labelledKind, index: storeyLevelIndex(level) };
    default:
      return assertUnreachable(key.labelledKind);
  }
};

/** The one plan number a row prints; null for ranges, lists and unnumbered rows. */
const storeyPlanNumber = (storey: ExtractedStoreyFacts): number | null =>
  storey.planNumbers.length === 1 ? (storey.planNumbers[0] ?? null) : null;

const isIndexOnlyRow = (storey: ExtractedStoreyFacts): boolean =>
  storey.planNumbers.length === 0 &&
  storey.label === null &&
  storey.level?.kind === 'single';

const isConsolidatedScoredStoreyRow = (
  storey: ExtractedStoreyFacts,
): boolean => {
  if (storey.level?.kind === 'roof') return false;
  const labelKey = canonicalStoreyKey({ label: storey.label });
  if (labelKey?.labelledKind === 'roof') return false;
  if (storey.level?.kind === 'typical') return true;
  if (storey.planNumbers.length > 0) return true;
  if ((labelKey?.planNumbers.length ?? 0) > 0) return true;
  if (isIndexOnlyRow(storey)) {
    return (
      storeyHasScoredContent(storey) ||
      storey.floorToFloorHeightM != null ||
      storey.innerHeightM != null
    );
  }
  if (storey.level != null) return true;
  if (labelKey?.labelledKind === 'typical') return false;
  if (labelKey?.labelledKind != null) return true;
  if (storey.label != null) {
    if (
      storey.floorToFloorHeightM != null ||
      storey.innerHeightM != null ||
      storey.areaM2 != null
    ) {
      return true;
    }
    if (storeyHasScoredContent(storey)) {
      return (labelKey?.planNumbers.length ?? 0) > 0;
    }
    return false;
  }
  return storeyHasScoredContent(storey);
};

const applyIndexHeightsToPlans = (
  plans: readonly ExtractedStoreyFacts[],
  indexRows: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const byPlan = [...plans].sort(
    (a, b) => (a.planNumbers[0] ?? 0) - (b.planNumbers[0] ?? 0),
  );
  const byIndex = [...indexRows].sort(
    (a, b) =>
      (storeyLevelIndex(a.level) ?? 0) - (storeyLevelIndex(b.level) ?? 0),
  );
  return byPlan.map((storey, i) => {
    const fromIndex = byIndex[i];
    if (fromIndex === undefined) return storey;
    return {
      ...storey,
      floorToFloorHeightM:
        storey.floorToFloorHeightM ?? fromIndex.floorToFloorHeightM,
      innerHeightM: storey.innerHeightM ?? fromIndex.innerHeightM,
    };
  });
};

const fillSoloProgramArea = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  if (storey.areaM2 == null || storey.programs.length !== 1) return storey;
  const only = storey.programs[0];
  if (only === undefined || only.areaM2 != null) return storey;
  if (only.share !== undefined && only.share !== null) return storey;
  return { ...storey, programs: [{ ...only, areaM2: storey.areaM2 }] };
};

/**
 * Place plan-numbered storeys from the entrance plan once one is known (a
 * named entrance, or exactly one storey the model marked at 0). When neither
 * the model nor the sheet names the entrance, the gap fill is Plan 10.
 */
const placeStoreysFromEntrance = (
  storeys: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const groundPlan = inferGroundFloorPlanNumber(
    storeys.map((s) => ({
      label: s.label,
      planNumber: storeyPlanNumber(s),
      markedEntrance: isEntranceStoreyLevel(s.level),
    })),
  );
  const placed = storeys.map((storey) =>
    storeyIndexLocked(storey) ||
    storeyIndexOmitted(storey) ||
    groundPlan == null ||
    storey.planNumbers.length === 0
      ? storey
      : {
          ...storey,
          level: placeStoreyLevel({
            level: storey.level,
            planNumbers: storey.planNumbers,
            entrancePlanNumber: groundPlan,
          }),
        },
  );
  const groundRow =
    placed.find(
      (s) => s.planNumbers.length > 0 && isEntranceStoreyLevel(s.level),
    ) ?? placed.find((s) => isEntranceStoreyLevel(s.level));
  const groundMark = groundRow?.elevationMarkM ?? null;
  return placed.map((storey) => ({
    ...storey,
    elevationFromGroundM:
      storey.elevationMarkM != null && groundMark != null
        ? storey.elevationMarkM - groundMark
        : isEntranceStoreyLevel(storey.level)
          ? 0
          : storey.elevationFromGroundM,
  }));
};

const isNamedEntranceRow = (storey: ExtractedStoreyFacts): boolean =>
  storey.planNumbers.length === 0 && labelLooksLikeGroundFloor(storey.label);

/**
 * A named `Entréplan` row without a number folds into the numbered storey
 * that ended up at index 0, so the entrance is not listed twice.
 */
const foldNamedEntranceRow = (
  storeys: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const numbered = storeys.find(
    (s) => s.planNumbers.length > 0 && isEntranceStoreyLevel(s.level),
  );
  const named = storeys.find(
    (storey) => !storeyIndexOmitted(storey) && isNamedEntranceRow(storey),
  );
  if (numbered === undefined || named === undefined) return [...storeys];
  return storeys.flatMap((storey) => {
    if (storey === named) return [];
    return storey === numbered ? [mergeStoreyPair(numbered, named)] : [storey];
  });
};

const labelForPlanNumbers = (storey: ExtractedStoreyFacts): string | null => {
  const printed = printedEntranceLabel(storey.label);
  if (printed != null) return printed;
  if (storey.planNumbers.length === 0) return storey.label;
  return planLabel(storey.planNumbers, storey.label);
};

const planLabelForNumbers = (
  storey: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  if (storey.planNumbers.length === 0) return storey;
  return { ...storey, label: labelForPlanNumbers(storey) };
};

/** An entrance the documents named by word or by index 0, with no plan number. */
const isUnnumberedEntranceRow = (storey: ExtractedStoreyFacts): boolean => {
  if (storey.planNumbers.length > 0) return false;
  if (labelLooksLikeGroundFloor(storey.label)) return true;
  return storey.level?.kind === 'single' && storey.level.index === 0;
};

/**
 * Fills plan number 10 when nothing else named the entrance, and writes
 * `Plan N` on rows whose number is printed. A printed entrance name
 * (`Entréplan`) stays. Does not invent a number for a garage or any other
 * role, and does not change a level a numbered row already has. An unplaced
 * Plan 10 becomes the entrance when nothing else named one.
 */
const assignUnspecifiedEntrancePlanNumber = (
  storeys: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const known = inferGroundFloorPlanNumber(
    storeys.map((storey) => ({
      label: storey.label,
      planNumber: storeyPlanNumber(storey),
      markedEntrance: isEntranceStoreyLevel(storey.level),
    })),
  );
  const entrance = known ?? UNSPECIFIED_ENTRANCE_PLAN_NUMBER;
  return storeys.map((storey) => {
    if (storeyIndexLocked(storey) || storeyIndexOmitted(storey)) return storey;
    if (storey.planNumbers.length > 0) {
      const number = storeyPlanNumber(storey);
      if (
        known == null &&
        number === UNSPECIFIED_ENTRANCE_PLAN_NUMBER &&
        storey.level == null
      ) {
        return planLabelForNumbers({
          ...storey,
          level: { kind: 'single', index: 0 },
        });
      }
      return planLabelForNumbers(storey);
    }
    if (!isUnnumberedEntranceRow(storey)) return storey;
    const numbered = { ...storey, planNumbers: [entrance] };
    return {
      ...numbered,
      label: labelForPlanNumbers(numbered),
      level: { kind: 'single', index: 0 },
    };
  });
};

const singlePlan = (storey: ExtractedStoreyFacts): number | null =>
  storey.planNumbers.length === 1 ? (storey.planNumbers[0] ?? null) : null;

/**
 * Entrance plan implied by numbered floors above the entrance.
 * The entrance row itself is not a source. Disagreeing floors yield null.
 */
const entrancePlanFromUpperFloors = (
  storeys: readonly ExtractedStoreyFacts[],
  stackIndexes: readonly number[],
): number | null => {
  if (stackIndexes.length === 0) return null;
  const low = Math.min(...stackIndexes);
  const high = Math.max(...stackIndexes);
  const implied = new Set<number>();
  for (const storey of storeys) {
    const plan = singlePlan(storey);
    const index = storeyLevelIndex(storey.level);
    if (plan == null || index == null || index <= 0) continue;
    if (index < low || index > high) continue;
    if (index === plan) continue;
    implied.add(plan - index);
  }
  if (implied.size !== 1) return null;
  return [...implied][0] ?? null;
};

/** Entrance implied by a floor that already has both a plan number and an index. */
const entrancePlanFromPlacedFloors = (
  storeys: readonly ExtractedStoreyFacts[],
): number | null => {
  const implied = new Set<number>();
  for (const storey of storeys) {
    const plan = singlePlan(storey);
    const index = storeyLevelIndex(storey.level);
    if (plan == null || index == null || index === plan) continue;
    implied.add(plan - index);
  }
  if (implied.size !== 1) return null;
  return [...implied][0] ?? null;
};

const knownPlanNumbers = (storeys: readonly ExtractedStoreyFacts[]): number[] =>
  [...new Set(storeys.flatMap((storey) => storey.planNumbers))]
    .filter((plan) => Number.isInteger(plan))
    .sort((a, b) => a - b);

/**
 * When a typical row exists, one row per integer plan from the lowest known
 * plan number to the highest. Nothing outside that range. The label is not read.
 */
const fillTypicalPlanSpan = <T extends ExtractedStoreyFacts>(
  storeys: readonly T[],
): T[] => {
  if (!storeys.some((storey) => storey.level?.kind === 'typical')) {
    return [...storeys];
  }
  const known = knownPlanNumbers(storeys);
  const low = known[0];
  const high = known[known.length - 1];
  if (low === undefined || high === undefined || high <= low)
    return [...storeys];
  const present = new Set(known);
  const template = storeys[0];
  const added: T[] = [];
  for (let plan = low; plan <= high; plan += 1) {
    if (present.has(plan)) continue;
    added.push({
      ...template,
      ...emptyExtractedStoreyFacts(),
      label: formatPlanNumbers([plan]),
      planNumbers: [plan],
      level: null,
    } as T);
  }
  if (added.length === 0) return [...storeys];
  return [
    ...storeys.filter((storey) => storey.level?.kind !== 'typical'),
    ...added,
  ];
};

const mergeStoreyFactFields = (
  target: ExtractedStoreyFacts,
  donor: ExtractedStoreyFacts,
): ExtractedStoreyFacts => {
  const programs = mergeStoreyPrograms(target.programs, donor.programs);
  return {
    ...target,
    areaM2: target.areaM2 ?? donor.areaM2,
    innerHeightM: target.innerHeightM ?? donor.innerHeightM,
    floorToFloorHeightM:
      target.floorToFloorHeightM ?? donor.floorToFloorHeightM,
    elevationMarkM: target.elevationMarkM ?? donor.elevationMarkM,
    elevationFromGroundM:
      target.elevationFromGroundM ?? donor.elevationFromGroundM,
    programs,
  };
};

/**
 * Facts from an index row win for the index and for heights the section
 * measured. Other facts stay on the plan row unless that row has none.
 */
const mergeIndexSource = <T extends ExtractedStoreyFacts>(
  planRow: T,
  indexRow: T,
): T => {
  const merged = mergeStoreyFactFields(planRow, indexRow);
  return {
    ...merged,
    level: indexRow.level ?? planRow.level,
    floorToFloorHeightM:
      indexRow.floorToFloorHeightM ?? planRow.floorToFloorHeightM,
    innerHeightM: indexRow.innerHeightM ?? planRow.innerHeightM,
  } as T;
};

/**
 * One row per floor any step already emitted, then the facts stored for
 * that plan. The side view is one index source. Indexes other steps emitted
 * stay when the side view emitted none. A side-view stack stays when those
 * indexes are missing elsewhere. A typical span fills only the plans between
 * the lowest and highest known numbers. Facts copy onto the matching plan
 * number or the matching index. A plan with no stored fact stays empty.
 * Parts are not mixed; the caller passes one volume.
 */
export const completeExtractedStoreys = <T extends ExtractedStoreyFacts>(
  storeys: readonly T[],
): T[] => {
  const spanned = fillTypicalPlanSpan(storeys);
  const stackIndexes = spanned.flatMap((storey) => {
    if (!isIndexOnlyRow(storey)) return [];
    const index = storeyLevelIndex(storey.level);
    return index != null && index >= 0 ? [index] : [];
  });
  const entrance = entrancePlanFromUpperFloors(spanned, stackIndexes);
  const placement = entrancePlanFromPlacedFloors(spanned);
  const fromStack = new Set<ExtractedStoreyFacts>();
  const numbered = spanned.map((storey) => {
    const index = storeyLevelIndex(storey.level);
    if (
      entrance != null &&
      isIndexOnlyRow(storey) &&
      index != null &&
      index >= 0 &&
      stackIndexes.includes(index)
    ) {
      const plan = entrance + index;
      const entranceName = printedEntranceLabel(storey.label);
      const next = {
        ...storey,
        planNumbers: [plan],
        label: entranceName ?? formatPlanNumbers([plan]),
      };
      fromStack.add(next);
      return next;
    }
    const plan = singlePlan(storey);
    if (plan == null || storey.level != null || placement == null) {
      return storey;
    }
    return {
      ...storey,
      level: { kind: 'single' as const, index: plan - placement },
    };
  });
  const byPlan = new Map<number, number>();
  const ordered: T[] = [];
  for (const storey of numbered) {
    const plan = singlePlan(storey);
    if (plan == null) {
      ordered.push(storey);
      continue;
    }
    const at = byPlan.get(plan);
    if (at === undefined) {
      byPlan.set(plan, ordered.length);
      ordered.push(storey);
      continue;
    }
    const previous = ordered[at];
    if (previous === undefined) continue;
    const stack = fromStack.has(previous)
      ? previous
      : fromStack.has(storey)
        ? storey
        : null;
    const other =
      stack === previous ? storey : stack === storey ? previous : null;
    ordered[at] =
      stack != null && other != null
        ? mergeIndexSource(other, stack)
        : (mergeStoreyFactFields(previous, storey) as T);
  }
  const merged = ordered;
  const filled = merged.map((storey) => {
    const plan = singlePlan(storey);
    const index = storeyLevelIndex(storey.level);
    const donors = merged.filter((donor) => {
      if (donor === storey) return false;
      if (plan != null && donor.planNumbers.includes(plan)) return true;
      return (
        index != null &&
        donor.planNumbers.length === 0 &&
        storeyLevelIndex(donor.level) === index
      );
    });
    return donors.reduce((target, donor) => {
      if (donor.planNumbers.length === 0)
        return mergeIndexSource(target, donor);
      return mergeStoreyFactFields(target, donor) as T;
    }, storey);
  });
  const taken = new Set(
    filled.flatMap((storey) => {
      if (storey.planNumbers.length === 0) return [];
      const index = storeyLevelIndex(storey.level);
      return index == null ? [] : [index];
    }),
  );
  return filled.filter((storey) => {
    if (storey.planNumbers.length > 0) return true;
    if (storey.label != null) return true;
    const index = storeyLevelIndex(storey.level);
    return index == null || !taken.has(index);
  });
};

const canonicalizeStoreys = (
  storeys: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const canonical = mergeStoreyFacts(
    storeys.flatMap(normalizeStoredStorey).flatMap((s, i) => {
      const next = canonicalizeStoreyRow(s, i);
      return next == null ? [] : [next];
    }),
  );
  const planRows = canonical.filter((s) => s.planNumbers.length > 0);
  const indexRows = canonical.filter(isIndexOnlyRow);
  const labelledRows = canonical.filter(
    (s) => s.planNumbers.length === 0 && !isIndexOnlyRow(s),
  );
  const cluster = longestContiguousPlanCluster(
    planRows.flatMap((s) => s.planNumbers),
  );
  const clusterStart = cluster[0];
  const inCluster = (storey: ExtractedStoreyFacts): boolean =>
    storey.planNumbers.some((n) => cluster.includes(n));
  /**
   * A numbered entrance whose plans all sit below the contiguous run.
   * The level is already index 0. The label is not read.
   */
  const entranceUnderCluster = (storey: ExtractedStoreyFacts): boolean => {
    if (clusterStart === undefined) return false;
    if (!isEntranceStoreyLevel(storey.level)) return false;
    if (storey.planNumbers.length === 0) return false;
    return Math.max(...storey.planNumbers) < clusterStart;
  };
  const clustered = cluster.length >= 2 ? planRows.filter(inCluster) : planRows;
  const entranceBelow =
    cluster.length >= 2
      ? planRows.filter(
          (storey) => !inCluster(storey) && entranceUnderCluster(storey),
        )
      : [];
  const leadsStack = (storey: ExtractedStoreyFacts): boolean =>
    !storeyIndexOmitted(storey) &&
    (labelLooksLikeUnderground(storey.label) ||
      labelLooksLikeGroundFloor(storey.label) ||
      (storeyLevelIndex(storey.level) ?? 1) < 1);
  const usePlans = clustered.length >= 2;
  const base = usePlans
    ? [
        ...entranceBelow,
        ...labelledRows.filter(leadsStack),
        ...applyIndexHeightsToPlans(clustered, indexRows),
        ...labelledRows.filter((storey) => !leadsStack(storey)),
      ]
    : canonical;
  // A stated storey area stays, including one smaller than another floor.
  const prepared = base.map(fillSoloProgramArea);
  const folded = foldNamedEntranceRow(placeStoreysFromEntrance(prepared));
  const placed = placeStoreysFromEntrance(folded);
  const placedIndex = new Set(
    placed.flatMap((storey) => {
      if (!isIndexOnlyRow(storey)) return [];
      const index = storeyLevelIndex(storey.level);
      return index == null ? [] : [index];
    }),
  );
  const reattached = indexRows.filter((storey) => {
    const index = storeyLevelIndex(storey.level);
    return index != null && !placedIndex.has(index);
  });
  const completed = completeExtractedStoreys([...placed, ...reattached]);
  const leftUnnumbered = new Set(
    completed.filter(
      (storey) =>
        reattached.includes(storey) && storey.planNumbers.length === 0,
    ),
  );
  const numberedByFallback = assignUnspecifiedEntrancePlanNumber(
    completed.filter((storey) => !leftUnnumbered.has(storey)),
  );
  return joinStoreysSharingIndex(
    placeStoreysFromEntrance([
      ...numberedByFallback,
      ...completed.filter((storey) => leftUnnumbered.has(storey)),
    ]),
  )
    .map(withoutPlacementFlags)
    .filter(isConsolidatedScoredStoreyRow);
};

/**
 * Index a row already occupies on its own. A range, a repeated floor, a roof
 * and a row with no index do not.
 */
const indexOccupiedAlone = (storey: ExtractedStoreyFacts): number | null => {
  if (storeyIndexOmitted(storey)) return null;
  const level = storey.level;
  if (level == null) return null;
  switch (level.kind) {
    case 'single':
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return level.index;
    case 'range':
    case 'typical':
    case 'roof':
      return null;
    default:
      return assertUnreachable(level);
  }
};

/** Special kind already on this row, placed at `index`. A plain index is not one. */
const specialLevelAtIndex = (
  level: StoreyLevel | null,
  index: number,
): StoreyLevel | null => {
  if (level == null) return null;
  switch (level.kind) {
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return { kind: level.kind, index };
    case 'single':
    case 'range':
    case 'typical':
    case 'roof':
      return null;
    default:
      return assertUnreachable(level);
  }
};

const planNumberKey = (storey: ExtractedStoreyFacts): string | null =>
  storey.planNumbers.length === 0
    ? null
    : [...storey.planNumbers].sort((a, b) => a - b).join(',');

/** Two printed plan lists at one index are two claims, not one storey. */
const planNumbersDisagree = (
  prev: ExtractedStoreyFacts,
  storey: ExtractedStoreyFacts,
): boolean => {
  const left = planNumberKey(prev);
  const right = planNumberKey(storey);
  return left != null && right != null && left !== right;
};

/**
 * Rows already placed on one index are one storey. A special kind on either
 * row is the level. Facts fill whatever that row left empty. A row with no
 * index stays as it is. Two different plan numbers at that index stay apart.
 */
const joinStoreysSharingIndex = (
  storeys: readonly ExtractedStoreyFacts[],
): ExtractedStoreyFacts[] => {
  const at = new Map<number, number>();
  const out: ExtractedStoreyFacts[] = [];
  for (const storey of storeys) {
    const index = indexOccupiedAlone(storey);
    if (index == null) {
      out.push(storey);
      continue;
    }
    const pos = at.get(index);
    const prev = pos === undefined ? undefined : out[pos];
    if (pos === undefined || prev === undefined) {
      at.set(index, out.length);
      out.push(storey);
      continue;
    }
    if (planNumbersDisagree(prev, storey)) {
      out.push(storey);
      continue;
    }
    const merged = mergeStoreyFactFields(prev, storey);
    const level =
      specialLevelAtIndex(prev.level, index) ??
      specialLevelAtIndex(storey.level, index) ??
      prev.level ??
      storey.level;
    out[pos] = {
      ...merged,
      label: prev.label ?? storey.label,
      planNumbers:
        prev.planNumbers.length > 0 ? prev.planNumbers : storey.planNumbers,
      level,
    };
  }
  return out;
};

/**
 * Upper index the repeated floor does not occupy. A placed attic or technical
 * floor is that bound. Otherwise an exact count, and only when no unplaced
 * attic or technical floor could still be the top.
 */
const repeatedFloorSpanEnd = (
  storeys: readonly ExtractedStoreyFacts[],
  storeyCount: StoreyCount | null,
): number | null => {
  const placedTops = storeys.flatMap((storey) => {
    const kind = storey.level?.kind;
    if (kind !== 'attic' && kind !== 'technical') return [];
    const index = storeyLevelIndex(storey.level);
    return index != null && index >= 1 ? [index] : [];
  });
  const count = storeyCount?.kind === 'exact' ? storeyCount.value : null;
  if (placedTops.length > 0) return Math.min(...placedTops);
  const unplacedTop = storeys.some((storey) => {
    const kind = storey.level?.kind;
    return (
      (kind === 'attic' || kind === 'technical') &&
      storeyLevelIndex(storey.level) == null
    );
  });
  if (unplacedTop || count == null || count <= 1) return null;
  return count;
};

const repeatedFloorFromTemplate = (
  template: ExtractedStoreyFacts,
  index: number,
): ExtractedStoreyFacts => ({
  ...emptyExtractedStoreyFacts(),
  label: template.label,
  level: { kind: 'single', index },
  innerHeightM: template.innerHeightM,
  floorToFloorHeightM: template.floorToFloorHeightM,
  areaM2: template.areaM2,
  programs: template.programs,
  elevationMarkM: template.elevationMarkM,
  elevationFromGroundM: template.elevationFromGroundM,
});

/**
 * A `typical` row is the repeated floor, not an index. When the entrance is
 * placed and the floors above it are bounded, each of those floors is its own
 * storey. The row stays when that span is not known.
 */
const materializeTypicalStoreys = (
  storeys: readonly ExtractedStoreyFacts[],
  storeyCount: StoreyCount | null,
): ExtractedStoreyFacts[] => {
  const templates = storeys.filter(
    (storey) => storey.level?.kind === 'typical',
  );
  const template = templates[0];
  if (template === undefined) return [...storeys];
  const rest = storeys.filter((storey) => storey.level?.kind !== 'typical');
  if (!rest.some((storey) => isEntranceStoreyLevel(storey.level))) {
    return [...storeys];
  }
  const spanEnd = repeatedFloorSpanEnd(rest, storeyCount);
  if (spanEnd == null || spanEnd <= 1) return [...storeys];
  const donor = templates
    .slice(1)
    .reduce((acc, row) => mergeStoreyFactFields(acc, row), template);
  const hosts = new Set<ExtractedStoreyFacts>();
  const added: ExtractedStoreyFacts[] = [];
  for (let index = 1; index < spanEnd; index += 1) {
    const host = rest.find((storey) =>
      storeyLevelPositionIndexes(storeyLevelPosition(storey.level)).includes(
        index,
      ),
    );
    if (host !== undefined) {
      hosts.add(host);
      continue;
    }
    added.push(repeatedFloorFromTemplate(donor, index));
  }
  const kept = rest.map((storey) =>
    hosts.has(storey) ? mergeStoreyFactFields(storey, donor) : storey,
  );
  return [...kept, ...added].sort(
    (a, b) => storeyLevelSortKey(a.level) - storeyLevelSortKey(b.level),
  );
};

const canonicalizeFactsStoreys = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => ({
  ...facts,
  buildings: facts.buildings.map((b) => ({
    ...b,
    storeys: materializeTypicalStoreys(
      canonicalizeStoreys(b.storeys),
      b.storeyCount,
    ),
    parts: b.parts.map((part) => ({
      ...part,
      storeys: materializeTypicalStoreys(
        canonicalizeStoreys(part.storeys),
        part.storeyCount,
      ),
    })),
  })),
});

const withPlanDerivedStoreyCount = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  const withStoreys = canonicalizeFactsStoreys(facts);
  const tokens = withStoreys.buildings.flatMap((b) =>
    [b.storeys, ...b.parts.map((part) => part.storeys)].flatMap((storeys) =>
      storeys.flatMap((s, i) => storeyPathTokens(s, i)),
    ),
  );
  const plans = uniquePlanNumbersFromStoreyTokens(tokens);
  if (plans.length < 2) return withStoreys;
  return { ...withStoreys, storeyCount: plans.length };
};

/**
 * Collapse alias buildings on the resolved tree. `buildingCount` 1 → one
 * row when the tree has not already named several houses. Same name → one
 * row. Used after heuristic resolve and LLM coalesce. Unique `Plan N` rows
 * override ranked `storeyCount`. Junk storey labels and area-table indexes
 * are dropped.
 */
const storeyHasScoredContent = (storey: ExtractedStoreyFacts): boolean =>
  storey.areaM2 != null ||
  storey.programs.some((program) => program.areaM2 != null);

/** BTA, window ratio, apartment count, or a storey with area — not a label-only shell. */
export const buildingHasScoredSubstance = (
  building: ExtractedBuildingFacts,
): boolean => {
  if (building.btaM2 != null) return true;
  if (building.windowRatio !== null) return true;
  if (building.apartmentCount != null) return true;
  if (building.storeys.some(storeyHasScoredContent)) return true;
  if (
    building.parts.some(
      (part) => part.btaM2 != null || part.storeys.some(storeyHasScoredContent),
    )
  ) {
    return true;
  }
  return false;
};

const dropNonSubstantiveScoredBuildings = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  const keyed = facts.buildings.filter((b) => b.buildingInstanceKey != null);
  const substantive = keyed.filter(buildingHasScoredSubstance);
  if (facts.buildingCount === 1 && keyed.length > 1 && substantive.length > 0) {
    return facts;
  }
  if (substantive.length === 0 || substantive.length === keyed.length) {
    return facts;
  }
  const kept = facts.buildings.filter((building) => {
    if (building.buildingInstanceKey === null) return true;
    return buildingHasScoredSubstance(building);
  });
  return {
    ...facts,
    buildings: kept,
    buildingCount:
      facts.buildingCount === null
        ? kept.filter((b) => b.buildingInstanceKey != null).length
        : Math.min(facts.buildingCount, kept.length),
  };
};

/**
 * An unkeyed building (`buildings[_]`) is project-level residue. It is not a
 * building once the tree has any keyed building — unless the batch is one
 * building, where every key already collapses together.
 */
const dropUnkeyedBuildingWhenKeyedExist = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  const keyed = facts.buildings.filter((b) => b.buildingInstanceKey != null);
  const severalHouses = countsSeveralHouses(
    keyed.flatMap((building) =>
      building.buildingInstanceKey == null
        ? []
        : [building.buildingInstanceKey],
    ),
  );
  if (facts.buildingCount === 1 && !severalHouses) return facts;
  if (keyed.length === 0 || keyed.length === facts.buildings.length) {
    return facts;
  }
  return { ...facts, buildings: keyed };
};

const keysOfSingleBuilding = (
  building: ExtractedBuildingFacts,
): readonly string[] =>
  building.buildingInstanceKey === null ? [] : [building.buildingInstanceKey];

const keysOfGroupedBuilding = (
  row: GroupedExtractedBuildingFacts,
): readonly string[] =>
  row.instanceKeys.length > 0 ? row.instanceKeys : keysOfSingleBuilding(row);

/** Resolved buildings plus the alias keys folded into each row. */
export type CollapsedExtractedBuildings = {
  facts: ExtractedDesignFacts;
  /** Parallel to {@link CollapsedExtractedBuildings.facts} `buildings`. */
  instanceKeys: readonly (readonly string[])[];
};

/**
 * Same collapse as {@link collapseExtractedBuildings}. `instanceKeys` keeps
 * every key the row absorbed. The working building keeps one id.
 */
export const collapseExtractedBuildingsWithKeys = (
  facts: ExtractedDesignFacts,
): CollapsedExtractedBuildings => {
  const factsWithoutPhantom = dropUnkeyedBuildingWhenKeyedExist(
    dropNonSubstantiveScoredBuildings(facts),
  );
  if (factsWithoutPhantom.buildings.length <= 1) {
    const resolved = withPlanDerivedStoreyCount(factsWithoutPhantom);
    return {
      facts: resolved,
      instanceKeys: resolved.buildings.map(keysOfSingleBuilding),
    };
  }
  const houseKeys = factsWithoutPhantom.buildings.flatMap((building) =>
    building.buildingInstanceKey == null ? [] : [building.buildingInstanceKey],
  );
  const keyedWithScheduleBta = factsWithoutPhantom.buildings.filter(
    (building) =>
      building.buildingInstanceKey != null && building.btaM2 != null,
  );
  const collapseToSingleScoredVolume =
    !countsSeveralHouses(houseKeys) &&
    (factsWithoutPhantom.buildingCount === 1 ||
      (keyedWithScheduleBta.length === 1 &&
        factsWithoutPhantom.buildings.filter(
          (building) => building.buildingInstanceKey != null,
        ).length > 1));
  if (collapseToSingleScoredVolume) {
    const grouped = mergeGroupedBuildings(factsWithoutPhantom.buildings);
    return {
      facts: withPlanDerivedStoreyCount({
        ...factsWithoutPhantom,
        buildings: [stripInstanceKeys(grouped)],
      }),
      instanceKeys: [keysOfGroupedBuilding(grouped)],
    };
  }
  const groupedRows = groupExtractedBuildingsForDisplay(
    factsWithoutPhantom.buildings,
  );
  const grouped = withPlanDerivedStoreyCount({
    ...factsWithoutPhantom,
    buildings: groupedRows.map(stripInstanceKeys),
  });
  const resolved =
    grouped.buildingCount !== 1 || grouped.buildings.length <= 1
      ? grouped
      : { ...grouped, buildingCount: grouped.buildings.length };
  return {
    facts: resolved,
    instanceKeys: groupedRows.map(keysOfGroupedBuilding),
  };
};

/**
 * The collapsed buildings with the rows that stand joined as one house merged
 * into one row. Each group is the ids of one house; a row joins the first
 * group its keys meet. Rows no group names stay as they are.
 */
export const joinedIntoHouses = (
  collapsed: CollapsedExtractedBuildings,
  houses: readonly (readonly string[])[],
): CollapsedExtractedBuildings => {
  const houseOf = (keys: readonly string[]): number =>
    houses.findIndex((ids) => ids.some((id) => keys.includes(id)));
  const rows: { indexes: number[]; house: number }[] = [];
  collapsed.facts.buildings.forEach((_, index) => {
    const house = houseOf(collapsed.instanceKeys[index] ?? []);
    const joined =
      house < 0 ? undefined : rows.find((row) => row.house === house);
    if (joined === undefined) rows.push({ indexes: [index], house });
    else joined.indexes.push(index);
  });
  if (rows.length === collapsed.facts.buildings.length) return collapsed;
  const buildings = rows.map(({ indexes }) => {
    const members = indexes.flatMap((index) => {
      const building = collapsed.facts.buildings[index];
      return building === undefined ? [] : [building];
    });
    return members.length === 1 && members[0] !== undefined
      ? members[0]
      : stripInstanceKeys(mergeGroupedBuildings(members));
  });
  return {
    facts: withPlanDerivedStoreyCount({
      ...collapsed.facts,
      buildings,
      buildingCount: buildings.length,
    }),
    instanceKeys: rows.map(({ indexes }) => [
      ...new Set(
        indexes.flatMap((index) => collapsed.instanceKeys[index] ?? []),
      ),
    ]),
  };
};

export const collapseExtractedBuildings = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => collapseExtractedBuildingsWithKeys(facts).facts;

const compositionKeyMatches = (
  building: ExtractedBuildingFacts,
  key: string,
): boolean => {
  const normalized = normalizeExtractedBuildingName(key);
  return (
    building.buildingInstanceKey === key ||
    (building.buildingInstanceKey !== null &&
      normalizeExtractedBuildingName(building.buildingInstanceKey) ===
        normalized) ||
    (building.buildingName !== null &&
      normalizeExtractedBuildingName(building.buildingName) === normalized)
  );
};

const houseAsParts = (
  house: ExtractedBuildingFacts,
): ExtractedBuildingPartFacts[] => {
  const key = house.buildingInstanceKey;
  const name = house.buildingName ?? key ?? 'Hus';
  const own: ExtractedBuildingPartFacts = {
    name,
    storeyCount: house.storeyCount,
    btaM2: house.btaM2,
    ...volumeFieldsOf(house),
    storeys: house.storeys,
    sourceBuildingKey: key,
  };
  return [
    ...(house.storeys.length > 0 || house.parts.length === 0 ? [own] : []),
    ...house.parts.map((part) => ({ ...part, sourceBuildingKey: key })),
  ];
};

const houseEvidenceRank = (house: ExtractedBuildingFacts): number =>
  (house.storeys.length > 0 ? 2 : 0) + (house.btaM2 != null ? 1 : 0);

const houseHasOwnStoreyStack = (house: ExtractedBuildingFacts): boolean =>
  house.storeys.length > 0 ||
  house.parts.some((part) => part.storeys.length > 0);

/**
 * Joined blueprint rows sometimes self-report a shared body via
 * `physicalAttachments`. When the merger left `buildingCompositions` empty,
 * derive one composition row per connected cluster.
 */
export const buildingCompositionsFromJoinedBlueprintExtractions = (
  joined: readonly ProjectFileBatchJoinedBlueprintExtraction[],
): ProjectFileBatchBuildingComposition[] => {
  const keyByLabel = new Map<string, string>();
  for (const row of joined) {
    const key = trimNull(row.buildingInstanceKey);
    const label = trimNull(row.buildingLabel);
    if (key === null) continue;
    keyByLabel.set(key, key);
    if (label !== null) {
      keyByLabel.set(normalizeExtractedBuildingName(label), key);
    }
  }
  const resolveOtherKey = (label: string): string | null => {
    const ref = extractBuildingReference(label, label);
    if (ref !== null) return ref.key;
    const normalized = normalizeExtractedBuildingName(label);
    const direct = keyByLabel.get(normalized);
    if (direct !== undefined) return direct;
    for (const row of joined) {
      const rowLabel = trimNull(row.buildingLabel);
      if (
        rowLabel !== null &&
        normalizeExtractedBuildingName(rowLabel) === normalized
      ) {
        return trimNull(row.buildingInstanceKey);
      }
    }
    return null;
  };
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    const p = parent.get(key);
    if (p === undefined || p === key) return key;
    const root = find(p);
    parent.set(key, root);
    return root;
  };
  const unite = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(rb, ra);
  };
  for (const row of joined) {
    const self = trimNull(row.buildingInstanceKey);
    if (self === null) continue;
    parent.set(self, find(self));
    for (const attachment of row.physicalAttachments) {
      if (!attachment.shouldModelAsSingleBuilding) continue;
      const other = resolveOtherKey(attachment.otherBuildingLabel);
      if (other === null || other === self) continue;
      unite(self, other);
    }
  }
  const clusters = new Map<string, Set<string>>();
  for (const row of joined) {
    const key = trimNull(row.buildingInstanceKey);
    if (key === null) continue;
    const root = find(key);
    const set = clusters.get(root) ?? new Set<string>();
    set.add(key);
    clusters.set(root, set);
  }
  const out: ProjectFileBatchBuildingComposition[] = [];
  for (const keys of clusters.values()) {
    if (keys.size < 2) continue;
    const composingBuildingKeys = [...keys].sort();
    const note =
      joined
        .flatMap((row) =>
          row.buildingInstanceKey !== null && keys.has(row.buildingInstanceKey)
            ? row.physicalAttachments
                .filter((a) => a.shouldModelAsSingleBuilding)
                .map((a) => a.sharedStructure)
            : [],
        )
        .find((text) => text.trim() !== '') ?? '';
    out.push({
      composingBuildingKeys,
      relationship: 'connected_volume',
      compositionNote: note,
      shouldModelAsSingleBuilding: true,
      areas: null,
    });
  }
  return out;
};

const WINDOW_RATIO_KIND_BY_METHOD = {
  read: 'printed',
  calculated: 'calculated',
  estimated: 'estimated',
} as const satisfies Record<
  WindowRatioDeterminedBy,
  'printed' | 'calculated' | 'estimated'
>;

/** Same order as {@link chooseWindowRatio}. */
const WINDOW_RATIO_KIND_ORDER = ['printed', 'calculated', 'estimated'] as const;

type FoldedWindowRatioKind = (typeof WINDOW_RATIO_KIND_ORDER)[number];

type WindowRatioFields = Pick<
  ExtractedBuildingFacts,
  'windowRatio' | 'windowRatioEstimated' | 'windowRatioDeterminedBy'
>;

const windowRatioFieldsOf = (house: WindowRatioFields): WindowRatioFields => ({
  windowRatio: house.windowRatio,
  windowRatioEstimated: house.windowRatioEstimated,
  windowRatioDeterminedBy: house.windowRatioDeterminedBy,
});

/**
 * Kind of a ratio the house already chose. Matches
 * {@link windowRatioEvidenceFromCandidate}: a false estimate flag is printed.
 */
const windowRatioKindForFold = (
  house: WindowRatioFields,
): FoldedWindowRatioKind | null => {
  if (house.windowRatio === null) return null;
  if (house.windowRatioDeterminedBy !== null) {
    return WINDOW_RATIO_KIND_BY_METHOD[house.windowRatioDeterminedBy];
  }
  if (house.windowRatioEstimated === true) return 'estimated';
  if (house.windowRatioEstimated === false) return 'printed';
  return null;
};

/**
 * Houses are already ranked, the one that holds the storeys first. The
 * strongest method wins. The same method keeps that house's chosen ratio
 * instead of the alphabetically earlier instance key.
 */
const windowRatioKeptOnAliasFold = (
  houses: readonly ExtractedBuildingFacts[],
): WindowRatioFields | null => {
  for (const kind of WINDOW_RATIO_KIND_ORDER) {
    const house = houses.find((row) => windowRatioKindForFold(row) === kind);
    if (house !== undefined) return windowRatioFieldsOf(house);
  }
  return null;
};

/**
 * Alias keys of one house are not parts. The row that already holds the
 * storeys stays the building; the other keys only fill fields it leaves
 * empty. A window ratio that row already chose stays, unless an alias was
 * determined by a stronger method. Distinct house keys still become parts
 * in {@link joinHouses}.
 */
const foldAliasHouses = (
  houses: readonly ExtractedBuildingFacts[],
): ExtractedBuildingFacts => {
  const ranked = [...houses].sort(
    (a, b) => houseEvidenceRank(b) - houseEvidenceRank(a),
  );
  const merged = stripInstanceKeys(mergeGroupedBuildings(ranked));
  const primary = ranked[0];
  if (primary === undefined) return merged;
  const windowRatio = windowRatioKeptOnAliasFold(ranked);
  return {
    ...merged,
    buildingInstanceKey: primary.buildingInstanceKey,
    buildingName: primary.buildingName,
    ...(windowRatio === null ? {} : windowRatio),
  };
};

/**
 * Sum of the values the houses state. Null when none states one, and with
 * `every_house` also when one house does not: every house has that area,
 * so a missing one makes the sum a part of the total.
 */
const statedSum = (
  values: readonly (number | null)[],
  required: 'every_house' | 'stated_houses',
): number | null => {
  const stated = values.filter((value): value is number => value != null);
  if (stated.length === 0) return null;
  if (required === 'every_house' && stated.length < values.length) return null;
  return stated.reduce((sum, v) => sum + v, 0);
};

const joinHouses = (
  houses: readonly ExtractedBuildingFacts[],
  composition: ProjectFileBatchBuildingComposition,
): ExtractedBuildingFacts => ({
  ...emptyExtractedBuildingFacts(),
  buildingInstanceKey: houses[0]?.buildingInstanceKey ?? null,
  buildingName: houses
    .map((house) => house.buildingName ?? house.buildingInstanceKey ?? '')
    .filter((name) => name !== '')
    .join(' + '),
  ...mergeWindowRatioFields(houses),
  beamSpanM: firstPresent(houses.map((house) => house.beamSpanM)),
  slabThicknessMm: firstPresent(houses.map((house) => house.slabThicknessMm)),
  roofForm: firstPresent(houses.map((house) => house.roofForm)),
  btaM2: statedSum(
    houses.map((house) => house.btaM2),
    'every_house',
  ),
  ljusBtaM2: statedSum(
    houses.map((house) => house.ljusBtaM2),
    'every_house',
  ),
  boaM2: statedSum(
    houses.map((house) => house.boaM2),
    'stated_houses',
  ),
  loaM2: statedSum(
    houses.map((house) => house.loaM2),
    'stated_houses',
  ),
  byaM2: statedSum(
    houses.map((house) => house.byaM2),
    'every_house',
  ),
  parts: houses.flatMap(houseAsParts),
  connection: {
    relationship: composition.relationship,
    note: composition.compositionNote,
  },
});

/**
 * Houses the composition step marked as one building
 * (`shouldModelAsSingleBuilding`, typically a shared garage) become one
 * building. Distinct house keys (`hus`, `byggnad`, `building`, `block`)
 * become `parts`, each keeping its key, name, and storeys. Any other keys
 * are aliases of that house: their storeys and areas stay on the building.
 * A house joins at most one composition; rows that match fewer than two
 * buildings are ignored. `buildingCount` drops by the houses merged away.
 */
type PlanDrawing = Pick<
  ExtractedDrawing,
  | 'buildingInstanceId'
  | 'buildingInstanceIds'
  | 'buildingLabel'
  | 'drawingKind'
  | 'storeyKind'
  | 'planNumbers'
  | 'viewLabel'
  | 'coverage'
>;

const storeyFromPlanDrawing = (
  drawing: PlanDrawing,
): ExtractedStoreyFacts[] => {
  if (drawing.drawingKind !== 'plan') return [];
  const coverage = drawing.coverage ?? drawingPlanCoverage(drawing);
  if (coverage === null) return [];
  if (coverage.kind === 'specific' && coverage.level.kind === 'roof') return [];
  if (coverage.kind === 'remaining') {
    return [
      {
        ...emptyExtractedStoreyFacts(),
        label: drawing.viewLabel,
        planNumbers: drawing.planNumbers,
        level: { kind: 'typical' },
      },
    ];
  }
  const indexes = storeyLevelPositionIndexes(
    storeyLevelPosition(coverage.level),
  );
  if (
    coverage.level.kind === 'range' &&
    indexes.length > 1 &&
    indexes.length === drawing.planNumbers.length
  ) {
    return indexes.flatMap((index, offset) => {
      const planNumber = drawing.planNumbers[offset];
      if (planNumber === undefined) return [];
      const level: StoreyLevel = { kind: 'single', index };
      return [
        {
          ...emptyExtractedStoreyFacts(),
          label: planLabel([planNumber], drawing.viewLabel),
          planNumbers: [planNumber],
          level,
        },
      ];
    });
  }
  const planNumber =
    drawing.planNumbers.length === 1 ? drawing.planNumbers[0] : undefined;
  const numbered = {
    ...emptyExtractedStoreyFacts(),
    label: drawing.viewLabel,
    planNumbers: planNumber === undefined ? [] : [planNumber],
    level: coverage.level,
  };
  return [{ ...numbered, label: labelForPlanNumbers(numbered) }];
};

/**
 * Plan numbers on a repeated sheet name the storeys above the entrance.
 * The sheet stays `remaining`, so those rows are not its own drawings.
 * Null when this drawing is not a numbered repeated plan, or the entrance
 * plan is unknown — a plan number is never invented.
 */
const storeysNamedByRepeatedPlan = (
  drawing: PlanDrawing,
  entrancePlanNumber: number | null,
): ExtractedStoreyFacts[] | null => {
  const coverage = drawing.coverage ?? drawingPlanCoverage(drawing);
  if (
    coverage?.kind !== 'remaining' ||
    drawing.planNumbers.length === 0 ||
    entrancePlanNumber == null
  ) {
    return null;
  }
  return drawing.planNumbers.flatMap((planNumber) => {
    const index = planNumber - entrancePlanNumber;
    if (index < 1) return [];
    return [
      {
        ...emptyExtractedStoreyFacts(),
        label: planLabel([planNumber], drawing.viewLabel),
        planNumbers: [planNumber],
        level: { kind: 'single', index },
      },
    ];
  });
};

/** Entrance plan printed on an entrance sheet, else Plan 10. */
const entrancePlanFromDrawings = (drawings: readonly PlanDrawing[]): number => {
  const named = drawings.find(
    (drawing) =>
      drawing.drawingKind === 'plan' &&
      drawing.storeyKind === 'entrance' &&
      drawing.planNumbers.length === 1,
  );
  return named?.planNumbers[0] ?? UNSPECIFIED_ENTRANCE_PLAN_NUMBER;
};

const storeysFromPlanDrawings = (
  drawings: readonly PlanDrawing[],
  entrancePlanNumber: number | null,
): ExtractedStoreyFacts[] => {
  const out: ExtractedStoreyFacts[] = [];
  const seen = new Set<string>();
  for (const drawing of drawings) {
    const named = storeysNamedByRepeatedPlan(drawing, entrancePlanNumber);
    const rows = named ?? storeyFromPlanDrawing(drawing);
    for (const storey of rows) {
      if (storey.level == null) continue;
      const key = `${storey.planNumbers.join(',')}\0${JSON.stringify(storey.level)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(storey);
    }
  }
  return out;
};

const exactCount = (count: StoreyCount | null): number | null =>
  count?.kind === 'exact' ? count.value : null;

const buildingHasStoreys = (building: ExtractedBuildingFacts): boolean =>
  building.storeys.length > 0 ||
  building.parts.some((part) => part.storeys.length > 0);

const drawingAttachmentText = (
  drawing: Pick<PlanDrawing, 'buildingLabel' | 'viewLabel'>,
): string =>
  [drawing.buildingLabel, drawing.viewLabel]
    .filter((part) => part != null && part.trim() !== '')
    .join('\n');

const entrancePlanByBuildingFromFacts = (
  facts: ExtractedDesignFacts,
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const building of facts.buildings) {
    const storeys = [
      ...building.storeys,
      ...building.parts.flatMap((part) => part.storeys),
    ];
    const known = inferGroundFloorPlanNumber(
      storeys.map((storey) => ({
        label: storey.label,
        planNumber: storeyPlanNumber(storey),
        markedEntrance: isEntranceStoreyLevel(storey.level),
      })),
    );
    const entrance = known ?? UNSPECIFIED_ENTRANCE_PLAN_NUMBER;
    const key = building.buildingInstanceKey;
    if (key != null) map.set(key, entrance);
  }
  if (facts.buildings.length !== 1) return map;
  const only = [...map.values()][0];
  if (only !== undefined) map.set('_', only);
  return map;
};

/**
 * Tag each plan to the buildings its label names, then place plan numbers
 * and coverage from those buildings' storeys. An entrance with no printed
 * number is Plan 10. A number the model or the sheet already gave is kept.
 */
export const placeDrawingsForExtractedStoreys = (
  facts: ExtractedDesignFacts,
  drawings: readonly ExtractedDrawing[],
  known: readonly KnownBuildingIdentity[],
): ExtractedDrawing[] => {
  const attached =
    known.length === 0
      ? [...drawings]
      : drawings.map((drawing) => {
          const keys = resolveDrawingBuildingKeys(
            [
              drawing.buildingInstanceId,
              ...(drawing.buildingInstanceIds ?? []),
            ],
            drawingAttachmentText(drawing),
            known,
          );
          const buildingInstanceId =
            keys.length === 1 ? (keys[0] ?? null) : null;
          const previous = drawing.buildingInstanceIds ?? [];
          const sameIds =
            previous.length === keys.length &&
            previous.every((key, index) => key === keys[index]);
          if (drawing.buildingInstanceId === buildingInstanceId && sameIds) {
            return drawing;
          }
          return { ...drawing, buildingInstanceId, buildingInstanceIds: keys };
        });
  return placeDrawingCoverages(attached, {
    entrancePlanByBuilding: entrancePlanByBuildingFromFacts(facts),
  });
};

const planNumbersOverlap = (
  a: readonly number[],
  b: readonly number[],
): boolean => a.some((plan) => b.includes(plan));

const storeyPlanNumbersForMatch = (
  storey: ExtractedStoreyFacts,
): readonly number[] =>
  storey.planNumbers.length > 0
    ? storey.planNumbers
    : parsePlanNumbers(storey.label);

const isEntranceStorey = (storey: ExtractedStoreyFacts): boolean =>
  isEntranceStoreyLevel(storey.level) ||
  labelLooksLikeGroundFloor(storey.label) ||
  canonicalStoreyKey({ label: storey.label })?.labelledKind === 'entrance';

/**
 * Same storey when a plan number is shared (including numbers parsed from the
 * printed label), both sides share a storey level index, the canonical token
 * matches, or one side names the entrance and the other numbers it.
 */
const storeysAreSame = (
  a: ExtractedStoreyFacts,
  b: ExtractedStoreyFacts,
): boolean => {
  if (
    planNumbersOverlap(
      storeyPlanNumbersForMatch(a),
      storeyPlanNumbersForMatch(b),
    )
  ) {
    return true;
  }
  const aIndex = storeyLevelIndex(a.level);
  const bIndex = storeyLevelIndex(b.level);
  if (aIndex !== null && bIndex !== null && aIndex === bIndex) return true;
  const aKey = canonicalStoreyKey({ label: a.label });
  const bKey = canonicalStoreyKey({ label: b.label });
  if (aKey !== null && bKey !== null && aKey.token === bKey.token) return true;
  if (!isEntranceStorey(a) || !isEntranceStorey(b)) return false;
  return (
    storeyPlanNumbersForMatch(a).length === 0 ||
    storeyPlanNumbersForMatch(b).length === 0
  );
};

const normalizeOtherAreaLabel = (label: string | null): string =>
  label === null ? '' : normalizeExtractedBuildingName(label);

const toHundredths = (value: number): number => Math.round(value * 100);

/**
 * Drops a building otherAreas row only when the parts' rows with the same
 * label add up to it, i.e. the building row is the unsplit copy of the part
 * rows. A building total that the part rows do not account for stays, so
 * partial part-level rows cannot erase the building-level row.
 */
export const dropBuildingOtherAreasWhenLabelOnPart = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  let changed = false;
  const buildings = facts.buildings.map((building) => {
    if (building.parts.length === 0 || building.otherAreas.length === 0) {
      return building;
    }
    const partAreaByLabel = new Map<string, number>();
    for (const part of building.parts) {
      for (const row of part.otherAreas) {
        const key = normalizeOtherAreaLabel(row.label);
        if (key === '') continue;
        partAreaByLabel.set(
          key,
          (partAreaByLabel.get(key) ?? 0) + (row.areaM2 ?? 0),
        );
      }
    }
    const nextBuildingOtherAreas = building.otherAreas.filter((row) => {
      const partArea = partAreaByLabel.get(normalizeOtherAreaLabel(row.label));
      if (partArea === undefined) return true;
      if (
        row.areaM2 !== null &&
        toHundredths(row.areaM2) !== toHundredths(partArea)
      ) {
        return true;
      }
      changed = true;
      return false;
    });
    if (nextBuildingOtherAreas.length === building.otherAreas.length) {
      return building;
    }
    return { ...building, otherAreas: nextBuildingOtherAreas };
  });
  return changed ? { ...facts, buildings } : facts;
};

const clipStoreysToExactStoreyCount = (
  storeys: readonly ExtractedStoreyFacts[],
  count: StoreyCount | null,
): ExtractedStoreyFacts[] => {
  const max = exactCount(count);
  if (max == null) return [...storeys];
  const maxIndex = max - 1;
  return storeys.filter((storey) => {
    const index = storeyLevelIndex(storey.level);
    if (index == null || index < 0) return true;
    return index <= maxIndex || storeyHasScoredContent(storey);
  });
};

const storeyWithinExactCount = (
  storey: ExtractedStoreyFacts,
  count: StoreyCount | null,
): boolean => {
  const max = exactCount(count);
  const index = storeyLevelIndex(storey.level);
  if (max == null || index == null || index < 0) return true;
  return index < max;
};

const addStoreyToPart = (
  part: ExtractedBuildingPartFacts,
  storey: ExtractedStoreyFacts,
): ExtractedBuildingPartFacts => {
  const existing = part.storeys.find((row) => storeysAreSame(row, storey));
  if (existing === undefined) {
    const storeys = [...part.storeys, storey].sort(
      (a, b) => storeyLevelSortKey(a.level) - storeyLevelSortKey(b.level),
    );
    return { ...part, storeys };
  }
  return {
    ...part,
    storeys: part.storeys.map((row) =>
      row === existing
        ? {
            ...mergeStoreyFactFields(existing, storey),
            programs: [...existing.programs, ...storey.programs],
          }
        : row,
    ),
  };
};

/**
 * A part's `part_name` can be tagged on a storey that part does not have
 * according to the section (its exact storey count). Such a storey moves to
 * the one sibling part whose count reaches that index, so its programs are
 * not clipped away. With no single such sibling it stays on the part it was
 * read on: a storey is never dropped.
 */
export const reassignStoreysOutsidePartCounts = (
  parts: readonly ExtractedBuildingPartFacts[],
): ExtractedBuildingPartFacts[] => {
  const moves = new Map<number, ExtractedStoreyFacts[]>();
  const stayed = parts.map((part, partIndex) => {
    const storeys = part.storeys.filter((storey) => {
      if (storeyWithinExactCount(storey, part.storeyCount)) return true;
      const homes = parts.flatMap((other, otherIndex) =>
        otherIndex !== partIndex &&
        exactCount(other.storeyCount) != null &&
        storeyWithinExactCount(storey, other.storeyCount)
          ? [otherIndex]
          : [],
      );
      const home = homes[0];
      if (homes.length !== 1 || home === undefined) return true;
      moves.set(home, [...(moves.get(home) ?? []), storey]);
      return false;
    });
    return storeys.length === part.storeys.length ? part : { ...part, storeys };
  });
  if (moves.size === 0) return [...parts];
  return stayed.map((part, index) =>
    (moves.get(index) ?? []).reduce(addStoreyToPart, part),
  );
};

const _partsWithExactStoreyCount = (
  parts: readonly ExtractedBuildingPartFacts[],
): number => parts.filter((part) => part.storeyCount?.kind === 'exact').length;

/**
 * Two named slices whose section stacks differ in height (e.g. Del A / Del B).
 * Many typology rows with their own counts are not this pattern.
 */
export const buildingHasSectionAttributedPartStoreys = (
  building: ExtractedBuildingFacts,
): boolean => {
  const exact = building.parts.filter(
    (part) => part.storeyCount?.kind === 'exact',
  );
  if (exact.length !== 2) return false;
  const a = exact[0]?.storeyCount;
  const b = exact[1]?.storeyCount;
  if (a?.kind !== 'exact' || b?.kind !== 'exact') return false;
  return a.value !== b.value;
};

/**
 * When a building has no storeys from text, fill them from plan crops.
 * A crop tied to known buildings fills those buildings. A crop with no
 * known building (a site plan, or a project-name slug) fills every building
 * that still has none. A building that already has storeys keeps the rows
 * and count the merge chose: drawings attach to them, they add none.
 */
export const fillBuildingStoreysFromDrawings = (
  facts: ExtractedDesignFacts,
  drawings: readonly PlanDrawing[],
  known: readonly KnownBuildingIdentity[],
): ExtractedDesignFacts => {
  const placed = drawings.map((drawing) => ({
    drawing,
    keys: resolveDrawingBuildingKeys(
      [drawing.buildingInstanceId, ...(drawing.buildingInstanceIds ?? [])],
      drawingAttachmentText(drawing),
      known,
    ),
  }));
  const entranceByBuilding = entrancePlanByBuildingFromFacts(facts);
  const entranceFor = (
    key: string | null,
    drawings: readonly PlanDrawing[],
  ): number | null => {
    if (key != null) {
      const stated = entranceByBuilding.get(key);
      if (stated !== undefined) return stated;
    }
    const shared = entranceByBuilding.get('_');
    if (shared !== undefined) return shared;
    return entrancePlanFromDrawings(drawings);
  };
  const siteDrawings = placed
    .filter((row) => row.keys.length === 0)
    .map((row) => row.drawing);
  const siteStoreys = storeysFromPlanDrawings(
    siteDrawings,
    entranceFor(null, siteDrawings),
  );
  let changed = false;
  const buildings = facts.buildings.map((building) => {
    if (buildingHasStoreys(building)) return building;
    const key = building.buildingInstanceKey;
    const own = placed
      .filter((row) => key != null && row.keys.includes(key))
      .map((row) => row.drawing);
    const storeys = materializeTypicalStoreys(
      own.length > 0
        ? storeysFromPlanDrawings(own, entranceFor(key, own))
        : siteStoreys,
      building.storeyCount,
    );
    if (storeys.length === 0) return building;
    changed = true;
    return { ...building, storeys };
  });
  return changed ? { ...facts, buildings } : facts;
};

const projectSlugFromName = (projectName: string | null): string | null => {
  if (projectName == null) return null;
  const first = projectName.trim().split(/\s+/)[0];
  if (first === undefined || first === '') return null;
  return normalizeBuildingInstanceKey(first);
};

/**
 * When a building has several parts with their own storey stacks, keep counts
 * and rows on each part and drop a merged building-level list that would copy
 * the taller stack onto every part.
 */
export const syncPartAttributedStoreyLayout = (
  facts: ExtractedDesignFacts,
): ExtractedDesignFacts => {
  let changed = false;
  const slug = projectSlugFromName(facts.projectName);
  const buildings = facts.buildings.map((building) => {
    if (!buildingHasSectionAttributedPartStoreys(building)) return building;
    const reassigned = reassignStoreysOutsidePartCounts(building.parts);
    let buildingChanged = reassigned.some(
      (part, index) => part !== building.parts[index],
    );
    const parts = reassigned.map((part) => {
      const storeys = clipStoreysToExactStoreyCount(
        part.storeys,
        part.storeyCount,
      );
      if (
        storeys.length === part.storeys.length &&
        storeys.every((row, index) => row === part.storeys[index])
      ) {
        return part;
      }
      buildingChanged = true;
      return { ...part, storeys };
    });
    let next: ExtractedBuildingFacts = buildingChanged
      ? { ...building, parts }
      : building;
    if (next.storeys.length > 0 || next.storeyCount != null) {
      buildingChanged = true;
      next = { ...next, storeys: [], storeyCount: null };
    }
    if (
      slug != null &&
      facts.buildingCount === 1 &&
      next.buildingInstanceKey !== slug
    ) {
      buildingChanged = true;
      next = { ...next, buildingInstanceKey: slug };
    }
    if (!buildingChanged) return building;
    changed = true;
    return next;
  });
  return changed ? { ...facts, buildings } : facts;
};

/** The totals the merger states for the composed body win over its houses' own. */
const withCompositionAreas = (
  building: ExtractedBuildingFacts,
  composition: ProjectFileBatchBuildingComposition,
): ExtractedBuildingFacts => {
  const areas = composition.areas;
  if (areas === null) return building;
  return {
    ...building,
    btaM2: areas.btaM2 ?? building.btaM2,
    ljusBtaM2: areas.ljusBtaM2 ?? building.ljusBtaM2,
    boaM2: areas.boaM2 ?? building.boaM2,
    loaM2: areas.loaM2 ?? building.loaM2,
    byaM2: areas.byaM2 ?? building.byaM2,
  };
};

export const joinConnectedBuildings = (
  facts: ExtractedDesignFacts,
  compositions: readonly ProjectFileBatchBuildingComposition[],
): ExtractedDesignFacts => {
  const used = new Set<ExtractedBuildingFacts>();
  const joined = new Map<ExtractedBuildingFacts, ExtractedBuildingFacts>();
  for (const composition of compositions) {
    if (!composition.shouldModelAsSingleBuilding) continue;
    const houses = facts.buildings.filter(
      (building) =>
        !used.has(building) &&
        composition.composingBuildingKeys.some((key) =>
          compositionKeyMatches(building, key),
        ),
    );
    const first = houses[0];
    if (first === undefined || houses.length < 2) continue;
    for (const house of houses) used.add(house);
    const distinctHouses = countsSeveralHouses(
      houses.flatMap((house) =>
        house.buildingInstanceKey == null ? [] : [house.buildingInstanceKey],
      ),
    );
    const separateVolumeStacks =
      houses.length >= 2 && houses.filter(houseHasOwnStoreyStack).length >= 2;
    joined.set(
      first,
      withCompositionAreas(
        distinctHouses || separateVolumeStacks
          ? joinHouses(houses, composition)
          : foldAliasHouses(houses),
        composition,
      ),
    );
  }
  if (joined.size === 0) return facts;
  const buildings = facts.buildings.flatMap((building) => {
    const replacement = joined.get(building);
    if (replacement !== undefined) return [replacement];
    return used.has(building) ? [] : [building];
  });
  const merged = facts.buildings.length - buildings.length;
  return {
    ...facts,
    buildingCount:
      facts.buildingCount === null
        ? null
        : Math.max(buildings.length, facts.buildingCount - merged),
    buildings,
  };
};
